/* eslint-disable react-hooks/exhaustive-deps */
import moment from "moment";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import "./clubSalesAnalysis.css";
import TicketSoldCell from "./TicketSoldCell";
import ShareTableSheetButton from "../../components/tableSheet/ShareTableSheetButton";
import { pickGuestlistTicketStatsFromRow } from "./ticketSoldStats";
import Badge from "react-bootstrap/Badge";
import Button from "react-bootstrap/Button";
import Card from "react-bootstrap/Card";
import Col from "react-bootstrap/Col";
import Form from "react-bootstrap/Form";
import Modal from "react-bootstrap/Modal";
import OverlayTrigger from "react-bootstrap/OverlayTrigger";
import Popover from "react-bootstrap/Popover";
import Pagination from "react-bootstrap/Pagination";
import Row from "react-bootstrap/Row";
import Spinner from "react-bootstrap/Spinner";
import Table from "react-bootstrap/Table";
import Tab from "react-bootstrap/Tab";
import Tabs from "react-bootstrap/Tabs";
import Helmet from "react-helmet";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useSearchParams, useNavigate } from "react-router-dom";

import { CURRENT_EVENTS, PAST_EVENTS } from "../../api/endPoint";
import {
  fetchClubEmployees,
  downloadClubEventInvoice,
  fetchClubSalesBookings,
  fetchClubSalesIssuedTickets,
  fetchClubSalesReport,
  fetchClubTableBookingReportLines,
  postIssuedTicketResendLink,
} from "../../api/clubApi";
import { fetchEventBookings } from "../../api/orderApi";
import { pickEventDisplayNumber } from "../../helpers/eventDisplayNumber";
import { TableBookingCatalogSnapshot } from "../../components/clubCatalog/BookingCatalogSnapshots";
import { saveAs } from "file-saver";
import * as XLSX from "xlsx";
import Breadcrumbs from "../../components/customBreadcrumb/customBreadcrumb";
import CustomButton from "../../components/customButton/customButton";
import CustomeSelect from "../../components/customSelect/customSelect";
import instance from "../../helpers/axios";
import {
  asClubEmployeesList,
  formatClubEmployeeOptionLabel,
  pickClubEmployeeUserId,
} from "../../helpers/clubEmployeeList";
import useAuth from "../../hooks/useAuth";
import useMyEmpref from "../../hooks/useMyEmpref";
import useEventSummaryDownload from "../../hooks/useEventSummaryDownload";
import EventSummaryPendingDevicesModal from "../../components/eventSummary/EventSummaryPendingDevicesModal";
import { isEventStatusCompleted } from "../../helpers/eventSummaryReadiness";
import { triggerBlobDownload } from "../../helpers/fileDownload";
import { toTitleCase } from "../../helpers/titleCase";
import Constant from "../../util/constant";
import { clubListResponce } from "../../store/features/club/clubSlice";
import { normalizeListPayload } from "../../store/features/event/eventSlice";
import clubService from "../../store/features/services/clubService";
import { showToast } from "../../store/features/toast/toastSlice";
import {
  pickBookingGuestEmail,
  pickBookingGuestName,
  pickBookingGuestPhone,
} from "../tableBooking/tableBookingUtils";

const DEFAULT_CURRENCY_CODE =
  process.env.REACT_APP_DEFAULT_CURRENCY || "INR";

const asArray = (v) => (Array.isArray(v) ? v : []);

const BOOKING_PAGE_SIZE_OPTIONS = [10, 20, 50, 100];

function clampPageLimit(value, fallback = 10) {
  const n = Number(value);
  if (!Number.isFinite(n)) return fallback;
  return Math.min(100, Math.max(1, Math.round(n)));
}

/**
 * Fill one UI page when the API returns a shorter page than requested
 * (for example 10 rows while the control is set to 20).
 */
async function collectLimitedPage(fetchPage, uiPage, uiLimit) {
  const wanted = clampPageLimit(uiLimit);
  const page = Math.max(1, Number(uiPage) || 1);
  const first = await fetchPage(page, wanted);
  const firstItems = asArray(first?.items);
  const total = Number(first?.total) || 0;
  const echoedLimit = Number(first?.limit);
  const loadedThrough = (page - 1) * wanted + firstItems.length;
  const reachedEnd = total > 0 && loadedThrough >= total;
  if (
    firstItems.length >= wanted ||
    firstItems.length === 0 ||
    reachedEnd ||
    (echoedLimit >= wanted && firstItems.length >= echoedLimit)
  ) {
    return { items: firstItems.slice(0, wanted), total, page };
  }

  const serverSize = Math.max(
    1,
    echoedLimit > 0 && echoedLimit < wanted
      ? echoedLimit
      : firstItems.length
  );
  const start = (page - 1) * wanted;
  const end = start + wanted;
  const firstServerPage = Math.floor(start / serverSize) + 1;
  const lastServerPage = Math.ceil(end / serverSize);
  const chunks = [];
  for (
    let serverPage = firstServerPage;
    serverPage <= lastServerPage;
    serverPage += 1
  ) {
    const block =
      serverPage === page ? first : await fetchPage(serverPage, wanted);
    const chunk = asArray(block?.items);
    chunks.push(...chunk);
    if (chunk.length < serverSize) break;
  }
  const offset = start - (firstServerPage - 1) * serverSize;
  return {
    items: chunks.slice(Math.max(0, offset), Math.max(0, offset) + wanted),
    total,
    page,
  };
}

const unwrapResponse = (res) => {
  const envelope = res?.data ?? res;
  return envelope?.data ?? envelope;
};

/** Bookings & issued-tickets share list filters with the sales report but Joi forbids e.g. `breakdown`. */
const omitSalesReportOnlyParams = (params) => {
  const rest = { ...(params ?? {}) };
  delete rest.breakdown;
  return rest;
};

function eventStartCalendarDate(ev) {
  const raw = ev?.startdatetime ?? ev?.startDatetime ?? ev?.eventstartDate;
  if (!raw) return null;
  const m = moment(raw);
  return m.isValid() ? m.format("YYYY-MM-DD") : null;
}

/** True when event start falls on a calendar day inside the selected range. */
function isEventStartInRange(ev, dateFrom, dateTo) {
  const day = eventStartCalendarDate(ev);
  if (!day) return false;
  const from = String(dateFrom || "").trim();
  const to = String(dateTo || "").trim();
  if (!from && !to) return true;
  const effectiveFrom = from || to;
  const effectiveTo = to || from;
  return day >= effectiveFrom && day <= effectiveTo;
}

function filterEventsByStartDateRange(events, dateFrom, dateTo) {
  return asArray(events).filter((ev) =>
    isEventStartInRange(ev, dateFrom, dateTo)
  );
}

function formatEventOptionLabel(ev) {
  const title = ev?.title || ev?._id || "—";
  const day = eventStartCalendarDate(ev);
  return day ? `${moment(day).format("DD MMM YYYY")} — ${title}` : title;
}

function formatEventTitleOnly(ev) {
  return ev?.title || ev?._id || "—";
}

function buildEventSelectOptions(events) {
  return asArray(events).map((ev) => {
    const title = formatEventTitleOnly(ev);
    const day = eventStartCalendarDate(ev);
    const dateLabel = day ? moment(day).format("DD MMM YYYY") : "";
    const menuLabel = dateLabel ? `${dateLabel} — ${title}` : title;
    return {
      value: String(ev._id),
      label: menuLabel,
      title,
      dateLabel,
    };
  });
}

function ClubSalesSearchableEventSelect({
  events,
  value,
  onChange,
  disabled,
  placeholder,
}) {
  const options = useMemo(() => buildEventSelectOptions(events), [events]);
  const selected = useMemo(
    () => options.find((o) => o.value === String(value)) ?? null,
    [options, value]
  );

  return (
    <CustomeSelect
      options={options}
      value={selected}
      onChange={(opt) => onChange(opt?.value ?? "")}
      isSearchable
      isClearable
      closeMenuOnSelect
      isDisabled={disabled}
      placeholder={placeholder}
      formatOptionLabel={(option, { context }) =>
        context === "value" ? option.title : option.label
      }
      className="singleSelecter club-sales-event-select"
      classNamePrefix="react-select"
    />
  );
}

function ClubSalesSearchableClubSelect({
  clubs,
  value,
  onChange,
  allLabel,
}) {
  const options = useMemo(() => {
    const clubOptions = asArray(clubs)
      .map((club) => ({
        value: String(club?._id ?? ""),
        label: String(club?.name || club?.slug || club?._id || ""),
      }))
      .filter((option) => option.value);
    return [{ value: "", label: allLabel }, ...clubOptions];
  }, [clubs, allLabel]);
  const selected = useMemo(
    () => options.find((option) => option.value === String(value ?? "")) ?? options[0],
    [options, value]
  );

  return (
    <CustomeSelect
      options={options}
      value={selected}
      onChange={(opt) => onChange(opt?.value ?? "")}
      isSearchable
      isClearable={Boolean(value)}
      closeMenuOnSelect
      placeholder={allLabel}
      className="singleSelecter club-sales-event-select club-sales-club-select"
      classNamePrefix="react-select"
    />
  );
}

function ClubSalesSearchableEmployeeSelect({
  options,
  value,
  onChange,
  disabled,
  placeholder,
}) {
  const selectOptions = useMemo(
    () =>
      asArray(options).map((o) => ({
        value: String(o.id ?? o.value ?? ""),
        label: String(o.label ?? ""),
      })),
    [options]
  );
  const selected = useMemo(
    () =>
      value
        ? selectOptions.find((o) => o.value === String(value)) ?? null
        : null,
    [selectOptions, value]
  );

  return (
    <CustomeSelect
      options={selectOptions}
      value={selected}
      onChange={(opt) => onChange(opt?.value ?? "")}
      isSearchable
      isClearable
      closeMenuOnSelect
      isDisabled={disabled}
      placeholder={placeholder}
      className="singleSelecter club-sales-event-select"
      classNamePrefix="react-select"
    />
  );
}

const EVENT_SCOPES = {
  ALL: "",
  ONGOING: "ongoing",
  UPCOMING: "upcoming",
  PAST: "past",
};

function isEventCancelled(ev) {
  const status = String(ev?.eventstatus ?? "").trim().toLowerCase();
  return (
    status === Constant.GENERALKEYS.CANCEL.toLowerCase() ||
    status === "cancelled"
  );
}

function classifyEventScope(ev) {
  if (isEventCancelled(ev)) return "cancel";
  if (isEventStatusCompleted(ev?.eventstatus)) return EVENT_SCOPES.PAST;
  const status = String(ev?.eventstatus ?? "").trim().toLowerCase();
  if (status === Constant.GENERALKEYS.UPCOMING.toLowerCase()) {
    return EVENT_SCOPES.UPCOMING;
  }
  return EVENT_SCOPES.ONGOING;
}

function eventMatchesScope(ev, scope) {
  if (!scope || scope === EVENT_SCOPES.ALL) {
    return !isEventCancelled(ev);
  }
  return classifyEventScope(ev) === scope;
}

function filterEventsByScope(events, scope, dateFrom, dateTo) {
  const inRange = filterEventsByStartDateRange(
    asArray(events),
    dateFrom,
    dateTo
  );
  return inRange.filter((ev) => eventMatchesScope(ev, scope));
}

function normalizeBreakdownSeriesRow(raw) {
  if (!raw || typeof raw !== "object") return null;
  const period =
    raw.period ??
    raw.label ??
    raw._id ??
    raw.bucket ??
    raw.start ??
    raw.date;
  if (period == null || period === "") return null;
  return {
    period: String(period),
    orderCount: numOrZero(raw.orderCount ?? raw.ordercount ?? raw.orders),
    ticketsSold: numOrZero(
      raw.ticketsSold ?? raw.ticketssold ?? raw.tickets
    ),
    revenue: numOrZero(raw.revenue ?? raw.totalRevenue ?? raw.amount),
  };
}

function normalizeBreakdownSeries(data) {
  const raw =
    data?.breakdownSeries ?? data?.breakdown_series ?? data?.series;
  return asArray(raw).map(normalizeBreakdownSeriesRow).filter(Boolean);
}

function sumBreakdownSeriesRows(rows) {
  return asArray(rows).reduce(
    (acc, r) => ({
      orderCount: acc.orderCount + numOrZero(r?.orderCount),
      ticketsSold: acc.ticketsSold + numOrZero(r?.ticketsSold),
      revenue: acc.revenue + numOrZero(r?.revenue),
    }),
    { orderCount: 0, ticketsSold: 0, revenue: 0 }
  );
}

function filterByEventRowsForScope(rows, scope, scopedEventIds, selectedEventId) {
  if (selectedEventId?.trim()) return asArray(rows);
  if (!scope || scope === EVENT_SCOPES.ALL) return asArray(rows);
  return asArray(rows).filter((row) => {
    const id = String(row?.eventId ?? row?._id ?? "");
    if (id && scopedEventIds instanceof Set && scopedEventIds.has(id)) {
      return true;
    }
    const eventstatus = row?.eventstatus ?? row?.eventStatus;
    if (!eventstatus) {
      return false;
    }
    return eventMatchesScope({ eventstatus, _id: id }, scope);
  });
}

/**
 * Event start date range (Asia/Kolkata calendar days on backend).
 * Prefer plain YYYY-MM-DD from `<input type="date">`.
 */
function appendClubSalesReportDateParams(target, dateFrom, dateTo) {
  const from = String(dateFrom || "").trim();
  const to = String(dateTo || "").trim();
  if (!from && !to) return;
  const effectiveFrom = from || to;
  const effectiveTo = to || from;
  if (effectiveFrom > effectiveTo) return;
  target.dateFrom = effectiveFrom;
  target.dateTo = effectiveTo;
}

function mergeEventLists(...lists) {
  const map = new Map();
  for (const list of lists) {
    for (const ev of asArray(list)) {
      const id = ev?._id;
      if (id != null && id !== "") map.set(String(id), ev);
    }
  }
  return Array.from(map.values()).sort((a, b) => {
    const ta = a?.startdatetime ? new Date(a.startdatetime).getTime() : 0;
    const tb = b?.startdatetime ? new Date(b.startdatetime).getTime() : 0;
    return tb - ta;
  });
}

const fmtMoney = (n) =>
  n == null || Number.isNaN(Number(n))
    ? "—"
    : Number(n).toLocaleString(undefined, {
        minimumFractionDigits: 0,
        maximumFractionDigits: 2,
      });

const fmtCurrency = (n) => {
  const s = fmtMoney(n);
  if (s === "—") return "—";
  return DEFAULT_CURRENCY_CODE === "INR" ? `₹${s}` : `${DEFAULT_CURRENCY_CODE} ${s}`;
};

/** Admin settlement amounts: INR with en-IN grouping and exactly 2 decimal places. */
const fmtInrCurrency = (n) => {
  if (n == null || Number.isNaN(Number(n))) return "—";
  return `₹${Number(n).toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
};

function hasDefinedMoney(v) {
  return v != null && v !== "" && !Number.isNaN(Number(v));
}

/** INR display: ₹0.00 for zero; em dash only when value is missing. */
function fmtInrMoneyDisplay(n) {
  if (!hasDefinedMoney(n)) return "—";
  return fmtInrCurrency(n);
}

/**
 * GST-inclusive commission (18% = 9% CGST + 9% SGST) → ex-tax base + split.
 * Guarantees commissionBase + cgst + sgst === commission (2 dp).
 */
function deriveCommissionGstSplit(commission) {
  const total = numOrZero(commission);
  if (total <= 0) {
    return { commission: 0, commissionBase: 0, cgst: 0, sgst: 0 };
  }
  const baseExact = total / 1.18;
  const cgst = Math.round(((total - baseExact) / 2) * 100) / 100;
  const sgst = Math.round((total - baseExact - cgst) * 100) / 100;
  const commissionBase = Math.round((total - cgst - sgst) * 100) / 100;
  return { commission: total, commissionBase, cgst, sgst };
}

function buildAdminFinancialsByEventMap(adminFinancials) {
  const map = new Map();
  for (const row of asArray(adminFinancials?.byEvent)) {
    const eventId =
      row?.eventId != null && row.eventId !== "" ? String(row.eventId) : "";
    const rowId = row?._id != null && row._id !== "" ? String(row._id) : "";
    if (eventId) map.set(eventId, row);
    if (rowId && rowId !== eventId) map.set(rowId, row);
  }
  return map;
}

function sumAdminFinancialByEventRows(rows) {
  return rows.reduce(
    (acc, r) => ({
      ticketsSold: acc.ticketsSold + numOrZero(r?.ticketsSold),
      overallSale: acc.overallSale + numOrZero(r?.overallSale),
      ticketSales: acc.ticketSales + numOrZero(r?.ticketSales),
      commission: acc.commission + numOrZero(r?.commission),
      cgst: acc.cgst + numOrZero(r?.cgst),
      sgst: acc.sgst + numOrZero(r?.sgst),
    }),
    {
      ticketsSold: 0,
      overallSale: 0,
      ticketSales: 0,
      commission: 0,
      cgst: 0,
      sgst: 0,
    }
  );
}

function pickAdminFinForEventRow(row, adminFinByEvent) {
  return pickEventAdminFinancials(row, adminFinByEvent);
}

/**
 * Admin financials on byEvent rows (online ticket commission only) with fallback
 * to adminFinancials.byEvent matched by eventId.
 */
function pickEventAdminFinancials(row, adminFinByEvent) {
  const id = row?.eventId ?? row?._id;
  const fromMap =
    id != null && id !== ""
      ? adminFinByEvent.get(String(id)) ?? null
      : null;

  const pickNum = (...candidates) => {
    for (const v of candidates) {
      if (v != null && v !== "") return numOrZero(v);
    }
    return 0;
  };

  const ticketSales = pickNum(
    row?.ticketSales,
    row?.ticket_sales,
    fromMap?.ticketSales,
    fromMap?.ticket_sales,
    row?.revenue
  );

  const explicitOnlineRevenue = pickNum(
    row?.onlineRevenue,
    row?.online_revenue,
    fromMap?.onlineRevenue,
    fromMap?.online_revenue
  );
  const offlineRevenue = pickNum(
    row?.offlineRevenue,
    row?.offline_revenue,
    fromMap?.offlineRevenue,
    fromMap?.offline_revenue
  );
  const onlineRevenue =
    explicitOnlineRevenue > 0 ||
    hasDefinedMoney(row?.onlineRevenue) ||
    hasDefinedMoney(row?.online_revenue) ||
    hasDefinedMoney(fromMap?.onlineRevenue) ||
    hasDefinedMoney(fromMap?.online_revenue)
      ? explicitOnlineRevenue
      : ticketSales > 0
        ? Math.max(0, ticketSales - offlineRevenue)
        : explicitOnlineRevenue;

  const commission = pickNum(row?.commission, fromMap?.commission);
  const gstSplit = deriveCommissionGstSplit(commission);

  return {
    overallSale: pickNum(
      row?.overallSale,
      row?.overall_sale,
      fromMap?.overallSale,
      fromMap?.overall_sale
    ),
    ticketSales,
    onlineRevenue,
    commission: gstSplit.commission,
    commissionBase: gstSplit.commissionBase,
    cgst: gstSplit.cgst,
    sgst: gstSplit.sgst,
    eventEnded:
      row?.eventEnded ??
      row?.event_ended ??
      fromMap?.eventEnded ??
      fromMap?.event_ended,
  };
}

function sumEventAdminFinancials(rows, adminFinByEvent) {
  const totals = asArray(rows).reduce(
    (acc, row) => {
      const fin = pickEventAdminFinancials(row, adminFinByEvent);
      return {
        overallSale: acc.overallSale + fin.overallSale,
        ticketSales: acc.ticketSales + fin.ticketSales,
        onlineRevenue: acc.onlineRevenue + fin.onlineRevenue,
        commission: acc.commission + fin.commission,
      };
    },
    {
      overallSale: 0,
      ticketSales: 0,
      onlineRevenue: 0,
      commission: 0,
    }
  );
  const gstSplit = deriveCommissionGstSplit(totals.commission);
  return { ...totals, ...gstSplit };
}

function eventHasAdminFinancialFields(row) {
  return (
    row?.overallSale != null ||
    row?.overall_sale != null ||
    row?.ticketSales != null ||
    row?.ticket_sales != null ||
    row?.onlineRevenue != null ||
    row?.online_revenue != null ||
    row?.commission != null ||
    row?.commissionBase != null ||
    row?.commission_base != null ||
    row?.cgst != null ||
    row?.sgst != null
  );
}

function FeeBreakdownLine({ label, value }) {
  return (
    <div className="club-sales-fee-breakdown-line d-flex justify-content-between gap-3">
      <span className="text-muted">{label}</span>
      <span className="tabular-nums fw-medium">{fmtInrMoneyDisplay(value)}</span>
    </div>
  );
}

function RizztixCommissionBreakdownCell({ fin, bookingRate, t }) {
  const value = fmtInrMoneyDisplay(fin?.commission);
  const breakdown = (
    <Popover className="club-sales-commission-popover">
      <Popover.Header as="h3" className="small fw-semibold mb-0">
        {t("CLUB_SALES_COMMISSION_BREAKDOWN_TITLE")}
      </Popover.Header>
      <Popover.Body className="small py-2">
        <div className="club-sales-fee-breakdown d-flex flex-column gap-2">
          <FeeBreakdownLine
            label={t("CLUB_SALES_FIN_COMMISSION_BASE")}
            value={fin?.commissionBase}
          />
          <FeeBreakdownLine label={t("CLUB_SALES_FIN_CGST")} value={fin?.cgst} />
          <FeeBreakdownLine label={t("CLUB_SALES_FIN_SGST")} value={fin?.sgst} />
          <div className="club-sales-fee-breakdown-divider" />
          <FeeBreakdownLine
            label={t("CLUB_SALES_FIN_COMMISSION_TOTAL")}
            value={fin?.commission}
          />
        </div>
      </Popover.Body>
    </Popover>
  );

  return (
    <div className="club-sales-commission-cell-wrap text-end">
      {bookingRate != null ? (
        <div className="club-sales-commission-row-rate small text-muted mb-1">
          ({bookingRate}%)
        </div>
      ) : null}
    <OverlayTrigger trigger={["hover", "focus", "click"]} placement="left" overlay={breakdown} rootClose>
      <button
        type="button"
        className="club-sales-commission-cell-btn btn btn-link p-0 text-decoration-none text-reset tabular-nums"
        aria-label={t("CLUB_SALES_COMMISSION_BREAKDOWN_TITLE")}
      >
        <span>{value}</span>
        <span className="club-sales-commission-info ms-1" aria-hidden>
          ⓘ
        </span>
      </button>
    </OverlayTrigger>
    </div>
  );
}

function EventSettlementStatusBadge({ eventEnded, t }) {
  if (eventEnded === true) {
    return (
      <Badge pill bg="secondary" className="fw-normal px-2 py-1">
        {t("CLUB_SALES_SETTLEMENT_STATUS_ENDED")}
      </Badge>
    );
  }
  if (eventEnded === false) {
    return (
      <Badge pill bg="success" className="fw-normal px-2 py-1">
        {t("CLUB_SALES_SETTLEMENT_STATUS_LIVE")}
      </Badge>
    );
  }
  return (
    <span className="text-muted small">{t("CLUB_SALES_VALUE_EMPTY")}</span>
  );
}

const dash = (v, t) =>
  v == null || v === "" ? t("CLUB_SALES_VALUE_EMPTY") : String(v);

/** Bookings API may return flat buyer fields, nested `user`, or `userid` (event ticket list shape). */
function pickBookingTimestamp(row) {
  const raw =
    row?.orderstatustimestamp ??
    row?.paymentstatustimestamp ??
    row?.createdAt ??
    row?.bookedAt;
  if (raw == null) return null;
  if (typeof raw === "object" && raw.$date != null) return raw.$date;
  return raw;
}

function pickBookingEventTitle(row) {
  const v =
    row?.event?.title ??
    row?.eventTitle ??
    row?.eventname ??
    row?.eventName;
  return v != null && v !== "" ? String(v) : "";
}

function pickTicketTypeLabel(row) {
  const v =
    row?.tickettype ??
    row?.ticketType ??
    row?.ticketCategory ??
    row?.tickettypename ??
    row?.ticketTypeName;
  return v != null && v !== "" ? String(v) : "";
}

function pickTicketQty(row) {
  const n = row?.noofticket ?? row?.ticketCount ?? row?.quantity ?? row?.qty;
  if (n == null || n === "") return null;
  const num = Number(n);
  return Number.isFinite(num) ? num : n;
}

function pickBookingBuyerParts(row) {
  const name =
    row?.buyername ||
    row?.userid?.fullname ||
    row?.user?.fullname ||
    row?.buyer?.fullname ||
    "";
  const phone =
    row?.buyerphone ||
    row?.userid?.phone ||
    row?.user?.phone ||
    row?.buyer?.phone ||
    "";
  const email =
    row?.buyeremail ||
    row?.userid?.email ||
    row?.user?.email ||
    row?.buyer?.email ||
    "";
  return { name, phone, email };
}

function buildBookingSearchHaystack(row) {
  const { name, phone, email } = pickBookingBuyerParts(row);
  return [
    name,
    phone,
    email,
    pickBookingRef(row),
    pickBookingEventTitle(row),
    pickTicketTypeLabel(row),
    row?.soldBy?.fullname,
    row?.offlinepaymentmethod,
    row?.club?.name,
  ]
    .filter((v) => v != null && String(v).trim() !== "")
    .join(" ")
    .toLowerCase();
}

function bookingMatchesSearch(row, searchTerm) {
  const q = String(searchTerm || "").trim().toLowerCase();
  if (!q) return true;
  const haystack = buildBookingSearchHaystack(row);
  return q.split(/\s+/).filter(Boolean).every((word) => haystack.includes(word));
}

function filterBookingListItems(items, searchTerm) {
  const q = String(searchTerm || "").trim();
  if (!q) return asArray(items);
  return asArray(items).filter((row) => bookingMatchesSearch(row, q));
}

/** Searchable text for an event-bookings row (GET /order/event/:eventId/bookings). */
function buildEventBookingHaystack(row) {
  const ticketLabels = asArray(row?.tickets)
    .map(
      (tk) =>
        tk?.tickettype ?? tk?.type ?? tk?.name ?? tk?.category ?? tk?.title
    )
    .filter((v) => v != null && String(v).trim() !== "");
  return [
    row?.buyername,
    row?.buyerphone,
    row?.buyeremail,
    row?.bookingref,
    row?.orderid,
    row?.transactionid,
    row?.paymentid,
    row?.tickettype,
    ...ticketLabels,
    row?.salechannel,
    row?.orderstatus,
    row?.paymentstatus,
    row?.user?.name,
    row?.user?.phone,
    row?.user?.email,
  ]
    .filter((v) => v != null && String(v).trim() !== "")
    .join(" ")
    .toLowerCase();
}

function eventBookingMatchesSearch(row, searchTerm) {
  const q = String(searchTerm || "").trim().toLowerCase();
  if (!q) return true;
  const haystack = buildEventBookingHaystack(row);
  return q
    .split(/\s+/)
    .filter(Boolean)
    .every((word) => haystack.includes(word));
}

function filterEventBookingItems(items, searchTerm) {
  const q = String(searchTerm || "").trim();
  if (!q) return asArray(items);
  return asArray(items).filter((row) => eventBookingMatchesSearch(row, q));
}

const BOOKING_REF_KEYS = [
  "bookingref",
  "bookingRef",
  "booking_ref",
  "bookingReference",
  "booking_reference",
];

function bookingRowSources(row) {
  if (!row || typeof row !== "object") return [];
  return [
    row,
    row.order,
    row.booking,
    row.orderDetails,
    row.orderdetails,
    row.ticketOrder,
  ].filter((s) => s && typeof s === "object");
}

function looksLikeInternalOrderId(value) {
  const s = String(value ?? "").trim();
  if (!s) return false;
  return (
    s.startsWith("off_") ||
    /^[a-f0-9]{24}$/i.test(s) ||
    s.length > 20
  );
}

function pickScalarFromSources(sources, keys) {
  for (const src of sources) {
    for (const key of keys) {
      const v = src[key];
      if (v != null && String(v).trim() !== "") return String(v).trim();
    }
  }
  return "";
}

/** Customer-facing ref only — never orderid / off_* / orderRef. */
function pickBookingRef(row) {
  const ref = pickScalarFromSources(bookingRowSources(row), BOOKING_REF_KEYS);
  if (ref && !looksLikeInternalOrderId(ref)) return ref;

  const maybeRef = row?.ref ?? row?.reference;
  if (
    maybeRef != null &&
    String(maybeRef).trim() !== "" &&
    !looksLikeInternalOrderId(maybeRef)
  ) {
    return String(maybeRef).trim();
  }

  return "";
}

function pickOrderId(row) {
  const candidates = [
    row?.orderid,
    row?.orderId,
    row?.orderRef,
    row?.orderref,
    row?.id,
  ];
  for (const v of candidates) {
    if (v == null || String(v).trim() === "") continue;
    const s = String(v).trim();
    if (looksLikeInternalOrderId(s) || s.startsWith("off_")) return s;
  }
  return "";
}

/** Ensure bookingref is on the row when the API nests or aliases it. */
function normalizeBookingListItem(row) {
  if (!row || typeof row !== "object") return row;
  const bookingref = pickBookingRef(row);
  const orderId = pickOrderId(row);
  return {
    ...row,
    ...(bookingref
      ? { bookingref, bookingRef: bookingref }
      : {}),
    ...(orderId ? { _displayOrderId: orderId } : {}),
  };
}

/**
 * Admin-only identifiers (booking id / order ref / transaction id).
 * The API only includes these keys for ADMIN users, so we defensively check
 * `key in row` before reading. Returns "" when absent/blank so the cell shows a — dash.
 */
function pickAdminIdentifier(row, keys) {
  if (!row || typeof row !== "object") return "";
  for (const key of keys) {
    if (!(key in row)) continue;
    const v = row[key];
    if (v != null && String(v).trim() !== "") return String(v).trim();
  }
  return "";
}

const pickAdminBookingId = (row) =>
  pickAdminIdentifier(row, ["bookingRef", "bookingref"]);
const pickAdminOrderRef = (row) =>
  pickAdminIdentifier(row, ["orderRef", "orderref"]);
const pickAdminTransactionId = (row) =>
  pickAdminIdentifier(row, ["paymentid", "transactionid"]);

function pickBookingAmount(row) {
  if (row?.amount != null && row?.amount !== "") return row.amount;
  if (row?.totalAmount != null && row?.totalAmount !== "") return row.totalAmount;
  if (row?.totalamount != null && row?.totalamount !== "") return row.totalamount;
  const p = Number(row?.ticketprice);
  const q = Number(row?.noofticket ?? row?.quantity ?? row?.qty);
  if (Number.isFinite(p) && Number.isFinite(q)) return p * q;
  return null;
}

/** Amount actually collected. 0 for complimentary guestlist rows. */
function pickBookingAmountPaid(row) {
  if (row?.amountPaid != null && row?.amountPaid !== "") return row.amountPaid;
  if (row?.amountpaid != null && row?.amountpaid !== "") return row.amountpaid;
  return null;
}

/** Complimentary guestlist booking row (face value owed, ₹0 collected). */
function isGuestlistBookingRow(row) {
  return Boolean(row?.isguestlist || row?.isGuestlist);
}

/** Offline POS often creates one DB row per issued ticket; group by bookingref when present. */
function pickOfflineBookingGroupKey(row) {
  const channel = String(row?.salechannel ?? row?.saleChannel ?? "")
    .trim()
    .toUpperCase();
  if (channel !== "OFFLINE") {
    return `single|${pickBookingRef(row)}`;
  }

  const bookingRef =
    row?.bookingref ?? row?.bookingRef ?? row?.booking_ref ?? "";
  if (String(bookingRef).trim() !== "") {
    return `offline-ref|${String(bookingRef).trim()}`;
  }

  const ts = pickBookingTimestamp(row);
  const m = ts ? moment(ts) : null;
  const timeBucket = m?.isValid() ? m.format("YYYY-MM-DD HH:mm") : "";
  const { name, phone, email } = pickBookingBuyerParts(row);
  return [
    "offline-fingerprint",
    pickBookingEventTitle(row),
    timeBucket,
    pickTicketTypeLabel(row),
    channel,
    row?.offlinepaymentmethod ?? "",
    row?.soldBy?.fullname ?? row?.soldBy?.id ?? "",
    name,
    phone,
    email,
    row?.club?.name ?? row?.clubId ?? "",
  ].join("|");
}

function hasSaleDateRange(dateFrom, dateTo) {
  return Boolean(String(dateFrom || "").trim() || String(dateTo || "").trim());
}

function formatMergedTicketTypeLabels(rows) {
  const byType = new Map();
  for (const row of rows) {
    const label = pickTicketTypeLabel(row);
    if (!label) continue;
    const qty = numOrZero(pickTicketQty(row) ?? 1);
    byType.set(label, (byType.get(label) ?? 0) + qty);
  }
  if (byType.size === 0) return "";
  if (byType.size === 1) return [...byType.keys()][0];
  return [...byType.entries()]
    .map(([label, qty]) => (qty > 1 ? `${label} ×${qty}` : label))
    .join(", ");
}

function mergeOfflineBookingGroup(rows) {
  const qtySum = rows.reduce(
    (sum, r) => sum + numOrZero(pickTicketQty(r) ?? 1),
    0
  );
  const amountSum = rows.reduce(
    (sum, r) => sum + numOrZero(pickBookingAmount(r)),
    0
  );
  const amountPaidSum = rows.reduce(
    (sum, r) => sum + numOrZero(pickBookingAmountPaid(r)),
    0
  );
  const orderIds = rows.map((r) => pickOrderId(r)).filter((v) => v !== "");
  const sharedBookingRef = pickBookingRef(rows[0]);
  const ticketTypeSummary = formatMergedTicketTypeLabels(rows);

  return {
    ...rows[0],
    _grouped: rows.length > 1,
    _groupCount: rows.length,
    _mergedOrderIds: orderIds,
    bookingref: sharedBookingRef,
    ...(ticketTypeSummary
      ? { tickettype: ticketTypeSummary, ticketType: ticketTypeSummary }
      : {}),
    noofticket: qtySum,
    quantity: qtySum,
    qty: qtySum,
    amount: amountSum,
    amountPaid: amountPaidSum,
  };
}

function groupBookingListRows(items) {
  const list = asArray(items);
  if (list.length <= 1) return list;

  const buckets = new Map();
  const order = [];

  for (const row of list) {
    const key = pickOfflineBookingGroupKey(row);
    if (!buckets.has(key)) {
      buckets.set(key, []);
      order.push(key);
    }
    buckets.get(key).push(row);
  }

  return order.map((key) => {
    const group = buckets.get(key) ?? [];
    if (group.length <= 1) return group[0];
    return mergeOfflineBookingGroup(group);
  });
}

function pickBookingRefDisplay(row, t) {
  const ref = pickBookingRef(row);
  const orderIds = Array.isArray(row?._mergedOrderIds)
    ? row._mergedOrderIds.filter(Boolean)
    : [];
  const singleOrderId =
    row?._displayOrderId ?? pickOrderId(row);
  const titleIds =
    orderIds.length > 1
      ? orderIds
      : singleOrderId && singleOrderId !== ref
        ? [singleOrderId]
        : [];

  if (!ref) {
    return (
      <span
        className="text-muted"
        title={titleIds.length ? titleIds.join("\n") : undefined}
      >
        {dash(null, t)}
      </span>
    );
  }

  return (
    <span title={titleIds.length ? titleIds.join("\n") : undefined}>
      {ref}
      {row?._grouped && row?._groupCount > 1 ? (
        <span className="text-muted small d-block mt-1">
          {t("CLUB_SALES_BOOKING_GROUPED_REFS", { count: row._groupCount })}
        </span>
      ) : null}
    </span>
  );
}

function formatBookingTimestamp(row, t) {
  const raw = pickBookingTimestamp(row);
  if (raw == null) return dash(null, t);
  const m = moment(raw);
  return m.isValid() ? m.format("DD MMM YYYY, h:mm A") : dash(null, t);
}

function downloadCsv(filename, columns, rows) {
  const esc = (cell) => {
    const s = cell == null ? "" : String(cell);
    if (/[",\n\r]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
    return s;
  };
  const head = columns.map((c) => esc(c.header)).join(",");
  const body = rows
    .map((row) => columns.map((c) => esc(c.access(row))).join(","))
    .join("\r\n");
  const blob = new Blob([`${head}\r\n${body}`], {
    type: "text/csv;charset=utf-8;",
  });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

function sortByEventStartDesc(rows) {
  const copy = [...rows];
  copy.sort((a, b) => {
    const ta = a?.startdatetime ? new Date(a.startdatetime).getTime() : 0;
    const tb = b?.startdatetime ? new Date(b.startdatetime).getTime() : 0;
    return tb - ta;
  });
  return copy;
}

/** Null eventId rows last (general / no-event table bookings). */
function sortSalesReportByEventRows(rows) {
  const withEvent = [];
  const noEvent = [];
  for (const row of rows) {
    const id = row?.eventId ?? row?._id;
    if (id == null || id === "") noEvent.push(row);
    else withEvent.push(row);
  }
  return [...sortByEventStartDesc(withEvent), ...noEvent];
}

function sumTableBookingByEventRows(rows) {
  return rows.reduce(
    (acc, r) => ({
      bookingCount: acc.bookingCount + numOrZero(r?.bookingCount),
      guestsTotal: acc.guestsTotal + numOrZero(r?.guestsTotal),
      revenue: acc.revenue + numOrZero(r?.revenue),
      depositTotal: acc.depositTotal + numOrZero(r?.depositTotal),
      minimumSpendTotal:
        acc.minimumSpendTotal + numOrZero(r?.minimumSpendTotal),
    }),
    {
      bookingCount: 0,
      guestsTotal: 0,
      revenue: 0,
      depositTotal: 0,
      minimumSpendTotal: 0,
    }
  );
}

function pickTableBookingEventLabel(row, t) {
  const id = row?.eventId ?? row?._id;
  const title = row?.eventTitle ?? row?.title;
  if (id == null || id === "") return t("CLUB_SALES_TABLE_BOOKING_NO_EVENT");
  if (title != null && String(title).trim() !== "") return String(title).trim();
  return dash(id, t);
}

function pickEventRowTableBookings(row) {
  const raw = row?.tableBookings ?? row?.table_bookings;
  return (
    normalizeTableBookingSummaryRow(raw) ?? {
      bookingCount: 0,
      guestsTotal: 0,
      revenue: 0,
      onlineRevenue: 0,
      offlineRevenue: 0,
      depositTotal: 0,
      minimumSpendTotal: 0,
      commission: 0,
      commissionBase: 0,
      cgst: 0,
      sgst: 0,
    }
  );
}

function hasEventRowTableBookings(row) {
  return numOrZero(pickEventRowTableBookings(row).bookingCount) > 0;
}

function isTableOnlyEventRow(row) {
  const tb = pickEventRowTableBookings(row);
  return (
    numOrZero(tb.bookingCount) > 0 && numOrZero(row?.orderCount) === 0
  );
}

function fmtTableBookingCount(v, row, t) {
  if (!hasEventRowTableBookings(row) && numOrZero(v) === 0) {
    return dash(null, t);
  }
  return fmtMoney(v);
}

function fmtTableBookingMoney(v, row, t) {
  if (!hasEventRowTableBookings(row) && numOrZero(v) === 0) {
    return dash(null, t);
  }
  return fmtInrMoneyDisplay(v);
}

function fmtTableBookingOnlineMoney(tb, row, t) {
  const online = pickTableBookingOnlineRevenue(tb);
  if (!hasEventRowTableBookings(row) && online === 0) {
    return dash(null, t);
  }
  return fmtInrMoneyDisplay(online);
}

function pickTableBookingLineRef(row) {
  return (
    row?.bookingref ??
    row?.bookingRef ??
    row?.booking_ref ??
    ""
  );
}

function pickTableBookingLineServiceDate(row) {
  const raw =
    row?.servicedate ??
    row?.serviceDate ??
    row?.service_date ??
    row?.createdAt;
  if (raw == null) return null;
  if (typeof raw === "object" && raw.$date != null) return raw.$date;
  return raw;
}

function pickTableBookingLineGuest(row) {
  const user = row?.user ?? row?.userid ?? row?.guest;
  if (user && typeof user === "object") {
    return {
      name: pickBookingGuestName({ user }),
      email: pickBookingGuestEmail({ user }),
      phone: pickBookingGuestPhone({ user }),
    };
  }
  return {
    name: pickBookingGuestName(row),
    email: pickBookingGuestEmail(row),
    phone: pickBookingGuestPhone(row),
  };
}

function pickTableBookingLineTableName(row) {
  const table = row?.table;
  if (table && typeof table === "object") {
    const fromTable =
      table.displayLabel ??
      table.displaylabel ??
      table.label ??
      table.name ??
      table.title ??
      "";
    if (fromTable != null && String(fromTable).trim() !== "") {
      return String(fromTable).trim();
    }
  }
  const flat =
    row?.tableName ??
    row?.tablename ??
    row?.tableLabel ??
    row?.tablelabel ??
    row?.areaLabel ??
    row?.areaLabelDisplay ??
    row?.arealabel ??
    "";
  if (flat != null && String(flat).trim() !== "") return String(flat).trim();
  const area = row?.area;
  if (area && typeof area === "object") {
    const fromArea = area.label ?? area.name ?? area.displayLabel ?? "";
    if (fromArea != null && String(fromArea).trim() !== "") {
      return String(fromArea).trim();
    }
  }
  return "";
}

function pickTableBookingLineStaffLabel(row) {
  return (
    row?.empref ??
    row?.empRef ??
    row?.referredBy?.empref ??
    row?.referredBy?.fullname ??
    ""
  );
}

/** ONLINE vs OFFLINE for a table booking line (public checkout vs club POS). */
function normalizeKnownSalesChannel(raw) {
  if (raw == null || raw === "") return "";
  const s = String(raw).trim().toUpperCase();
  if (s === "ONLINE" || s === "OFFLINE" || s === "GUESTLIST") return s;
  return "";
}

function tableBookingLineSources(row) {
  if (!row || typeof row !== "object") return [];
  return [
    row,
    row.order,
    row.booking,
    row.tableBooking,
    row.table_booking,
    row.orderDetails,
    row.orderdetails,
    row.ticketOrder,
  ].filter((s) => s && typeof s === "object");
}

function pickSaleChannelFromSource(src) {
  if (!src || typeof src !== "object") return "";
  const direct = normalizeKnownSalesChannel(
    src?.salechannel ?? src?.saleChannel ?? src?.sale_channel
  );
  if (direct) return direct;

  if (
    src?.isOffline === true ||
    src?.isoffline === true ||
    src?.offlineSale === true ||
    src?.offline_sale === true
  ) {
    return "OFFLINE";
  }
  if (src?.isOnline === true || src?.isonline === true) return "ONLINE";

  const sourceRaw =
    src?.bookingSource ??
    src?.booking_source ??
    src?.bookedVia ??
    src?.booked_via ??
    src?.source ??
    "";
  const source = String(sourceRaw || "").trim().toUpperCase();
  if (!source) return "";
  if (
    source === "OFFLINE" ||
    source === "POS" ||
    source === "CLUB_POS" ||
    source.includes("OFFLINE")
  ) {
    return "OFFLINE";
  }
  if (
    source === "ONLINE" ||
    source === "WEB" ||
    source === "PUBLIC" ||
    source.includes("ONLINE")
  ) {
    return "ONLINE";
  }
  return "";
}

function pickTableBookingLineSaleChannel(row) {
  for (const src of tableBookingLineSources(row)) {
    const fromSource = pickSaleChannelFromSource(src);
    if (fromSource) return fromSource;
  }

  const orderRef =
    row?.orderRef ??
    row?.orderref ??
    row?.orderid ??
    row?.orderId ??
    row?.order?.orderRef ??
    row?.order?.orderref ??
    "";
  if (String(orderRef).trim().startsWith("off_")) return "OFFLINE";

  if (row?.soldBy || row?.sold_by || row?.order?.soldBy) return "OFFLINE";

  const offlinePay =
    row?.offlinepaymentmethod ??
    row?.offlinePaymentMethod ??
    row?.offline_payment_method ??
    row?.order?.offlinepaymentmethod;
  if (offlinePay != null && String(offlinePay).trim() !== "") return "OFFLINE";

  if (asArray(row?.offlineCollections ?? row?.offline_collections).length > 0) {
    return "OFFLINE";
  }
  if (numOrZero(row?.offlineCollectedTotal ?? row?.offline_collected_total) > 0) {
    return "OFFLINE";
  }

  return "ONLINE";
}

function TableBookingSaleChannelCell({ row, t }) {
  return (
    <ChannelBadge value={pickTableBookingLineSaleChannel(row)} t={t} />
  );
}

function pickTableBookingLineBookerName(row) {
  const booker = row?.booker;
  if (booker && typeof booker === "object") {
    const name =
      booker.fullname ?? booker.fullName ?? booker.name ?? booker.buyername;
    if (name != null && String(name).trim() !== "") {
      return String(name).trim();
    }
  }
  return pickTableBookingLineGuest(row).name || "";
}

function formatOfflineCollectionsCell(row, t) {
  const list = asArray(row?.offlineCollections ?? row?.offline_collections);
  const total = numOrZero(
    row?.offlineCollectedTotal ?? row?.offline_collected_total
  );
  if (list.length > 0) {
    return list
      .map((c) => {
        const method = c?.method ?? c?.type ?? c?.paymentMethod ?? "Gate";
        const amt = c?.amount ?? c?.value ?? c?.collected;
        return `${method}: ${fmtInrCurrency(amt)}`;
      })
      .join("; ");
  }
  if (total > 0) return fmtInrCurrency(total);
  return dash(null, t);
}

function normalizeOfflineByEmployeeRow(raw) {
  if (!raw || typeof raw !== "object") return null;
  const soldByUserId =
    raw.soldByUserId ??
    raw.sold_by_user_id ??
    raw.soldbyuserid ??
    raw._id;
  if (soldByUserId == null || soldByUserId === "") return null;
  return {
    soldByUserId: String(soldByUserId),
    fullname: raw.fullname ?? raw.fullName ?? raw.name ?? "",
    email: raw.email ?? "",
    orderCount: numOrZero(raw.orderCount ?? raw.order_count),
    ticketsSold: numOrZero(raw.ticketsSold ?? raw.tickets_sold),
    revenue: pickManagerTicketRevenue(raw),
  };
}

function normalizeTableBookingSummaryRow(raw) {
  if (!raw || typeof raw !== "object") return null;
  const onlineRevenue = numOrZero(
    raw.onlineRevenue ?? raw.online_revenue ?? raw.revenue
  );
  return {
    bookingCount: numOrZero(raw.bookingCount ?? raw.booking_count),
    guestsTotal: numOrZero(raw.guestsTotal ?? raw.guests_total),
    revenue: onlineRevenue,
    onlineRevenue,
    offlineRevenue: numOrZero(raw.offlineRevenue ?? raw.offline_revenue),
    depositTotal: numOrZero(raw.depositTotal ?? raw.deposit_total),
    minimumSpendTotal: numOrZero(
      raw.minimumSpendTotal ?? raw.minimum_spend_total
    ),
    commission: numOrZero(raw.commission),
    commissionBase: numOrZero(raw.commissionBase ?? raw.commission_base),
    cgst: numOrZero(raw.cgst),
    sgst: numOrZero(raw.sgst),
  };
}

function normalizeTableBookingByEventRow(raw) {
  if (!raw || typeof raw !== "object") return null;
  const eventIdRaw = raw.eventId ?? raw.event_id;
  const onlineRevenue = numOrZero(
    raw.onlineRevenue ?? raw.online_revenue ?? raw.revenue
  );
  return {
    eventId: eventIdRaw != null && eventIdRaw !== "" ? eventIdRaw : null,
    eventTitle: raw.eventTitle ?? raw.event_title ?? raw.title ?? "",
    startdatetime: raw.startdatetime ?? raw.startDatetime ?? raw.start_datetime,
    bookingCount: numOrZero(raw.bookingCount ?? raw.booking_count),
    guestsTotal: numOrZero(raw.guestsTotal ?? raw.guests_total),
    revenue: onlineRevenue,
    onlineRevenue,
    offlineRevenue: numOrZero(raw.offlineRevenue ?? raw.offline_revenue),
    depositTotal: numOrZero(raw.depositTotal ?? raw.deposit_total),
    minimumSpendTotal: numOrZero(
      raw.minimumSpendTotal ?? raw.minimum_spend_total
    ),
    commission: numOrZero(raw.commission),
    commissionBase: numOrZero(raw.commissionBase ?? raw.commission_base),
    cgst: numOrZero(raw.cgst),
    sgst: numOrZero(raw.sgst),
  };
}

function normalizeTableBookingByEmployeeRow(raw) {
  if (!raw || typeof raw !== "object") return null;
  return {
    referredByUserId:
      raw.referredByUserId ??
      raw.referred_by_user_id ??
      raw.referredbyuserid ??
      raw.soldByUserId ??
      raw.sold_by_user_id,
    empref: raw.empref ?? raw.empRef ?? raw.emp_ref ?? "",
    fullname: raw.fullname ?? raw.fullName ?? raw.name ?? "",
    email: raw.email ?? "",
    role: raw.role ?? "",
    bookingCount: numOrZero(raw.bookingCount ?? raw.booking_count),
    guestsTotal: numOrZero(raw.guestsTotal ?? raw.guests_total),
    revenue: numOrZero(raw.revenue),
    minimumSpendTotal: numOrZero(
      raw.minimumSpendTotal ?? raw.minimum_spend_total
    ),
  };
}

/** Supports camelCase and snake_case from GET /club/reports/sales. */
function normalizeTableBookingsFromReport(data) {
  const raw =
    data?.tableBookings ??
    data?.table_bookings ??
    data?.tableBooking ??
    data?.table_booking;
  if (raw == null) return null;
  if (typeof raw !== "object") return null;

  const summaryRaw = raw.summary ?? raw.Summary;
  const byEventRaw = raw.byEvent ?? raw.by_event ?? raw.byevent;
  const byEmployeeRaw = raw.byEmployee ?? raw.by_employee ?? raw.byemployee;

  const byEvent = asArray(byEventRaw)
    .map(normalizeTableBookingByEventRow)
    .filter(Boolean);
  const byEmployee = asArray(byEmployeeRaw)
    .map(normalizeTableBookingByEmployeeRow)
    .filter(Boolean);

  let summary = normalizeTableBookingSummaryRow(summaryRaw);
  if (!summary && (byEvent.length > 0 || byEmployee.length > 0)) {
    summary = sumTableBookingByEventRows(byEvent);
  }
  if (!summary) {
    summary = {
      bookingCount: 0,
      guestsTotal: 0,
      revenue: 0,
      depositTotal: 0,
      minimumSpendTotal: 0,
    };
  }

  return { summary, byEvent, byEmployee };
}

function numOrZero(v) {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
}

/** Total ticket face value (online + offline, excl. guestlist). */
function pickManagerTicketRevenue(row) {
  return numOrZero(row?.revenue);
}

function pickEventTicketSales(row, adminFinByEvent) {
  return pickEventAdminFinancials(row, adminFinByEvent).ticketSales;
}

function pickEventOnlineRevenue(row) {
  return numOrZero(row?.onlineRevenue ?? row?.online_revenue);
}

function pickEventOfflineRevenue(row) {
  return numOrZero(row?.offlineRevenue ?? row?.offline_revenue);
}

function pickTableBookingOnlineRevenue(tb) {
  if (!tb || typeof tb !== "object") return 0;
  return numOrZero(tb.onlineRevenue ?? tb.revenue);
}

function pickTableBookingOfflineRevenue(tb) {
  if (!tb || typeof tb !== "object") return 0;
  return numOrZero(tb.offlineRevenue ?? tb.offline_revenue);
}

function isGuestlistChannelRow(row) {
  return pickSalesChannelKey(row).toUpperCase() === "GUESTLIST";
}

function pickCategoryGuestlistCount(row) {
  return numOrZero(
    row?.guestlistCount ??
      row?.guestlistcount ??
      row?.guestlistTickets ??
      row?.guestlist_tickets
  );
}

function pickCategoryOnlineRevenue(row) {
  return numOrZero(row?.onlineRevenue ?? row?.online_revenue);
}

function pickCategoryOfflineRevenue(row) {
  return numOrZero(row?.offlineRevenue ?? row?.offline_revenue);
}

function pickCategoryTotalRevenue(row) {
  const total = numOrZero(row?.revenue);
  if (total > 0) return total;
  const combined =
    pickCategoryOnlineRevenue(row) + pickCategoryOfflineRevenue(row);
  return combined > 0 ? combined : total;
}

/** byCategory tier that is complimentary-only (not paid + guestlist on same tier). */
function isCategoryGuestlistOnlyRow(row) {
  if (!row || typeof row !== "object") return false;
  if (row?.isguestlist || row?.isGuestlist) return true;
  const price = unwrapMongoNumber(pickScalarFromRow(row, BY_EVENT_PRICE_KEYS));
  if (price != null && price !== "" && numOrZero(price) > 0) return false;
  const paidSold = numOrZero(row?.paidTicketsSold ?? row?.paid_tickets_sold);
  const onTickets = numOrZero(row?.onlineTickets ?? row?.online_tickets);
  const offTickets = numOrZero(row?.offlineTickets ?? row?.offline_tickets);
  if (paidSold > 0 || onTickets > 0 || offTickets > 0) return false;
  if (pickCategoryTotalRevenue(row) > 0) return false;
  if (pickCategoryOnlineRevenue(row) > 0 || pickCategoryOfflineRevenue(row) > 0) {
    return false;
  }
  return price != null && price !== "" && numOrZero(price) === 0;
}

function fmtCategoryMoneyAmount(amount, t) {
  if (!hasDefinedMoney(amount)) return dash(null, t);
  return fmtInrMoneyDisplay(amount);
}

function fmtCategoryTicketCount(value, t) {
  if (value == null || value === "") return dash(null, t);
  return numOrZero(value);
}

function fmtCategoryGuestlistCount(row, t) {
  const count = pickCategoryGuestlistCount(row);
  if (count > 0) return count;
  if (isCategoryGuestlistOnlyRow(row)) return count;
  return dash(null, t);
}

function isComplimentaryTicketRow(row) {
  if (!row || typeof row !== "object") return false;
  if (isGuestlistChannelRow(row)) return true;
  if (row?.isguestlist || row?.isGuestlist) return true;
  if (numOrZero(pickGuestlistTickets(row)) > 0) return true;
  const price = unwrapMongoNumber(pickScalarFromRow(row, BY_EVENT_PRICE_KEYS));
  if (price != null && price !== "" && numOrZero(price) === 0) return true;
  return false;
}

/** Paid ticket revenue only — excludes complimentary guestlist (always ₹0). */
function pickPaidSummaryRevenue(summary) {
  if (!summary || typeof summary !== "object") return 0;
  const on =
    summary.online && typeof summary.online === "object" ? summary.online : {};
  const off =
    summary.offline && typeof summary.offline === "object" ? summary.offline : {};
  return numOrZero(on.revenue) + numOrZero(off.revenue);
}

function fmtChannelRevenueDisplay(row, t) {
  if (isGuestlistChannelRow(row)) return dash(null, t);
  const rev = pickManagerTicketRevenue(row);
  return numOrZero(rev) > 0 ? fmtCurrency(rev) : dash(null, t);
}

function fmtTicketRevenueDisplay(row, t) {
  if (isComplimentaryTicketRow(row)) return dash(null, t);
  if (hasDefinedMoney(row?.revenue)) {
    return fmtInrMoneyDisplay(pickManagerTicketRevenue(row));
  }
  const rev = pickManagerTicketRevenue(row);
  return rev > 0 ? fmtInrCurrency(rev) : dash(null, t);
}

function fmtEventOnlineRevenueDisplay(row, t) {
  if (isComplimentaryTicketRow(row)) return dash(null, t);
  if (
    hasDefinedMoney(row?.onlineRevenue) ||
    hasDefinedMoney(row?.online_revenue)
  ) {
    return fmtInrMoneyDisplay(pickEventOnlineRevenue(row));
  }
  const online = pickEventOnlineRevenue(row);
  return online > 0 ? fmtInrMoneyDisplay(online) : dash(null, t);
}

function fmtAdminFinancialCell(fin, key) {
  if (!fin) return "—";
  return fmtInrMoneyDisplay(fin[key]);
}

function unwrapMongoNumber(v) {
  if (v == null || v === "") return null;
  if (typeof v === "object") {
    if (v.$numberDecimal != null) return Number(v.$numberDecimal);
    if (v.$numberInt != null) return Number(v.$numberInt);
    if (v.$numberLong != null) return Number(v.$numberLong);
    if (v.$numberDouble != null) return Number(v.$numberDouble);
  }
  const n = Number(v);
  return Number.isFinite(n) ? n : v;
}

function byEventRowSources(row) {
  if (!row || typeof row !== "object") return [];
  return [
    row,
    row.event,
    row.eventDetails,
    row.ticketStats,
    row.stats,
    row.metrics,
    row.pricing,
    row.inventoryStats,
  ].filter((s) => s && typeof s === "object");
}

const BY_EVENT_BOOKING_PERCENTAGE_KEYS = [
  "bookingpercentage",
  "bookingPercentage",
  "booking_percentage",
  "commissionRate",
  "commission_rate",
  "rizztixCommissionRate",
  "rizztix_commission_rate",
];

function pickScalarFromRow(row, keys) {
  for (const src of byEventRowSources(row)) {
    for (const key of keys) {
      const raw = src[key];
      if (raw == null || raw === "") continue;
      const v = unwrapMongoNumber(raw);
      if (v != null && v !== "") return v;
    }
  }
  return null;
}

function formatBookingPercentageRate(n) {
  const num = Number(n);
  if (!Number.isFinite(num) || num <= 0) return null;
  return Number.isInteger(num) ? num : Number(num.toFixed(2));
}

function buildEventBookingPercentageLookups(eventOptions) {
  const byId = new Map();
  const byTitle = new Map();
  for (const ev of asArray(eventOptions)) {
    let rate = null;
    for (const key of BY_EVENT_BOOKING_PERCENTAGE_KEYS) {
      const raw = ev?.[key];
      if (raw == null || raw === "") continue;
      rate = formatBookingPercentageRate(unwrapMongoNumber(raw));
      if (rate != null) break;
    }
    if (rate == null) continue;
    const id = ev?._id ?? ev?.eventId ?? ev?.id;
    if (id != null && id !== "") byId.set(String(id), rate);
    const title = ev?.title ?? ev?.eventTitle ?? ev?.eventtitle;
    if (title != null && String(title).trim() !== "") {
      byTitle.set(String(title).trim().toLowerCase(), rate);
    }
  }
  return { byId, byTitle };
}

function pickEventBookingPercentage(
  row,
  adminFinByEvent,
  extraSources = [],
  eventBookingLookups = null
) {
  if (!row || typeof row !== "object") return null;

  const id = row?.eventId ?? row?._id;
  const idStr = id != null && id !== "" ? String(id) : "";
  if (idStr && eventBookingLookups?.byId instanceof Map) {
    const fromCatalog = eventBookingLookups.byId.get(idStr);
    if (fromCatalog != null) return fromCatalog;
  }
  const rowTitle = row?.eventTitle ?? row?.title ?? row?.eventtitle;
  if (rowTitle && eventBookingLookups?.byTitle instanceof Map) {
    const fromTitle = eventBookingLookups.byTitle.get(
      String(rowTitle).trim().toLowerCase()
    );
    if (fromTitle != null) return fromTitle;
  }

  const fromMap =
    idStr && adminFinByEvent ? adminFinByEvent.get(idStr) ?? null : null;

  const sources = [row, fromMap, ...asArray(extraSources)].filter(
    (src) => src && typeof src === "object"
  );

  for (const src of sources) {
    for (const key of BY_EVENT_BOOKING_PERCENTAGE_KEYS) {
      const raw = src[key];
      if (raw == null || raw === "") continue;
      const rate = formatBookingPercentageRate(unwrapMongoNumber(raw));
      if (rate != null) return rate;
    }
    const nested = pickScalarFromRow(src, BY_EVENT_BOOKING_PERCENTAGE_KEYS);
    if (nested == null || nested === "") continue;
    const rate = formatBookingPercentageRate(nested);
    if (rate != null) return rate;
  }

  return null;
}

/** Shared header rate when one event or all visible events use the same positive %. */
function resolveSharedCommissionHeaderRate(
  rows,
  adminFinByEvent,
  extraSources = [],
  eventBookingLookups = null
) {
  const rates = asArray(rows)
    .map((row) =>
      pickEventBookingPercentage(
        row,
        adminFinByEvent,
        extraSources,
        eventBookingLookups
      )
    )
    .filter((rate) => rate != null);
  if (!rates.length) return null;
  const first = rates[0];
  if (rates.length === 1 || rates.every((rate) => rate === first)) return first;
  return null;
}

function fmtCommissionColumnLabel(
  rows,
  adminFinByEvent,
  extraSources,
  eventBookingLookups,
  t
) {
  const sharedRate = resolveSharedCommissionHeaderRate(
    rows,
    adminFinByEvent,
    extraSources,
    eventBookingLookups
  );
  if (sharedRate != null) {
    return t("CLUB_SALES_FIN_COMMISSION_WITH_RATE", { rate: sharedRate });
  }
  return t("CLUB_SALES_FIN_COMMISSION");
}

const BY_EVENT_PRICE_KEYS = [
  "price",
  "ticketprice",
  "ticketPrice",
  "minTicketPrice",
  "minPrice",
  "ticketPriceMin",
  "lowestTicketPrice",
];

const BY_EVENT_PRICE_MAX_KEYS = [
  "priceMax",
  "pricemax",
  "price_max",
  "ticketpricemax",
  "ticketPriceMax",
  "maxTicketPrice",
  "maxPrice",
  "highestTicketPrice",
];

const BY_EVENT_INVENTORY_KEYS = [
  "inventory",
  "inventoryTotal",
  "totalInventory",
  "ticketInventory",
  "totaltickets",
  "totalTickets",
  "ticketCapacity",
  "totalTicketCapacity",
];

const BY_EVENT_AVAILABLE_KEYS = [
  "availableForSale",
  "availableforsale",
  "available_for_sale",
  "available",
  "ticketsAvailable",
  "remainingTickets",
  "ticketsRemaining",
];

function deriveByEventTicketTierStats(row) {
  const tickets = Array.isArray(row?.tickets) ? row.tickets : [];
  if (!tickets.length) return {};

  const prices = tickets
    .map((t) => Number(unwrapMongoNumber(t?.ticketprice ?? t?.price)))
    .filter(Number.isFinite);
  const inventory = tickets.reduce(
    (sum, t) =>
      sum +
      numOrZero(
        unwrapMongoNumber(t?.totaltickets ?? t?.inventory ?? t?.totalInventory)
      ),
    0
  );
  let available = 0;
  for (const t of tickets) {
    const explicit = pickScalarFromRow(t, BY_EVENT_AVAILABLE_KEYS);
    if (explicit != null) {
      available += numOrZero(explicit);
      continue;
    }
    const total = numOrZero(
      unwrapMongoNumber(t?.totaltickets ?? t?.inventory ?? t?.totalInventory)
    );
    const sold = numOrZero(
      unwrapMongoNumber(t?.ticketssold ?? t?.ticketsSold ?? t?.sold)
    );
    available += Math.max(0, total - sold);
  }

  return {
    price: prices.length ? Math.min(...prices) : null,
    priceMax: prices.length ? Math.max(...prices) : null,
    inventory,
    availableForSale: available,
  };
}

/** Flatten byEvent row so price / inventory / tableBookings are on the row we render. */
function normalizeByEventSalesRow(row) {
  if (!row || typeof row !== "object") return row;
  const derived = deriveByEventTicketTierStats(row);
  const price =
    pickScalarFromRow(row, BY_EVENT_PRICE_KEYS) ?? derived.price ?? null;
  const priceMax =
    pickScalarFromRow(row, BY_EVENT_PRICE_MAX_KEYS) ?? derived.priceMax ?? null;
  const inventory =
    pickScalarFromRow(row, BY_EVENT_INVENTORY_KEYS) ?? derived.inventory ?? null;
  const availableForSale =
    pickScalarFromRow(row, BY_EVENT_AVAILABLE_KEYS) ??
    derived.availableForSale ??
    null;
  const tableBookings = pickEventRowTableBookings(row);
  const bookingpercentage =
    pickScalarFromRow(row, BY_EVENT_BOOKING_PERCENTAGE_KEYS) ?? null;

  return {
    ...row,
    ...(price != null ? { price: unwrapMongoNumber(price) } : {}),
    ...(priceMax != null ? { priceMax: unwrapMongoNumber(priceMax) } : {}),
    ...(inventory != null ? { inventory: unwrapMongoNumber(inventory) } : {}),
    ...(availableForSale != null
      ? { availableForSale: unwrapMongoNumber(availableForSale) }
      : {}),
    ...(bookingpercentage != null
      ? { bookingpercentage: unwrapMongoNumber(bookingpercentage) }
      : {}),
    tableBookings,
  };
}

function sumByEventRows(rows) {
  return rows.reduce(
    (acc, r) => {
      const gl = pickGuestlistTicketStatsFromRow(r);
      return {
        orderCount: acc.orderCount + numOrZero(r?.orderCount),
        ticketsSold: acc.ticketsSold + numOrZero(r?.ticketsSold),
        revenue: acc.revenue + pickManagerTicketRevenue(r),
        onlineTickets: acc.onlineTickets + numOrZero(r?.onlineTickets),
        offlineTickets: acc.offlineTickets + numOrZero(r?.offlineTickets),
        guestlistTickets:
          acc.guestlistTickets + numOrZero(pickGuestlistTickets(r)),
        guestlistInventory: acc.guestlistInventory + gl.inventory,
        guestlistAvailable: acc.guestlistAvailable + gl.available,
        inventory:
          acc.inventory + numOrZero(pickScalarFromRow(r, BY_EVENT_INVENTORY_KEYS)),
        available:
          acc.available + numOrZero(pickScalarFromRow(r, BY_EVENT_AVAILABLE_KEYS)),
      };
    },
    {
      orderCount: 0,
      ticketsSold: 0,
      revenue: 0,
      onlineTickets: 0,
      offlineTickets: 0,
      guestlistTickets: 0,
      guestlistInventory: 0,
      guestlistAvailable: 0,
      inventory: 0,
      available: 0,
    }
  );
}

function emptySalesSummaryShape() {
  const zero = { orderCount: 0, ticketsSold: 0, revenue: 0 };
  return {
    overall: { ...zero },
    online: { ...zero },
    offline: { ...zero },
    guestlist: { ...zero },
  };
}

/** Shown when event type + date range match zero events (no API mixed data). */
function buildEmptyClubSalesResult({
  clubId = null,
  eventType = null,
  dateFrom = "",
  dateTo = "",
} = {}) {
  return {
    clubId,
    filters: {
      dateFrom: dateFrom || null,
      dateTo: dateTo || null,
      eventType: eventType || null,
      saleChannel: null,
      guestlist: null,
      breakdown: null,
      soldByUserId: null,
      eventId: null,
    },
    summary: emptySalesSummaryShape(),
    byEvent: [],
    byChannel: [],
    byClub: [],
    byCategory: [],
    offlineByEmployee: [],
    breakdownSeries: [],
    tableBookings: {
      summary: {
        bookingCount: 0,
        guestsTotal: 0,
        revenue: 0,
        depositTotal: 0,
        minimumSpendTotal: 0,
      },
      byEmployee: [],
      byEvent: [],
    },
  };
}

function shouldUseEmptyScopedSalesReport(eventScope, eventId, scopedEventIds) {
  return (
    !eventId?.trim() &&
    eventScope &&
    eventScope !== EVENT_SCOPES.ALL &&
    scopedEventIds instanceof Set &&
    scopedEventIds.size === 0
  );
}

function sumByClubRows(rows) {
  return rows.reduce(
    (acc, r) => ({
      orderCount: acc.orderCount + numOrZero(r?.orderCount),
      ticketsSold: acc.ticketsSold + numOrZero(r?.ticketsSold),
      revenue: acc.revenue + pickManagerTicketRevenue(r),
    }),
    { orderCount: 0, ticketsSold: 0, revenue: 0 }
  );
}

function fmtCountCell(v, t) {
  const n = unwrapMongoNumber(v);
  return n != null && n !== "" ? String(n) : dash(null, t);
}

function pickCategoryPrice(row) {
  const v = unwrapMongoNumber(pickScalarFromRow(row, BY_EVENT_PRICE_KEYS));
  if (v == null || v === "") return null;
  return fmtCurrency(v);
}

function eventRowMatchesSelectedEvent(row, selectedEventId) {
  const id = String(selectedEventId ?? "").trim();
  if (!id) return false;
  const rowId = row?.eventId ?? row?._id;
  return rowId != null && String(rowId) === id;
}

function EventCategorySubRows({ categories, t }) {
  return asArray(categories).map((row, i) => {
    const ticketType = row?.ticketType ?? row?.tickettype;
    const categoryDesc = row?.categoryDesc ?? row?.categorydesc;
    const priceStr = pickCategoryPrice(row);
    const isGuestlistOnly = isCategoryGuestlistOnlyRow(row);
    return (
      <tr
        key={`cat-${i}-${row?.ticketTypeId ?? row?.tickettypeid ?? i}`}
        className="club-sales-category-subrow"
      >
        <td className="ps-4">
          <div className="fw-medium ps-3">{dash(ticketType, t)}</div>
          {categoryDesc != null && String(categoryDesc).trim() !== "" ? (
            <div className="small text-muted ps-3">
              {String(categoryDesc).trim()}
            </div>
          ) : null}
        </td>
        <td className="club-sales-col-ticket-sold">
          <TicketSoldCell row={row} soldOnly={isGuestlistOnly} />
        </td>
        <td className="text-end fw-medium tabular-nums">
          {isGuestlistOnly
            ? dash(null, t)
            : fmtCategoryMoneyAmount(pickCategoryOnlineRevenue(row), t)}
        </td>
        <td className="small">{priceStr ?? dash(null, t)}</td>
        <td className="text-end tabular-nums">
          {isGuestlistOnly
            ? dash(null, t)
            : fmtCategoryTicketCount(row?.onlineTickets, t)}
        </td>
        <td className="text-end tabular-nums">
          {isGuestlistOnly
            ? dash(null, t)
            : fmtCategoryTicketCount(row?.offlineTickets, t)}
        </td>
        {/* <td className="text-end tabular-nums">
          {isGuestlistOnly
            ? dash(null, t)
            : fmtCategoryMoneyAmount(pickCategoryOfflineRevenue(row), t)}
        </td> */}
        <td className="text-end pe-4 tabular-nums">
          {fmtCategoryGuestlistCount(row, t)}
        </td>
      </tr>
    );
  });
}

function pickTableBookingAdminFinancials(tb) {
  return deriveCommissionGstSplit(numOrZero(tb?.commission));
}

function mergeTableBookingDisplayRow(tbRow, salesRow, t) {
  const fromReport = tbRow ? normalizeTableBookingByEventRow(tbRow) : null;
  const fromSales = salesRow ? pickEventRowTableBookings(salesRow) : null;
  const tb = fromReport ?? fromSales ?? {
    bookingCount: 0,
    guestsTotal: 0,
    revenue: 0,
    onlineRevenue: 0,
    minimumSpendTotal: 0,
    commission: 0,
    cgst: 0,
    sgst: 0,
  };
  const eventId =
    fromReport?.eventId ??
    (salesRow?.eventId ?? salesRow?._id ?? null);
  const eventTitle =
    fromReport?.eventTitle ??
    salesRow?.eventTitle ??
    salesRow?.title ??
    "";
  const startdatetime =
    fromReport?.startdatetime ?? salesRow?.startdatetime ?? null;

  return {
    eventId,
    eventTitle,
    startdatetime,
    orderCount: numOrZero(salesRow?.orderCount),
    tableBookings: tb,
    bookingCount: tb.bookingCount,
    guestsTotal: tb.guestsTotal,
    revenue: tb.revenue,
    onlineRevenue: tb.onlineRevenue,
    minimumSpendTotal: tb.minimumSpendTotal,
    commission: tb.commission,
    commissionBase: tb.commissionBase,
    cgst: tb.cgst,
    sgst: tb.sgst,
    _label: pickTableBookingEventLabel(
      { eventId, eventTitle, title: eventTitle, _id: eventId },
      t
    ),
  };
}

function buildTableBookingByEventDisplayRows(displayByEvent, tableBookingsByEvent, t) {
  const tbById = new Map();
  for (const row of asArray(tableBookingsByEvent)) {
    const id =
      row?.eventId != null && row.eventId !== ""
        ? String(row.eventId)
        : "";
    if (id) tbById.set(id, row);
  }

  const seen = new Set();
  const rows = [];

  for (const salesRow of asArray(displayByEvent)) {
    const id = String(salesRow?.eventId ?? salesRow?._id ?? "");
    if (id) seen.add(id);
    rows.push(mergeTableBookingDisplayRow(id ? tbById.get(id) : null, salesRow, t));
  }

  for (const tbRow of asArray(tableBookingsByEvent)) {
    const id =
      tbRow?.eventId != null && tbRow.eventId !== ""
        ? String(tbRow.eventId)
        : "";
    if (id && !seen.has(id)) {
      rows.push(mergeTableBookingDisplayRow(tbRow, null, t));
      seen.add(id);
    }
  }

  return sortSalesReportByEventRows(rows);
}

function EventSelectedTableBookingSection({
  tb,
  eventRow,
  onOpenLines,
  isAdmin,
  t,
}) {
  const hasTable = numOrZero(tb?.bookingCount) > 0;
  const eventTb = eventRow ? pickEventRowTableBookings(eventRow) : null;
  const commissionSource =
    eventTb && numOrZero(eventTb.commission) > 0 ? eventTb : tb;
  const tableFin = pickTableBookingAdminFinancials(commissionSource);
  const commissionLabel = t("CLUB_SALES_FIN_COMMISSION");

  return (
    <Card className="border-0 shadow-sm mb-4 club-sales-event-table-booking-section">
      <Card.Header className="club-sales-card-header py-3 border-bottom">
        <span className="fw-semibold">{t("CLUB_SALES_TABLE_BOOKING_SECTION")}</span>
      </Card.Header>
      <Card.Body className="p-0">
        <div className="table-responsive">
          <Table hover className="mb-0 align-middle club-sales-ref-table">
            <thead className="club-sales-table-head">
              <tr className="small text-uppercase" style={{ fontSize: "0.68rem" }}>
                <th className="text-end py-3 border-0 ps-4">
                  {t("CLUB_SALES_TABLE_BOOKING_COL_BOOKINGS")}
                </th>
                <th className="text-end py-3 border-0">
                  {t("CLUB_SALES_TABLE_BOOKING_COL_GUESTS")}
                </th>
                <th className="text-end py-3 border-0">
                  {t("CLUB_SALES_COL_TABLE_REVENUE")}
                </th>
                <th
                  className={`text-end py-3 border-0${
                    isAdmin ? "" : " pe-4"
                  }`}
                >
                  {t("CLUB_SALES_TABLE_BOOKING_COL_MIN_SPEND")}
                </th>
                {isAdmin ? (
                  <>
                    <th className="text-end py-3 border-0">{commissionLabel}</th>
                    <th className="text-end py-3 border-0">
                      {t("CLUB_SALES_FIN_CGST")}
                    </th>
                    <th className="text-end py-3 border-0">
                      {t("CLUB_SALES_FIN_SGST")}
                    </th>
                  </>
                ) : null}
                <th className="text-end py-3 border-0 pe-4" />
              </tr>
            </thead>
            <tbody>
              <tr className="club-sales-table-booking-subrow fw-medium">
                <td className="text-end tabular-nums ps-4">
                  {fmtTableBookingCount(tb?.bookingCount, eventRow, t)}
                </td>
                <td className="text-end tabular-nums">
                  {fmtTableBookingCount(tb?.guestsTotal, eventRow, t)}
                </td>
                <td className="text-end tabular-nums">
                  {fmtTableBookingOnlineMoney(tb, eventRow, t)}
                </td>
                <td
                  className={`text-end tabular-nums${
                    isAdmin ? "" : " pe-4"
                  }`}
                >
                  {fmtTableBookingMoney(tb?.minimumSpendTotal, eventRow, t)}
                </td>
                {isAdmin ? (
                  <>
                    <td className="text-end tabular-nums">
                      <RizztixCommissionBreakdownCell fin={tableFin} t={t} />
                    </td>
                    <td className="text-end tabular-nums">
                      {fmtAdminFinancialCell(tableFin, "cgst")}
                    </td>
                    <td className="text-end tabular-nums">
                      {fmtAdminFinancialCell(tableFin, "sgst")}
                    </td>
                  </>
                ) : null}
                <td className="text-end pe-4">
                  {hasTable && onOpenLines ? (
                    <Button
                      variant="outline-light"
                      size="sm"
                      type="button"
                      onClick={onOpenLines}
                    >
                      {t("CLUB_SALES_TABLE_BOOKING_VIEW_LINES")}
                    </Button>
                  ) : (
                    dash(null, t)
                  )}
                </td>
              </tr>
            </tbody>
          </Table>
        </div>
      </Card.Body>
    </Card>
  );
}

function TableBookingsByEventSection({
  rows,
  summary,
  eventBookingLookups,
  adminFinByEvent,
  isAdmin,
  onOpenTableBookingLines,
  t,
}) {
  const list = asArray(rows);
  if (!list.length) return null;
  const totals =
    summary && typeof summary === "object"
      ? summary
      : sumTableBookingByEventRows(list);

  return (
    <Card className="border-0 shadow-sm mb-4 club-sales-event-table-booking-section">
      <Card.Header className="club-sales-card-header py-3 border-bottom">
        <span className="fw-semibold d-block">
          {t("CLUB_SALES_TABLE_BOOKING_BY_EVENT_TITLE")}
        </span>
        <span className="text-muted small fw-normal d-block mt-1">
          {t("CLUB_SALES_TABLE_BOOKING_BY_EVENT_HINT")}
        </span>
      </Card.Header>
      <Card.Body className="p-0">
        <div className="table-responsive">
          <Table hover className="mb-0 align-middle club-sales-ref-table">
            <thead className="club-sales-table-head">
              <tr className="small text-uppercase" style={{ fontSize: "0.68rem" }}>
                <th className="ps-4 py-3 border-0">{t("CLUB_SALES_COL_EVENT")}</th>
                <th className="py-3 border-0">{t("CLUB_SALES_REF_COL_EVENT_DATE")}</th>
                <th className="text-end py-3 border-0">
                  {t("CLUB_SALES_TABLE_BOOKING_COL_BOOKINGS")}
                </th>
                <th className="text-end py-3 border-0">
                  {t("CLUB_SALES_TABLE_BOOKING_COL_GUESTS")}
                </th>
                <th className="text-end py-3 border-0">
                  {t("CLUB_SALES_COL_TABLE_REVENUE")}
                </th>
                <th
                  className={`text-end py-3 border-0${
                    isAdmin ? "" : " pe-3"
                  }`}
                >
                  {t("CLUB_SALES_TABLE_BOOKING_COL_MIN_SPEND")}
                </th>
                {isAdmin ? (
                  <>
                    <th className="text-end py-3 border-0">
                      {t("CLUB_SALES_FIN_COMMISSION")}
                    </th>
                    <th className="text-end py-3 border-0">
                      {t("CLUB_SALES_FIN_CGST")}
                    </th>
                    <th className="text-end py-3 border-0">
                      {t("CLUB_SALES_FIN_SGST")}
                    </th>
                  </>
                ) : null}
                <th className="text-end py-3 border-0 pe-3" />
              </tr>
            </thead>
            <tbody>
              {list.map((row, i) => {
                const eventKey = row?.eventId ?? i;
                const hasTable = numOrZero(row?.bookingCount) > 0;
                const tableFin = pickTableBookingAdminFinancials(row);
                const bookingRate = pickEventBookingPercentage(
                  row,
                  adminFinByEvent,
                  [],
                  eventBookingLookups
                );
                const openLines = () => {
                  const id = row?.eventId;
                  onOpenTableBookingLines(
                    id != null && id !== "" ? String(id) : null
                  );
                };
                return (
                  <tr
                    key={`tb-ev-${eventKey}`}
                    className={
                      hasTable ? "club-sales-table-booking-row-clickable" : ""
                    }
                  >
                    <td className="ps-4 fw-medium">{row._label}</td>
                    <td className="small text-nowrap">
                      {row?.startdatetime
                        ? moment(row.startdatetime).format("DD MMM YYYY, h:mm A")
                        : dash(null, t)}
                    </td>
                    <td className="text-end tabular-nums">
                      {fmtTableBookingCount(row.bookingCount, row, t)}
                    </td>
                    <td className="text-end tabular-nums">
                      {fmtTableBookingCount(row.guestsTotal, row, t)}
                    </td>
                    <td className="text-end tabular-nums">
                      {fmtTableBookingOnlineMoney(row.tableBookings ?? row, row, t)}
                    </td>
                    <td
                      className={`text-end tabular-nums${
                        isAdmin ? "" : " pe-3"
                      }`}
                    >
                      {fmtTableBookingMoney(row.minimumSpendTotal, row, t)}
                    </td>
                    {isAdmin ? (
                      <>
                        <td className="text-end tabular-nums">
                          <RizztixCommissionBreakdownCell
                            fin={tableFin}
                            bookingRate={bookingRate}
                            t={t}
                          />
                        </td>
                        <td className="text-end tabular-nums">
                          {fmtAdminFinancialCell(tableFin, "cgst")}
                        </td>
                        <td className="text-end tabular-nums">
                          {fmtAdminFinancialCell(tableFin, "sgst")}
                        </td>
                      </>
                    ) : null}
                    <td className="text-end pe-3 club-sales-col-action">
                      {hasTable ? (
                        <Button
                          variant="outline-light"
                          size="sm"
                          type="button"
                          className="text-nowrap"
                          onClick={openLines}
                        >
                          {t("CLUB_SALES_TABLE_BOOKING_VIEW_LINES")}
                        </Button>
                      ) : (
                        dash(null, t)
                      )}
                    </td>
                  </tr>
                );
              })}
              <tr className="club-sales-table-total-row fw-bold">
                <td className="ps-4">{t("CLUB_SALES_REF_ROW_TOTAL")}</td>
                <td>—</td>
                <td className="text-end tabular-nums">{totals.bookingCount ?? 0}</td>
                <td className="text-end tabular-nums">{totals.guestsTotal ?? 0}</td>
                <td className="text-end tabular-nums">
                  {fmtInrMoneyDisplay(pickTableBookingOnlineRevenue(totals))}
                </td>
                <td
                  className={`text-end tabular-nums${
                    isAdmin ? "" : " pe-3"
                  }`}
                >
                  {fmtInrCurrency(totals.minimumSpendTotal)}
                </td>
                {isAdmin ? (
                  <>
                    <td className="text-end tabular-nums">
                      <RizztixCommissionBreakdownCell
                        fin={pickTableBookingAdminFinancials(totals)}
                        t={t}
                      />
                    </td>
                    <td className="text-end tabular-nums">
                      {fmtAdminFinancialCell(
                        pickTableBookingAdminFinancials(totals),
                        "cgst"
                      )}
                    </td>
                    <td className="text-end tabular-nums">
                      {fmtAdminFinancialCell(
                        pickTableBookingAdminFinancials(totals),
                        "sgst"
                      )}
                    </td>
                  </>
                ) : null}
                <td className="pe-3" />
              </tr>
            </tbody>
          </Table>
        </div>
      </Card.Body>
    </Card>
  );
}

function EventCategoryBreakdownBlock({ categories, t }) {
  if (!asArray(categories).length) return null;
  return (
    <EventCategorySubRows categories={categories} t={t} />
  );
}

function CategoryBreakdownTableCard({
  categories,
  resolvedEventTitle,
  selectedEventId,
  t,
}) {
  if (!asArray(categories).length) return null;

  return (
    <Card className="border-0 shadow-sm mb-4">
      <Card.Header className="club-sales-card-header py-3 border-bottom d-flex flex-column flex-sm-row flex-wrap align-items-sm-start justify-content-between gap-2">
        <div>
          <span className="fw-semibold d-block">
            {t("CLUB_SALES_CATEGORY_BREAKDOWN_TITLE")}
          </span>
          <span className="text-muted small fw-normal d-block mt-1">
            {t("CLUB_SALES_CATEGORY_BREAKDOWN_HINT", {
              event: resolvedEventTitle || dash(selectedEventId, t),
            })}
          </span>
        </div>
      </Card.Header>
      <Card.Body className="p-0">
        <div className="table-responsive club-sales-ticket-breakdown-wrap">
          <Table hover className="mb-0 align-middle club-sales-ref-table">
            <thead className="club-sales-table-head">
              <tr className="small text-uppercase" style={{ fontSize: "0.68rem" }}>
                <th className="ps-4 py-3 border-0">
                  {t("CLUB_SALES_CATEGORY_COL_CATEGORY")}
                </th>
                <th className="py-3 border-0 club-sales-col-ticket-sold">
                  {t("CLUB_SALES_REF_COL_TICKETS_SOLD")}
                </th>
                <th className="text-end py-3 border-0">
                  {t("CLUB_SALES_CATEGORY_COL_ONLINE_REVENUE")}
                </th>
                <th className="py-3 border-0">{t("CLUB_SALES_REF_COL_PRICE")}</th>
                <th className="text-end py-3 border-0">
                  {t("CLUB_SALES_REF_COL_ONLINE")}
                </th>
                <th className="text-end py-3 border-0">
                  {t("CLUB_SALES_REF_COL_OFFLINE")}
                </th>
                <th className="text-end pe-4 py-3 border-0">
                  {t("CLUB_SALES_CATEGORY_COL_GUESTLIST")}
                </th>
              </tr>
            </thead>
            <tbody>
              <EventCategoryBreakdownBlock categories={categories} t={t} />
            </tbody>
          </Table>
        </div>
      </Card.Body>
    </Card>
  );
}

function pickEventPrice(row) {
  return pickScalarFromRow(row, BY_EVENT_PRICE_KEYS);
}

function pickEventPriceMax(row) {
  return pickScalarFromRow(row, BY_EVENT_PRICE_MAX_KEYS);
}

/** Per-event price from byEvent.price / priceMax (or legacy min/max fields). */
function pickPriceRange(row) {
  const price = pickEventPrice(row);
  const priceMax = pickEventPriceMax(row);

  if (price == null || price === "") {
    if (priceMax != null && priceMax !== "") return fmtCurrency(priceMax);
    return null;
  }
  if (priceMax == null || priceMax === "" || Number(price) === Number(priceMax)) {
    return fmtCurrency(price);
  }
  return `${fmtCurrency(price)} – ${fmtCurrency(priceMax)}`;
}

/** Total row: min price to max priceMax across events — never sum prices. */
function pickPriceRangeTotal(rows) {
  let minP = null;
  let maxP = null;
  for (const row of rows) {
    const price = pickEventPrice(row);
    const priceMax = pickEventPriceMax(row);

    if (price != null && price !== "" && Number.isFinite(Number(price))) {
      const p = Number(price);
      minP = minP == null ? p : Math.min(minP, p);
      if (priceMax == null || priceMax === "") {
        maxP = maxP == null ? p : Math.max(maxP, p);
      }
    }
    if (priceMax != null && priceMax !== "" && Number.isFinite(Number(priceMax))) {
      const pm = Number(priceMax);
      maxP = maxP == null ? pm : Math.max(maxP, pm);
      if (price == null || price === "") {
        minP = minP == null ? pm : Math.min(minP, pm);
      }
    }
  }
  if (minP == null && maxP == null) return null;
  if (minP != null && maxP != null && minP !== maxP) {
    return `${fmtCurrency(minP)} – ${fmtCurrency(maxP)}`;
  }
  return fmtCurrency(minP ?? maxP);
}

function hasDefinedNumericField(rows, keys) {
  return rows.some((r) => pickScalarFromRow(r, keys) != null);
}

function pickInventory(row) {
  const v = pickScalarFromRow(row, BY_EVENT_INVENTORY_KEYS);
  return v != null && v !== "" ? String(v) : null;
}

function pickAvailable(row) {
  const v = pickScalarFromRow(row, BY_EVENT_AVAILABLE_KEYS);
  return v != null && v !== "" ? String(v) : null;
}

function pickGuestlistTickets(row) {
  const v = row?.guestlistTickets ?? row?.guestlist_tickets;
  if (v == null || v === "") return null;
  return v;
}

function pickSalesChannelKey(row) {
  const raw = row?.channel ?? row?._id ?? row?.salechannel ?? row?.saleChannel;
  return raw == null || raw === "" ? "" : String(raw).trim();
}

const SALES_CHANNEL_ORDER = {
  ONLINE: 0,
  OFFLINE: 1,
  ON_GROUND: 2,
  GUESTLIST: 3,
};

const TICKET_BY_CHANNEL_ROWS = [
  { key: "ONLINE", labelKey: "CLUB_SALES_KPI_ONLINE" },
  { key: "OFFLINE", labelKey: "CLUB_SALES_KPI_OFFLINE" },
  { key: "ON_GROUND", labelKey: "CLUB_SALES_KPI_ON_GROUND" },
  { key: "GUESTLIST", labelKey: "CLUB_SALES_KPI_GUESTLIST" },
];

/** Always four rows, in API order. Missing channels stay at 0 tickets. */
function buildTicketByChannelRows(rows) {
  const byKey = new Map();
  for (const row of asArray(rows)) {
    const key = pickSalesChannelKey(row).toUpperCase();
    if (!key || byKey.has(key)) continue;
    byKey.set(key, row);
  }
  return TICKET_BY_CHANNEL_ROWS.map(({ key, labelKey }) => {
    const raw = byKey.get(key)?.ticketsSold;
    const ticketsSold =
      raw == null || raw === "" || Number.isNaN(Number(raw)) ? 0 : Number(raw);
    return { key, labelKey, ticketsSold };
  });
}

function sortSalesChannelRows(rows) {
  return [...asArray(rows)].sort((a, b) => {
    const ka = pickSalesChannelKey(a).toUpperCase();
    const kb = pickSalesChannelKey(b).toUpperCase();
    return (
      (SALES_CHANNEL_ORDER[ka] ?? 99) - (SALES_CHANNEL_ORDER[kb] ?? 99) ||
      ka.localeCompare(kb)
    );
  });
}

function exportClubSalesReportToCsv({
  data,
  selectedEventId,
  resolvedEventTitle,
  isAdmin,
  t,
}) {
  if (data == null || typeof data !== "object") return;

  const byEvent = sortSalesReportByEventRows(
    asArray(data.byEvent ?? data.by_event).map(normalizeByEventSalesRow)
  );
  const byCategory = asArray(data.byCategory ?? data.by_category);
  const showCategoryBreakdown =
    Boolean(selectedEventId?.trim()) && byCategory.length > 0;
  const tableBookings = normalizeTableBookingsFromReport(data);
  const tableBookingSummary = tableBookings?.summary ?? {
    bookingCount: 0,
    guestsTotal: 0,
    revenue: 0,
    depositTotal: 0,
    minimumSpendTotal: 0,
  };

  const adminFinancials =
    isAdmin &&
    data.adminFinancials &&
    typeof data.adminFinancials === "object"
      ? data.adminFinancials
      : null;
  const adminFinByEvent = buildAdminFinancialsByEventMap(adminFinancials);
  const adminFinByEventRows = asArray(adminFinancials?.byEvent);
  const showAdminEventFinancials =
    isAdmin &&
    (byEvent.some((row) => eventHasAdminFinancialFields(row)) ||
      Boolean(adminFinancials));

  if (showCategoryBreakdown) {
    const eventLabel = resolvedEventTitle || selectedEventId || "";
    const categoryRows = byCategory.map((row) => {
      const isGuestlistOnly = isCategoryGuestlistOnlyRow(row);
      return {
        rowType: "Category",
        name: row?.ticketType ?? row?.tickettype ?? "",
        ticketsSold: row?.ticketsSold,
        onlineRevenue: isGuestlistOnly ? "" : pickCategoryOnlineRevenue(row),
        // offlineRevenue: isGuestlistOnly ? "" : pickCategoryOfflineRevenue(row),
        price: pickCategoryPrice(row) ?? "",
        onlineTickets: isGuestlistOnly ? "" : row?.onlineTickets,
        offlineTickets: isGuestlistOnly ? "" : row?.offlineTickets,
        guestlistCount: pickCategoryGuestlistCount(row),
      };
    });
    const tableRow = {
      rowType: "Table bookings",
      name: t("CLUB_SALES_TABLE_BOOKING_SECTION"),
      ticketsSold: "",
      onlineRevenue: "",
      price: "",
      onlineTickets: "",
      offlineTickets: "",
      bookingCount: tableBookingSummary.bookingCount,
      guestsTotal: tableBookingSummary.guestsTotal,
      tableRevenue: pickTableBookingOnlineRevenue(tableBookingSummary),
    };
    downloadCsv(
      `event-report-${eventLabel || "export"}-${Date.now()}.csv`,
      [
        { header: "Row type", access: (r) => r.rowType },
        { header: "Event / category", access: (r) => r.name },
        { header: "Tickets sold", access: (r) => r.ticketsSold },
        { header: "Online revenue", access: (r) => r.onlineRevenue },
        { header: "Price", access: (r) => r.price },
        { header: "Online tickets", access: (r) => r.onlineTickets },
        { header: "Offline tickets (paid)", access: (r) => r.offlineTickets },
        // { header: "Offline revenue", access: (r) => r.offlineRevenue },
        { header: "Guestlist", access: (r) => r.guestlistCount },
        { header: "Table bookings", access: (r) => r.bookingCount },
        { header: "Table guests", access: (r) => r.guestsTotal },
        { header: "Table revenue", access: (r) => r.tableRevenue },
      ],
      [...categoryRows, tableRow]
    );
    return;
  }

  if (!byEvent.length) return;

  const baseColumns = [
    { header: "Show / event", access: (r) => r.eventTitle ?? r.title },
    { header: "Start", access: (r) => r.startdatetime ?? "" },
    { header: "Price range", access: (r) => pickPriceRange(r) ?? "" },
    { header: "Inventory", access: (r) => pickInventory(r) ?? "" },
    { header: "Available", access: (r) => pickAvailable(r) ?? "" },
    { header: "Booked tickets", access: (r) => r.ticketsSold },
    { header: "Ticket sales", access: (r) => pickManagerTicketRevenue(r) },
    {
      header: "Online revenue",
      access: (r) => pickEventOnlineRevenue(r),
    },
    {
      header: "Offline revenue",
      access: (r) => pickEventOfflineRevenue(r),
    },
    { header: "Online tickets", access: (r) => r.onlineTickets },
    { header: "Offline tickets (paid)", access: (r) => r.offlineTickets },
    { header: "Guestlist tickets", access: (r) => pickGuestlistTickets(r) },
    { header: "Ticket orders", access: (r) => r.orderCount },
    {
      header: "Table bookings",
      access: (r) => pickEventRowTableBookings(r).bookingCount,
    },
    {
      header: "Table guests",
      access: (r) => pickEventRowTableBookings(r).guestsTotal,
    },
    {
      header: "Table revenue (online)",
      access: (r) => pickTableBookingOnlineRevenue(pickEventRowTableBookings(r)),
    },
    {
      header: "Table min spend",
      access: (r) => pickEventRowTableBookings(r).minimumSpendTotal,
    },
  ];
  const adminColumns = showAdminEventFinancials
    ? [
        {
          header: "Online revenue",
          access: (r) =>
            pickAdminFinForEventRow(r, adminFinByEvent)?.onlineRevenue,
        },
        {
          header: "Commission",
          access: (r) =>
            pickAdminFinForEventRow(r, adminFinByEvent)?.commission,
        },
        {
          header: "Commission base",
          access: (r) =>
            pickAdminFinForEventRow(r, adminFinByEvent)?.commissionBase,
        },
        {
          header: "CGST",
          access: (r) => pickAdminFinForEventRow(r, adminFinByEvent)?.cgst,
        },
        {
          header: "SGST",
          access: (r) => pickAdminFinForEventRow(r, adminFinByEvent)?.sgst,
        },
        {
          header: "Status",
          access: (r) => {
            const fin = pickAdminFinForEventRow(r, adminFinByEvent);
            if (fin?.eventEnded === true) return "Ended";
            if (fin?.eventEnded === false) return "Live";
            return "";
          },
        },
      ]
    : [];
  downloadCsv(
    `sales-by-event-${Date.now()}.csv`,
    [...baseColumns, ...adminColumns],
    byEvent
  );
}

function ChannelBadge({ value, t }) {
  const raw = value == null || value === "" ? "" : String(value).trim();
  if (!raw) {
    return (
      <span className="text-muted small">{t("CLUB_SALES_VALUE_EMPTY")}</span>
    );
  }
  const u = raw.toUpperCase();
  if (u === "OFFLINE") {
    return (
      <Badge pill bg="success" className="fw-normal px-2 py-1">
        OFFLINE
      </Badge>
    );
  }
  if (u === "ONLINE") {
    return (
      <Badge pill bg="primary" className="fw-normal px-2 py-1">
        ONLINE
      </Badge>
    );
  }
  if (u === "ON_GROUND") {
    return (
      <Badge pill bg="warning" className="fw-normal px-2 py-1 text-dark">
        ON GROUND
      </Badge>
    );
  }
  if (u === "GUESTLIST") {
    return (
      <Badge pill bg="info" className="fw-normal px-2 py-1 text-dark">
        GUESTLIST
      </Badge>
    );
  }
  if (/^[a-f0-9]{24}$/i.test(raw)) {
    return (
      <span className="text-muted small">{t("CLUB_SALES_VALUE_EMPTY")}</span>
    );
  }
  return (
    <Badge pill bg="secondary" className="fw-normal px-2 py-1">
      {raw}
    </Badge>
  );
}

function EmptySection({ t }) {
  return (
    <div className="text-center small py-4 px-2 rounded-3 club-sales-empty">
      {t("CLUB_SALES_NO_DATA_SECTION")}
    </div>
  );
}

/**
 * KPI row: uses `summary` from GET /club/reports/sales.
 * - overall: all matched PAID orders (tickets + revenue + order count).
 * - Tickets by channel: byChannel rows ONLINE, OFFLINE, ON_GROUND, GUESTLIST (ticketsSold).
 */
function GuestlistKpiSummaryCards({ summary, t }) {
  const gl =
    summary?.guestlist && typeof summary.guestlist === "object"
      ? summary.guestlist
      : null;
  if (!gl) return null;
  return (
    <Row className="g-3 mb-4 club-sales-kpi-row">
      <Col md={6}>
        <Card className="border-0 shadow-sm h-100 club-sales-kpi-card">
          <Card.Body className="py-3">
            <div className="club-sales-kpi-label text-uppercase text-muted small mb-2">
              {t("GUESTLIST_REPORT_ORDERS")}
            </div>
            <div className="club-sales-kpi-value fs-3 fw-bold tabular-nums">
              {gl.orderCount ?? "—"}
            </div>
          </Card.Body>
        </Card>
      </Col>
      <Col md={6}>
        <Card className="border-0 shadow-sm h-100 club-sales-kpi-card">
          <Card.Body className="py-3">
            <div className="club-sales-kpi-label text-uppercase text-muted small mb-2">
              {t("GUESTLIST_REPORT_TICKETS")}
            </div>
            <div className="club-sales-kpi-value fs-3 fw-bold tabular-nums">
              {gl.ticketsSold ?? "—"}
            </div>
          </Card.Body>
        </Card>
      </Col>
    </Row>
  );
}

function KpiSummaryCards({ summary, byChannel, t }) {
  if (!summary || typeof summary !== "object" || !summary.overall) return null;
  const o = summary.overall;
  const channelRows = buildTicketByChannelRows(byChannel);
  const capRaw =
    o.totalTicketCapacity ??
    o.ticketCapacity ??
    o.inventoryTotal ??
    o.totalInventory ??
    null;
  const cap = capRaw != null && capRaw !== "" ? Number(capRaw) : null;
  const sold = numOrZero(o.ticketsSold);
  const pct =
    cap != null && cap > 0 && Number.isFinite(sold)
      ? ((sold / cap) * 100).toFixed(2)
      : null;
  const interested =
    summary.interestedCount ??
    summary.notifyMeCount ??
    o.interestedCount ??
    o.notifyMeCount;

  return (
    <div className="mb-2 club-sales-tickets-kpi-section">
      <h6 className="fw-semibold mb-3">{t("CLUB_SALES_TICKETS_SECTION")}</h6>
      <Row className="g-3 mb-4 club-sales-kpi-row">
      <Col md={4}>
        <Card className="border-0 shadow-sm h-100 club-sales-kpi-card">
          <Card.Body className="py-3 d-flex flex-column">
            <div className="club-sales-kpi-label text-uppercase text-muted small mb-2">
              {t("CLUB_SALES_REF_LABEL_TICKETS")}
            </div>
            <div className="club-sales-kpi-value fs-3 fw-bold tabular-nums">
              {cap != null && cap > 0
                ? `${fmtMoney(sold)} / ${fmtMoney(cap)}`
                : fmtMoney(o.ticketsSold)}
            </div>
            {pct != null ? (
              <div className="club-sales-kpi-pct text-muted small mt-1">
                ({pct}%)
              </div>
            ) : null}
            <div className="club-sales-kpi-sub text-muted small mt-2">
              {t("CLUB_SALES_REF_ORDERS_LINE", { n: o.orderCount ?? "—" })}
            </div>
          </Card.Body>
        </Card>
      </Col>
      <Col md={4}>
        <Card className="border-0 shadow-sm h-100 club-sales-kpi-card">
          <Card.Body className="py-3 d-flex flex-column">
            <div className="club-sales-kpi-label text-uppercase text-muted small mb-2">
              {t("CLUB_SALES_REF_COL_TOTAL_REVENUE")}
            </div>
            <div className="club-sales-kpi-value fs-3 fw-bold tabular-nums">
              {fmtCurrency(pickPaidSummaryRevenue(summary))}
            </div>
          </Card.Body>
        </Card>
      </Col>
      <Col md={4}>
        <Card className="border-0 shadow-sm h-100 club-sales-kpi-card">
          <Card.Body className="py-3 d-flex flex-column">
            <div className="club-sales-kpi-label text-uppercase text-muted small mb-2">
              {t("CLUB_SALES_REF_LABEL_CHANNEL_MIX")}
            </div>
            <div className="club-sales-kpi-mix small">
              {channelRows.map((row, index) => (
                <div
                  key={row.key}
                  className={`d-flex justify-content-between py-1${
                    index < channelRows.length - 1
                      ? " border-bottom border-secondary border-opacity-25"
                      : ""
                  }`}
                >
                  <span className="text-muted">{t(row.labelKey)}</span>
                  <span className="fw-semibold tabular-nums">
                    {row.ticketsSold} {t("CLUB_SALES_REF_TICKETS_UNIT")}
                  </span>
                </div>
              ))}
            </div>
            {interested != null && interested !== "" ? (
              <div className="club-sales-kpi-sub text-muted small mt-2">
                {t("CLUB_SALES_REF_LABEL_INTERESTED")}: {String(interested)}
              </div>
            ) : null}
          </Card.Body>
        </Card>
      </Col>
    </Row>
    </div>
  );
}

/**
 * Admin-only financial settlement (adminFinancials from GET /club/reports/sales).
 * Hidden when event is selected but not yet ended.
 */
function AdminFinancialSettlementSection({
  adminFinancials,
  selectedEventId,
  byCategory,
  resolvedEventTitle,
  t,
}) {
  if (!adminFinancials || typeof adminFinancials !== "object") return null;

  const eventMeta =
    adminFinancials.event && typeof adminFinancials.event === "object"
      ? adminFinancials.event
      : null;
  const eventPending =
    Boolean(selectedEventId?.trim()) &&
    eventMeta &&
    eventMeta.eventEnded === false;

  if (eventPending) {
    const eventLabel =
      resolvedEventTitle ||
      eventMeta.eventTitle ||
      selectedEventId ||
      "";
    return (
      <Card className="border-0 shadow-sm mb-4 club-sales-settlement-section">
        <Card.Body className="py-4">
          <h6 className="fw-semibold mb-2">
            {t("CLUB_SALES_SETTLEMENT_TITLE")}
          </h6>
          <p className="small text-muted mb-0">
            {t("CLUB_SALES_SETTLEMENT_PENDING", { event: eventLabel })}
          </p>
        </Card.Body>
      </Card>
    );
  }

  const fin = adminFinancials;
  const settlementCards = [
    {
      label: t("CLUB_SALES_FIN_TOTAL_SALES"),
      value: fmtInrMoneyDisplay(fin.overallSale),
    },
    {
      label: t("CLUB_SALES_FIN_TICKET_SALES"),
      value: fmtInrMoneyDisplay(fin.ticketSales),
    },
    {
      label: t("CLUB_SALES_FIN_COMMISSION"),
      value: fmtInrMoneyDisplay(fin.commission),
    },
    {
      label: t("CLUB_SALES_FIN_CGST"),
      value: fmtInrMoneyDisplay(fin.cgst),
    },
    {
      label: t("CLUB_SALES_FIN_SGST"),
      value: fmtInrMoneyDisplay(fin.sgst),
    },
    {
      label: t("CLUB_SALES_FIN_TICKETS_SOLD"),
      value: fin.ticketsSold ?? "—",
      numeric: true,
    },
    {
      label: t("CLUB_SALES_FIN_CHECKOUTS"),
      value: fin.salesCount ?? "—",
      numeric: true,
    },
  ];

  return (
    <div className="club-sales-settlement-section mb-4">
      <div className="mb-3">
        <h6 className="fw-semibold mb-1">{t("CLUB_SALES_SETTLEMENT_TITLE")}</h6>
        {eventMeta?.eventTitle || resolvedEventTitle ? (
          <p className="small text-muted mb-0">
            {eventMeta?.eventTitle || resolvedEventTitle}
            {eventMeta?.enddatetime
              ? ` · ${moment(eventMeta.enddatetime).format("DD MMM YYYY, h:mm A")}`
              : ""}
          </p>
        ) : null}
      </div>
      <Row className="g-3 club-sales-kpi-row">
        {settlementCards.map((card) => (
          <Col key={card.label} xs={6} md={4} xl={3}>
            <Card className="border-0 shadow-sm h-100 club-sales-kpi-card club-sales-settlement-card">
              <Card.Body className="py-3">
                <div className="club-sales-kpi-label text-uppercase text-muted small mb-2">
                  {card.label}
                </div>
                <div
                  className={`club-sales-kpi-value fs-5 fw-bold ${
                    card.numeric ? "tabular-nums" : ""
                  }`}
                >
                  {card.value}
                </div>
              </Card.Body>
            </Card>
          </Col>
        ))}
      </Row>
    </div>
  );
}

function TableBookingKpiSummaryCards({ tableBookings, t, clubId }) {
  const tb =
    tableBookings?.summary && typeof tableBookings.summary === "object"
      ? tableBookings.summary
      : {
          bookingCount: 0,
          guestsTotal: 0,
          revenue: 0,
          depositTotal: 0,
          minimumSpendTotal: 0,
        };

  const bookingCount = numOrZero(tb.bookingCount);

  if (bookingCount === 0) {
    return (
      <div className="mb-4 club-sales-table-booking-section">
        <div className="d-flex flex-wrap align-items-center justify-content-between gap-2 mb-3">
          <h6 className="fw-semibold mb-0">
            {t("CLUB_SALES_TABLE_BOOKING_SECTION")}
          </h6>
          <ShareTableSheetButton clubId={clubId} />
        </div>
        <div className="club-sales-empty-hint text-center small text-muted py-4 px-3 rounded-3 border border-secondary border-opacity-25">
          {t("CLUB_SALES_TABLE_BOOKING_EMPTY")}
        </div>
      </div>
    );
  }

  const cards = [
    {
      label: t("CLUB_SALES_TABLE_BOOKING_KPI_BOOKINGS"),
      value: tb.bookingCount ?? "—",
      numeric: true,
    },
    {
      label: t("CLUB_SALES_TABLE_BOOKING_KPI_GUESTS"),
      value: tb.guestsTotal ?? "—",
      numeric: true,
    },
    {
      label: t("CLUB_SALES_TABLE_BOOKING_KPI_REVENUE"),
      value: fmtInrCurrency(tb.revenue),
    },
    {
      label: t("CLUB_SALES_TABLE_BOOKING_KPI_MIN_SPEND"),
      value: fmtInrCurrency(tb.minimumSpendTotal),
    },
  ];

  return (
    <div className="mb-4 club-sales-table-booking-section">
      <div className="d-flex flex-wrap align-items-center justify-content-between gap-2 mb-3">
        <h6 className="fw-semibold mb-0">
          {t("CLUB_SALES_TABLE_BOOKING_SECTION")}
        </h6>
        <ShareTableSheetButton clubId={clubId} />
      </div>
      <Row className="g-3 club-sales-kpi-row">
        {cards.map((card) => (
          <Col key={card.label} xs={6} md={4} xl>
            <Card className="border-0 shadow-sm h-100 club-sales-kpi-card club-sales-table-booking-kpi-card">
              <Card.Body className="py-3">
                <div className="club-sales-kpi-label text-uppercase text-muted small mb-2">
                  {card.label}
                </div>
                <div
                  className={`club-sales-kpi-value fs-5 fw-bold ${
                    card.numeric ? "tabular-nums" : ""
                  }`}
                >
                  {card.value}
                </div>
              </Card.Body>
            </Card>
          </Col>
        ))}
      </Row>
    </div>
  );
}

function catalogKindLabel(kind, t) {
  const key = String(kind ?? "").trim().toUpperCase();
  if (key === "ADDON") return t("CATALOG_KIND_ADDON");
  if (key === "PREORDER") return t("CATALOG_KIND_PREORDER");
  return key ? String(kind) : dash(null, t);
}

function catalogSourceLabel(source, t) {
  const key = String(source ?? "").trim().toUpperCase();
  if (key === "TICKET") return t("CATALOG_SOURCE_TICKET");
  if (key === "TABLE") return t("CATALOG_SOURCE_TABLE");
  return key ? String(source) : dash(null, t);
}

function catalogSaleChannelLabel(channel, t) {
  const key = String(channel ?? "").trim().toUpperCase();
  if (key === "ONLINE") return t("CLUB_SALES_KPI_ONLINE");
  if (key === "OFFLINE") return t("CLUB_SALES_CHANNEL_OFFLINE");
  if (key === "ON_GROUND") return t("CLUB_SALES_KPI_ON_GROUND");
  if (key === "GUESTLIST") return t("CLUB_SALES_KPI_GUESTLIST");
  return key ? String(channel) : dash(null, t);
}

function formatCatalogPurchasedAt(value, t) {
  if (value == null || value === "") return dash(null, t);
  const parsed = moment(value);
  return parsed.isValid()
    ? parsed.format("DD MMM YYYY, h:mm A")
    : String(value);
}

function CatalogSnapshotBuyerCell({ line, t }) {
  const name = String(line?.buyername ?? "").trim();
  const phone = String(line?.buyerphone ?? "").trim();
  const email = String(line?.buyeremail ?? "").trim();
  return (
    <div className="small">
      <div className="fw-medium">{name || dash(null, t)}</div>
      {phone ? <div className="club-sales-guest-meta">{phone}</div> : null}
      {email ? (
        <div
          className="club-sales-guest-meta text-truncate"
          style={{ maxWidth: "14rem" }}
        >
          {email}
        </div>
      ) : null}
    </div>
  );
}

const CATALOG_CARD_QUERY = {
  ticketAddon: { catalogSource: "TICKET", catalogKind: "ADDON" },
  tableAddon: { catalogSource: "TABLE", catalogKind: "ADDON" },
  preorder: { catalogKind: "PREORDER" },
};

const CATALOG_PAGE_SIZES = [10, 20, 50, 100];

function catalogLinesForCard(lines, cardKey) {
  const query = CATALOG_CARD_QUERY[cardKey];
  if (!query) return [];
  return asArray(lines).filter((line) => {
    const kind = String(line?.kind ?? "").trim().toUpperCase();
    const source = String(line?.source ?? "").trim().toUpperCase();
    if (kind !== query.catalogKind) return false;
    if (query.catalogSource && source !== query.catalogSource) return false;
    return true;
  });
}

function pickCatalogSnapshotPage(block, fallbackPage, fallbackLimit) {
  const snap =
    block?.catalogSnapshots && typeof block.catalogSnapshots === "object"
      ? block.catalogSnapshots
      : null;
  const candidates = [snap, block].filter(
    (item) => item && typeof item === "object"
  );
  const pageSource =
    candidates.find(
      (item) =>
        Array.isArray(item.lines) &&
        (item.page != null || item.total != null || item.totalPages != null)
    ) ||
    candidates.find((item) => Array.isArray(item.lines)) ||
    {};
  const limit = Math.min(
    100,
    Math.max(1, Number(pageSource.limit) || Number(fallbackLimit) || 10)
  );
  const page = Math.max(1, Number(pageSource.page) || Number(fallbackPage) || 1);
  const total = Math.max(0, Number(pageSource.total) || 0);
  const totalPages = Math.max(
    0,
    Number(pageSource.totalPages) || (total > 0 ? Math.ceil(total / limit) : 0)
  );
  return {
    lines: Array.isArray(pageSource.lines) ? pageSource.lines : [],
    page,
    limit,
    total,
    totalPages,
  };
}

function CatalogSnapshotLinesTable({ lines, t }) {
  if (!lines.length) {
    return (
      <div className="text-center small py-4 px-3 club-sales-empty border-0 rounded-0">
        {t("CATALOG_PURCHASES_EMPTY")}
      </div>
    );
  }
  return (
    <div className="table-responsive">
      <Table hover className="mb-0 align-middle club-sales-ref-table">
        <thead className="club-sales-table-head">
          <tr className="small text-uppercase" style={{ fontSize: "0.7rem" }}>
            <th className="ps-4 py-3 border-0">{t("CATALOG_COL_BUYER")}</th>
            <th className="py-3 border-0">{t("CATALOG_COL_PRODUCT")}</th>
            <th className="py-3 border-0">{t("CATALOG_COL_TYPE")}</th>
            <th className="py-3 border-0">{t("CATALOG_COL_SOURCE")}</th>
            <th className="py-3 border-0">{t("CATALOG_COL_CHANNEL")}</th>
            <th className="text-end py-3 border-0">{t("CATALOG_COL_QTY")}</th>
            <th className="text-end py-3 border-0">{t("CATALOG_COL_AMOUNT")}</th>
            <th className="py-3 border-0">{t("CATALOG_COL_EVENT")}</th>
            <th className="py-3 border-0">{t("CATALOG_COL_REF")}</th>
            <th className="pe-4 py-3 border-0">{t("CATALOG_COL_WHEN")}</th>
          </tr>
        </thead>
        <tbody>
          {lines.map((line, index) => (
            <tr key={line?.recordId || `catalog-line-${index}`}>
              <td className="ps-4">
                <CatalogSnapshotBuyerCell line={line} t={t} />
              </td>
              <td className="fw-medium">{dash(line?.name, t)}</td>
              <td>{catalogKindLabel(line?.kind, t)}</td>
              <td>{catalogSourceLabel(line?.source, t)}</td>
              <td>{catalogSaleChannelLabel(line?.salechannel, t)}</td>
              <td className="text-end tabular-nums">
                {line?.qty == null || line.qty === "" ? 0 : line.qty}
              </td>
              <td className="text-end fw-medium tabular-nums">
                {fmtCurrency(numOrZero(line?.lineTotal))}
              </td>
              <td>{dash(line?.eventTitle, t)}</td>
              <td className="font-monospace small">{dash(line?.ref, t)}</td>
              <td className="pe-4 text-nowrap">
                {formatCatalogPurchasedAt(line?.purchasedAt, t)}
              </td>
            </tr>
          ))}
        </tbody>
      </Table>
    </div>
  );
}

function CatalogSnapshotKpiCards({ data, t, onLoadCatalogLines }) {
  const [selectedKey, setSelectedKey] = useState(null);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailError, setDetailError] = useState("");
  const snap =
    data?.catalogSnapshots && typeof data.catalogSnapshots === "object"
      ? data.catalogSnapshots
      : {};
  const cards = [
    {
      key: "ticketAddon",
      label: t("CATALOG_KPI_TICKET_ADDON"),
      value: fmtCurrency(numOrZero(snap.ticketAddonTotal)),
      clickable: true,
    },
    {
      key: "tableAddon",
      label: t("CATALOG_KPI_TABLE_ADDON"),
      value: fmtCurrency(numOrZero(snap.tableAddonTotal)),
      clickable: true,
    },
    {
      key: "preorder",
      label: t("CATALOG_KPI_PREORDER"),
      value: fmtCurrency(numOrZero(snap.preorderTotal)),
      clickable: true,
    },
    {
      key: "preorderExtra",
      label: t("CATALOG_KPI_PREORDER_EXTRA"),
      value: fmtCurrency(numOrZero(snap.preorderExtraOnline)),
      clickable: false,
    },
  ];
  const selectedCard = cards.find((card) => card.key === selectedKey) || null;
  const selectedLines = selectedCard
    ? catalogLinesForCard(detail?.lines, selectedCard.key)
    : [];
  const detailPage = Math.max(1, Number(detail?.page) || page);
  const detailTotalPages = Math.max(0, Number(detail?.totalPages) || 0);
  const detailTotal = Math.max(0, Number(detail?.total) || 0);

  useEffect(() => {
    if (!selectedKey || typeof onLoadCatalogLines !== "function") {
      setDetail(null);
      setDetailLoading(false);
      return undefined;
    }
    let cancelled = false;
    setDetailLoading(true);
    setDetailError("");
    onLoadCatalogLines({ cardKey: selectedKey, page, limit })
      .then((result) => {
        if (!cancelled) setDetail(result);
      })
      .catch((err) => {
        if (!cancelled) {
          setDetail({
            lines: [],
            page,
            limit,
            total: 0,
            totalPages: 0,
          });
          setDetailError(
            typeof err === "string"
              ? err
              : err?.message || t("CLUB_SALES_LOAD_ERROR")
          );
        }
      })
      .finally(() => {
        if (!cancelled) setDetailLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [selectedKey, page, limit, onLoadCatalogLines]);

  const toggleCard = (key) => {
    setPage(1);
    setSelectedKey((current) => (current === key ? null : key));
  };

  return (
    <div className="mb-4">
      <h6 className="fw-semibold mb-3">{t("CATALOG_KPI_SECTION")}</h6>
      <Row className="g-3 club-sales-kpi-row">
        {cards.map((card) => {
          const isSelected = selectedKey === card.key;
          return (
            <Col key={card.key} xs={6} md={3}>
              <Card
                className={`border-0 shadow-sm h-100 club-sales-kpi-card${
                  card.clickable ? " club-sales-catalog-kpi-card" : ""
                }${isSelected ? " is-selected" : ""}`}
                role={card.clickable ? "button" : undefined}
                tabIndex={card.clickable ? 0 : undefined}
                aria-pressed={card.clickable ? isSelected : undefined}
                onClick={card.clickable ? () => toggleCard(card.key) : undefined}
                onKeyDown={
                  card.clickable
                    ? (event) => {
                        if (event.key === "Enter" || event.key === " ") {
                          event.preventDefault();
                          toggleCard(card.key);
                        }
                      }
                    : undefined
                }
              >
                <Card.Body className="py-3">
                  <div className="club-sales-kpi-label text-uppercase text-muted small mb-2">
                    {card.label}
                  </div>
                  <div className="club-sales-kpi-value fs-5 fw-bold tabular-nums">
                    {card.value}
                  </div>
                </Card.Body>
              </Card>
            </Col>
          );
        })}
      </Row>
      {selectedCard ? (
        <Card className="border-0 shadow-sm mt-3">
          <Card.Header className="club-sales-card-header py-3 border-bottom d-flex flex-wrap align-items-center justify-content-between gap-2">
            <span className="fw-semibold">{selectedCard.label}</span>
            <Form.Select
              size="sm"
              className="club-sales-catalog-page-size"
              aria-label={t("CATALOG_PAGE_SIZE")}
              value={limit}
              onChange={(event) => {
                const next = Math.min(100, Math.max(1, Number(event.target.value) || 10));
                setLimit(next);
                setPage(1);
              }}
            >
              {CATALOG_PAGE_SIZES.map((size) => (
                <option key={size} value={size}>
                  {size}
                </option>
              ))}
            </Form.Select>
          </Card.Header>
          <Card.Body className="p-0">
            {detailLoading ? (
              <div className="d-flex align-items-center gap-2 small px-4 py-4">
                <Spinner animation="border" size="sm" role="status" />
                <span>{t("LOADING")}</span>
              </div>
            ) : detailError ? (
              <div className="text-center small py-4 px-3 text-danger">
                {detailError}
              </div>
            ) : (
              <CatalogSnapshotLinesTable lines={selectedLines} t={t} />
            )}
            {!detailLoading && detailTotal > 0 ? (
              <div className="d-flex flex-wrap align-items-center justify-content-between gap-2 px-3 py-3 border-top border-secondary border-opacity-25">
                <span className="small text-muted">
                  {t("CATALOG_LINES_PAGE_OF", {
                    page: detailPage,
                    totalPages: detailTotalPages || 1,
                    count: detailTotal,
                  })}
                </span>
                <Pagination className="mb-0 flex-wrap">
                  <Pagination.Prev
                    disabled={detailPage <= 1}
                    onClick={() => setPage(detailPage - 1)}
                  />
                  <Pagination.Item active>{detailPage}</Pagination.Item>
                  <Pagination.Next
                    disabled={detailTotalPages > 0 && detailPage >= detailTotalPages}
                    onClick={() => setPage(detailPage + 1)}
                  />
                </Pagination>
              </div>
            ) : null}
          </Card.Body>
        </Card>
      ) : null}
    </div>
  );
}

function ChannelOverviewCards({ rows, t }) {
  const list = asArray(rows);
  if (!list.length) return null;

  return (
    <div className="mb-4 club-sales-channel-overview-section">
      <h6 className="fw-semibold mb-3">{t("CLUB_SALES_OVERVIEW_CHANNELS")}</h6>
      <Row className="g-3 club-sales-kpi-row">
        {list.map((row, i) => (
          <Col
            sm={6}
            xl={4}
            key={`ch-card-${pickSalesChannelKey(row) || i}`}
          >
            <Card className="border-0 shadow-sm h-100 club-sales-kpi-card">
              <Card.Body className="py-3 d-flex flex-column">
                <div className="d-flex align-items-center justify-content-between gap-2 mb-2">
                  <div className="club-sales-kpi-label text-uppercase text-muted small mb-0">
                    {t("CLUB_SALES_COL_CHANNEL")}
                  </div>
                  <ChannelBadge value={pickSalesChannelKey(row)} t={t} />
                </div>
                <div className="club-sales-kpi-value fs-5 fw-bold tabular-nums">
                  {isGuestlistChannelRow(row) ? (
                    <span className="text-muted fs-6 fw-normal">
                      {t("CLUB_SALES_GUESTLIST_NO_REVENUE")}
                    </span>
                  ) : (
                    fmtChannelRevenueDisplay(row, t)
                  )}
                </div>
                <div className="club-sales-kpi-sub text-muted small mt-2">
                  {t("CLUB_SALES_METRIC_ORDERS_TICKETS", {
                    orders: row?.orderCount ?? "—",
                    tickets: row?.ticketsSold ?? "—",
                  })}
                </div>
              </Card.Body>
            </Card>
          </Col>
        ))}
      </Row>
    </div>
  );
}

function TableBookingGuestCell({ row, t }) {
  const guest = pickTableBookingLineGuest(row);
  const name = (guest.name ?? "").trim();
  const phone = (guest.phone ?? "").trim();
  const email = (guest.email ?? "").trim();
  if (!name && !phone && !email) return dash(null, t);
  return (
    <div className="small">
      {name ? <div className="fw-medium">{name}</div> : null}
      {phone ? <div className="club-sales-guest-meta">{phone}</div> : null}
      {email ? (
        <div
          className="club-sales-guest-meta text-truncate"
          style={{ maxWidth: "14rem" }}
        >
          {email}
        </div>
      ) : null}
    </div>
  );
}

function sumTableBookingByEmployeeRows(rows) {
  return asArray(rows).reduce(
    (acc, r) => ({
      bookingCount: acc.bookingCount + numOrZero(r?.bookingCount),
      guestsTotal: acc.guestsTotal + numOrZero(r?.guestsTotal),
      revenue: acc.revenue + numOrZero(r?.revenue),
      minimumSpendTotal:
        acc.minimumSpendTotal + numOrZero(r?.minimumSpendTotal),
    }),
    { bookingCount: 0, guestsTotal: 0, revenue: 0, minimumSpendTotal: 0 }
  );
}

function sumOfflineByEmployeeRows(rows) {
  return asArray(rows).reduce(
    (acc, r) => ({
      orderCount: acc.orderCount + numOrZero(r?.orderCount),
      ticketsSold: acc.ticketsSold + numOrZero(r?.ticketsSold),
      revenue: acc.revenue + pickManagerTicketRevenue(r),
    }),
    { orderCount: 0, ticketsSold: 0, revenue: 0 }
  );
}

function BreakdownOverTimeSection({ rows, breakdownLabel, t }) {
  const list = asArray(rows);
  if (!list.length) return null;
  const totals = sumBreakdownSeriesRows(list);

  return (
    <Card className="border-0 shadow-sm mb-4">
      <Card.Header className="club-sales-card-header py-3 border-bottom">
        <span className="fw-semibold d-block">
          {t("CLUB_SALES_SECTION_BREAKDOWN")}
        </span>
        <span className="text-muted small fw-normal d-block mt-1">
          {breakdownLabel
            ? t("CLUB_SALES_BREAKDOWN_HINT", { period: breakdownLabel })
            : t("CLUB_SALES_TABLE_CAPTION_SERIES")}
        </span>
      </Card.Header>
      <Card.Body className="p-0">
        <div className="table-responsive">
          <Table hover className="mb-0 align-middle club-sales-ref-table">
            <thead className="club-sales-table-head">
              <tr className="small text-uppercase" style={{ fontSize: "0.7rem" }}>
                <th className="ps-4 py-3 border-0">{t("CLUB_SALES_COL_PERIOD")}</th>
                <th className="text-end py-3 border-0">{t("CLUB_SALES_COL_ORDERS")}</th>
                <th className="text-end py-3 border-0">{t("CLUB_SALES_COL_TICKETS")}</th>
                <th className="text-end pe-4 py-3 border-0">{t("CLUB_SALES_COL_REVENUE")}</th>
              </tr>
            </thead>
            <tbody>
              {list.map((row, i) => (
                <tr key={`bd-${row.period}-${i}`}>
                  <td className="ps-4 fw-medium">{row.period}</td>
                  <td className="text-end tabular-nums">{row.orderCount}</td>
                  <td className="text-end tabular-nums">{row.ticketsSold}</td>
                  <td className="text-end pe-4 tabular-nums">
                    {fmtCurrency(row.revenue)}
                  </td>
                </tr>
              ))}
              <tr className="club-sales-table-total-row fw-bold">
                <td className="ps-4">{t("CLUB_SALES_REF_ROW_TOTAL")}</td>
                <td className="text-end tabular-nums">{totals.orderCount}</td>
                <td className="text-end tabular-nums">{totals.ticketsSold}</td>
                <td className="text-end pe-4 tabular-nums">
                  {fmtCurrency(totals.revenue)}
                </td>
              </tr>
            </tbody>
          </Table>
        </div>
      </Card.Body>
    </Card>
  );
}

function OfflineTicketsByEmployeeSection({ rows, t }) {
  const list = asArray(rows)
    .map(normalizeOfflineByEmployeeRow)
    .filter(Boolean);
  if (!list.length) return null;
  const totals = sumOfflineByEmployeeRows(list);

  return (
    <Card className="border-0 shadow-sm mb-4">
      <Card.Header className="club-sales-card-header py-3 border-bottom">
        <span className="fw-semibold d-block">
          {t("CLUB_SALES_SECTION_OFFLINE_EMPLOYEE")}
        </span>
        <span className="text-muted small fw-normal d-block mt-1">
          {t("CLUB_SALES_OFFLINE_BY_EMPLOYEE_HINT")}
        </span>
      </Card.Header>
      <Card.Body className="p-0">
        <div className="table-responsive">
          <Table hover className="mb-0 align-middle club-sales-ref-table">
            <thead className="club-sales-table-head">
              <tr className="small text-uppercase" style={{ fontSize: "0.7rem" }}>
                <th className="ps-4 py-3 border-0">{t("CLUB_SALES_COL_STAFF")}</th>
                <th className="py-3 border-0">{t("CLUB_SALES_COL_EMAIL")}</th>
                <th className="text-end py-3 border-0">{t("CLUB_SALES_REF_COL_BOOKINGS")}</th>
                <th className="text-end py-3 border-0">{t("CLUB_SALES_REF_COL_TICKETS_SOLD")}</th>
                <th className="text-end pe-4 py-3 border-0">{t("CLUB_SALES_REF_COL_TOTAL_REVENUE")}</th>
              </tr>
            </thead>
            <tbody>
              {list.map((row) => (
                <tr key={`off-emp-${row.soldByUserId}`}>
                  <td className="ps-4 fw-medium">{dash(row.fullname, t)}</td>
                  <td className="small text-break">{dash(row.email, t)}</td>
                  <td className="text-end tabular-nums">{row.orderCount}</td>
                  <td className="text-end tabular-nums">{row.ticketsSold}</td>
                  <td className="text-end pe-4 fw-medium tabular-nums">
                    {fmtCurrency(row.revenue)}
                  </td>
                </tr>
              ))}
              <tr className="club-sales-table-total-row fw-bold">
                <td className="ps-4">{t("CLUB_SALES_REF_ROW_TOTAL")}</td>
                <td>—</td>
                <td className="text-end tabular-nums">{totals.orderCount}</td>
                <td className="text-end tabular-nums">{totals.ticketsSold}</td>
                <td className="text-end pe-4 tabular-nums">
                  {fmtCurrency(totals.revenue)}
                </td>
              </tr>
            </tbody>
          </Table>
        </div>
      </Card.Body>
    </Card>
  );
}

function TableReferralsByEmployeeSection({ rows, t }) {
  const list = asArray(rows)
    .map(normalizeTableBookingByEmployeeRow)
    .filter(Boolean);
  if (!list.length) return null;
  const totals = sumTableBookingByEmployeeRows(list);

  return (
    <Card className="border-0 shadow-sm mb-4">
      <Card.Header className="club-sales-card-header py-3 border-bottom">
        <span className="fw-semibold d-block">
          {t("CLUB_SALES_TABLE_BOOKING_BY_EMPLOYEE_TITLE")}
        </span>
        <span className="text-muted small fw-normal d-block mt-1">
          {t("CLUB_SALES_TABLE_BOOKING_BY_EMPLOYEE_HINT")}
        </span>
      </Card.Header>
      <Card.Body className="p-0">
        <div className="table-responsive">
          <Table hover className="mb-0 align-middle club-sales-ref-table">
            <thead className="club-sales-table-head">
              <tr className="small text-uppercase" style={{ fontSize: "0.7rem" }}>
                <th className="ps-4 py-3 border-0">{t("CLUB_SALES_COL_STAFF")}</th>
                <th className="py-3 border-0">{t("CLUB_SALES_TABLE_BOOKING_COL_EMP_REF")}</th>
                <th className="text-end py-3 border-0">{t("CLUB_SALES_TABLE_BOOKING_COL_BOOKINGS")}</th>
                <th className="text-end py-3 border-0">{t("CLUB_SALES_TABLE_BOOKING_COL_GUESTS")}</th>
                <th className="text-end py-3 border-0">{t("CLUB_SALES_REF_COL_TOTAL_REVENUE")}</th>
                <th className="text-end pe-4 py-3 border-0">{t("CLUB_SALES_TABLE_BOOKING_COL_MIN_SPEND")}</th>
              </tr>
            </thead>
            <tbody>
              {list.map((row, i) => (
                <tr key={`tb-emp-${row.referredByUserId ?? row.empref ?? i}`}>
                  <td className="ps-4 fw-medium">{dash(row.fullname, t)}</td>
                  <td className="small font-monospace">{dash(row.empref, t)}</td>
                  <td className="text-end tabular-nums">{row.bookingCount}</td>
                  <td className="text-end tabular-nums">{row.guestsTotal}</td>
                  <td className="text-end tabular-nums">
                    {fmtInrCurrency(row.revenue)}
                  </td>
                  <td className="text-end pe-4 tabular-nums">
                    {fmtInrCurrency(row.minimumSpendTotal)}
                  </td>
                </tr>
              ))}
              <tr className="club-sales-table-total-row fw-bold">
                <td className="ps-4">{t("CLUB_SALES_REF_ROW_TOTAL")}</td>
                <td>—</td>
                <td className="text-end tabular-nums">{totals.bookingCount}</td>
                <td className="text-end tabular-nums">{totals.guestsTotal}</td>
                <td className="text-end tabular-nums">
                  {fmtInrCurrency(totals.revenue)}
                </td>
                <td className="text-end pe-4 tabular-nums">
                  {fmtInrCurrency(totals.minimumSpendTotal)}
                </td>
              </tr>
            </tbody>
          </Table>
        </div>
        <p className="small text-muted mb-0 px-3 py-3 border-top border-secondary border-opacity-25">
          {t("CLUB_SALES_TABLE_BOOKING_BY_EMPLOYEE_FOOTNOTE")}
        </p>
      </Card.Body>
    </Card>
  );
}

function TableBookingLinesTable({ items, t, showGateCollections = false }) {
  return (
    <div className="table-responsive club-sales-bookings-wrap">
      <Table hover className="mb-0 align-middle small club-sales-ref-table">
        <thead className="club-sales-table-head">
          <tr style={{ fontSize: "0.68rem" }} className="text-uppercase">
            <th className="ps-3 py-3 border-0">
              {t("CLUB_SALES_TABLE_BOOKING_COL_REF")}
            </th>
            <th className="py-3 border-0">
              {t("CLUB_SALES_TABLE_BOOKING_COL_GUEST")}
            </th>
            <th className="py-3 border-0">
              {t("CLUB_SALES_TABLE_BOOKING_COL_TABLE")}
            </th>
            <th className="py-3 border-0">
              {t("CLUB_SALES_TABLE_BOOKING_COL_CHANNEL")}
            </th>
            <th className="py-3 border-0">
              {t("CLUB_SALES_TABLE_BOOKING_COL_PARTY")}
            </th>
            <th className="text-end py-3 border-0">
              {t("CLUB_SALES_TABLE_BOOKING_COL_DEPOSIT")}
            </th>
            {showGateCollections ? (
              <th className="text-end py-3 border-0">
                {t("CLUB_SALES_TABLE_BOOKING_COL_GATE")}
              </th>
            ) : null}
            <th className="text-end py-3 border-0">
              {t("CLUB_SALES_TABLE_BOOKING_COL_MIN_SPEND")}
            </th>
            <th className="py-3 border-0">
              {t("CLUB_SALES_TABLE_BOOKING_COL_STATUS")}
            </th>
            <th className="py-3 border-0 pe-3">
              {t("CLUB_SALES_TABLE_BOOKING_COL_EMP_REF")}
            </th>
          </tr>
        </thead>
        <tbody>
          {items.map((row, i) => {
            const ref = pickTableBookingLineRef(row);
            const bookerName = pickTableBookingLineBookerName(row);
            return (
              <tr key={`tbl-line-${ref || i}-${i}`}>
                <td className="ps-3">
                  <code>{ref ? ref : dash(null, t)}</code>
                </td>
                <td className="small fw-medium">
                  {bookerName ? bookerName : <TableBookingGuestCell row={row} t={t} />}
                </td>
                <td className="small">
                  {dash(pickTableBookingLineTableName(row), t)}
                </td>
                <td>
                  <TableBookingSaleChannelCell row={row} t={t} />
                </td>
                <td className="tabular-nums">
                  {row?.partySize ?? row?.partysize ?? "—"}
                </td>
                <td className="text-end tabular-nums">
                  {fmtInrCurrency(
                    row?.depositAmount ?? row?.depositamount
                  )}
                </td>
                {showGateCollections ? (
                  <td className="text-end small">
                    {formatOfflineCollectionsCell(row, t)}
                  </td>
                ) : null}
                <td className="text-end tabular-nums">
                  {fmtInrCurrency(row?.minimumSpend ?? row?.minimumspend)}
                </td>
                <td className="small">{dash(row?.status, t)}</td>
                <td className="small font-monospace pe-3">
                  {dash(pickTableBookingLineStaffLabel(row), t)}
                  <TableBookingCatalogSnapshot booking={row} />
                </td>
              </tr>
            );
          })}
        </tbody>
      </Table>
    </div>
  );
}

function StaffTableBookingLinesSection({ lines, staffLabel, t }) {
  const items = asArray(lines);
  if (!items.length) return null;

  return (
    <Card className="border-0 shadow-sm mb-4">
      <Card.Header className="club-sales-card-header py-3 border-bottom">
        <span className="fw-semibold d-block">
          {t("CLUB_SALES_EMPLOYEE_TABLE_LINES_TITLE")}
        </span>
        <span className="text-muted small fw-normal d-block mt-1">
          {t("CLUB_SALES_EMPLOYEE_TABLE_LINES_HINT", {
            name: staffLabel || t("CLUB_SALES_VALUE_EMPTY"),
          })}
        </span>
      </Card.Header>
      <Card.Body className="p-0">
        <TableBookingLinesTable
          items={items}
          t={t}
          showGateCollections
        />
      </Card.Body>
    </Card>
  );
}

function TableBookingLinesModal({
  show,
  onHide,
  eventLabel,
  items,
  loading,
  page,
  limit,
  total,
  onPageChange,
  onLimitChange,
  t,
}) {
  const totalPages = Math.max(1, Math.ceil((total || 0) / (limit || 20)));

  return (
    <Modal
      show={show}
      onHide={onHide}
      size="xl"
      centered
      scrollable
      className="club-sales-table-booking-lines-modal"
    >
      <Modal.Header closeButton closeVariant="white">
        <Modal.Title as="h6" className="fw-semibold">
          {t("CLUB_SALES_TABLE_BOOKING_LINES_TITLE")}
          {eventLabel ? (
            <span className="text-muted fw-normal small d-block mt-1">
              {eventLabel}
            </span>
          ) : null}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body className="p-0">
        <div className="d-flex flex-wrap align-items-center justify-content-between gap-2 px-3 py-3 border-bottom border-secondary border-opacity-25">
          <Form.Select
            className="club-sales-bookings-limit"
            style={{ width: "auto", minWidth: "5.5rem" }}
            value={String(limit)}
            onChange={(e) => onLimitChange(Number(e.target.value))}
          >
            {BOOKING_PAGE_SIZE_OPTIONS.map((size) => (
              <option key={size} value={String(size)}>
                {size}
              </option>
            ))}
          </Form.Select>
        </div>
        {loading ? (
          <div className="club-sales-loading-panel border-0">
            <Spinner animation="border" size="sm" role="status" />
          </div>
        ) : items.length === 0 ? (
          <EmptySection t={t} />
        ) : (
          <div className="table-responsive club-sales-bookings-wrap">
            <Table hover className="mb-0 align-middle small club-sales-ref-table">
              <thead className="club-sales-table-head">
                <tr style={{ fontSize: "0.68rem" }} className="text-uppercase">
                  <th className="ps-3 py-3 border-0">
                    {t("CLUB_SALES_TABLE_BOOKING_COL_REF")}
                  </th>
                  <th className="py-3 border-0">
                    {t("CLUB_SALES_TABLE_BOOKING_COL_PARTY")}
                  </th>
                  <th className="text-end py-3 border-0">
                    {t("CLUB_SALES_TABLE_BOOKING_COL_DEPOSIT")}
                  </th>
                  <th className="text-end py-3 border-0">
                    {t("CLUB_SALES_TABLE_BOOKING_COL_MIN_SPEND")}
                  </th>
                  <th className="py-3 border-0">
                    {t("CLUB_SALES_TABLE_BOOKING_COL_SERVICE_DATE")}
                  </th>
                  <th className="py-3 border-0">
                    {t("CLUB_SALES_TABLE_BOOKING_COL_TABLE")}
                  </th>
                  <th className="py-3 border-0">
                    {t("CLUB_SALES_TABLE_BOOKING_COL_CHANNEL")}
                  </th>
                  <th className="py-3 border-0">
                    {t("CLUB_SALES_TABLE_BOOKING_COL_STATUS")}
                  </th>
                  <th className="py-3 border-0">
                    {t("CLUB_SALES_TABLE_BOOKING_COL_GUEST")}
                  </th>
                  <th className="pe-3 py-3 border-0">
                    {t("CLUB_SALES_TABLE_BOOKING_COL_STAFF")}
                  </th>
                </tr>
              </thead>
              <tbody>
                {items.map((row, i) => {
                  const ref = pickTableBookingLineRef(row);
                  const serviceRaw = pickTableBookingLineServiceDate(row);
                  return (
                    <tr key={`tbl-${ref || i}-${i}`}>
                      <td className="ps-3">
                        <code>{ref ? ref : dash(null, t)}</code>
                      </td>
                      <td className="tabular-nums">
                        {row?.partySize ?? row?.partysize ?? "—"}
                      </td>
                      <td className="text-end tabular-nums">
                        {fmtInrCurrency(
                          row?.depositAmount ?? row?.depositamount
                        )}
                      </td>
                      <td className="text-end tabular-nums">
                        {fmtInrCurrency(
                          row?.minimumSpend ?? row?.minimumspend
                        )}
                      </td>
                      <td className="small text-nowrap">
                        {serviceRaw
                          ? moment(serviceRaw).format("DD MMM YYYY")
                          : dash(null, t)}
                      </td>
                      <td className="small">
                        {dash(pickTableBookingLineTableName(row), t)}
                      </td>
                      <td>
                        <TableBookingSaleChannelCell row={row} t={t} />
                      </td>
                      <td className="small">{dash(row?.status, t)}</td>
                      <td>
                        <TableBookingGuestCell row={row} t={t} />
                      </td>
                      <td className="pe-3 small">
                        {dash(pickTableBookingLineStaffLabel(row), t)}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </Table>
          </div>
        )}
        {!loading && total > 0 ? (
          <div className="d-flex flex-wrap align-items-center justify-content-between gap-2 px-3 py-3 border-top border-secondary border-opacity-25">
            <span className="small text-muted">
              {t("CLUB_SALES_TABLE_BOOKING_LINES_PAGE_OF", {
                page,
                totalPages,
                count: total,
              })}
            </span>
            <Pagination className="mb-0 flex-wrap">
              <Pagination.Prev
                disabled={page <= 1}
                onClick={() => onPageChange(page - 1)}
              />
              <Pagination.Item active>{page}</Pagination.Item>
              <Pagination.Next
                disabled={page >= totalPages}
                onClick={() => onPageChange(page + 1)}
              />
            </Pagination>
          </div>
        ) : null}
      </Modal.Body>
      <Modal.Footer className="border-secondary border-opacity-25">
        <Button variant="outline-light" size="sm" onClick={onHide}>
          Close
        </Button>
      </Modal.Footer>
    </Modal>
  );
}

/**
 * Renders GET /club/reports/sales payload: summary, byEvent (onlineTickets / offlineTickets / guestlistTickets), byClub,
 * byCategory (when eventId filter is set), filters, byChannel, offlineByEmployee.
 */
function ClubSalesReportDisplay({
  data,
  resolvedClubName,
  resolvedEventTitle,
  selectedEventId,
  reportEventScope,
  scopedEventIds,
  breakdownPeriod,
  selectedStaffUserId,
  selectedStaffLabel,
  saleChannelFilter,
  guestlistMode,
  tableBookingMode,
  isAdmin,
  singleClubFiltered,
  eventBookingLookups,
  onOpenTableBookingLines,
  staffTableBookingLines,
  onLoadCatalogLines,
  t,
}) {
  if (data == null) return null;
  if (typeof data === "string") {
    return (
      <Card className="border-0 shadow-sm">
        <Card.Body>
          <p className="small mb-0" style={{ whiteSpace: "pre-wrap" }}>
            {data}
          </p>
        </Card.Body>
      </Card>
    );
  }

  const summary =
    data.summary && typeof data.summary === "object" ? data.summary : null;
  const byEventRaw = sortSalesReportByEventRows(
    asArray(data.byEvent ?? data.by_event).map(normalizeByEventSalesRow)
  );
  const singleEventSelected = Boolean(selectedEventId?.trim());
  const byEvent = filterByEventRowsForScope(
    byEventRaw,
    reportEventScope,
    scopedEventIds,
    selectedEventId
  );
  const byEventTotals = sumByEventRows(byEvent);
  const byClub = asArray(data.byClub);
  const byClubTotals = sumByClubRows(byClub);
  const byChannel = sortSalesChannelRows(data.byChannel);
  const offlineByEmployee = asArray(data.offlineByEmployee)
    .map(normalizeOfflineByEmployeeRow)
    .filter(Boolean);
  const scopeFiltersTable =
    reportEventScope &&
    reportEventScope !== EVENT_SCOPES.ALL &&
    !singleEventSelected;
  const displaySummary =
    scopeFiltersTable && byEvent.length === 0
      ? emptySalesSummaryShape()
      : summary;
  const displayByChannel =
    scopeFiltersTable && byEvent.length === 0 ? [] : byChannel;
  const displayOfflineByEmployee =
    scopeFiltersTable && byEvent.length === 0 ? [] : offlineByEmployee;
  const byCategory = asArray(data.byCategory ?? data.by_category);
  const showCategoryBreakdown = singleEventSelected && byCategory.length > 0;
  const splitEventMetrics = true;
  const displayByEvent = singleEventSelected
    ? (() => {
        const matched = byEvent.filter((row) =>
          eventRowMatchesSelectedEvent(row, selectedEventId)
        );
        return matched.length > 0 ? matched : byEvent;
      })()
    : byEvent;

  const tableBookings = normalizeTableBookingsFromReport(data);
  const tableBookingSummary =
    tableBookings?.summary ?? {
      bookingCount: 0,
      guestsTotal: 0,
      revenue: 0,
      depositTotal: 0,
      minimumSpendTotal: 0,
    };
  const tableBookingByEmployee = asArray(tableBookings?.byEmployee);
  const displayTableBookingByEvent = buildTableBookingByEventDisplayRows(
    displayByEvent,
    tableBookings?.byEvent,
    t
  );
  const showTableBookingSections = !guestlistMode;
  const showTicketReportSections = !guestlistMode && !tableBookingMode;
  const showEmployeeSalesContext =
    !guestlistMode &&
    !tableBookingMode &&
    String(saleChannelFilter || "").toUpperCase() !== "ONLINE";
  const staffTableBookingLinesResolved = asArray(
    staffTableBookingLines ??
      data.tableBookingLines ??
      data.table_booking_lines
  );
  const breakdownSeries =
    reportEventScope === EVENT_SCOPES.PAST && breakdownPeriod
      ? normalizeBreakdownSeries(data)
      : [];
  const breakdownLabel =
    breakdownPeriod === "week"
      ? t("CLUB_SALES_BREAKDOWN_WEEK")
      : breakdownPeriod === "month"
        ? t("CLUB_SALES_BREAKDOWN_MONTH")
        : "";

  const adminFinancials =
    isAdmin &&
    data.adminFinancials &&
    typeof data.adminFinancials === "object"
      ? data.adminFinancials
      : null;
  const adminFinByEvent = buildAdminFinancialsByEventMap(adminFinancials);
  const adminFinByEventRows = asArray(adminFinancials?.byEvent);
  const eventAdminTotals = sumEventAdminFinancials(byEvent, adminFinByEvent);
  const showAdminEventFinancials =
    isAdmin &&
    (byEvent.some((row) => eventHasAdminFinancialFields(row)) ||
      Boolean(adminFinancials));
  const reportEventMeta =
    (data.event && typeof data.event === "object" ? data.event : null) ??
    (data.eventDetails && typeof data.eventDetails === "object"
      ? data.eventDetails
      : null) ??
    (adminFinancials?.event && typeof adminFinancials.event === "object"
      ? adminFinancials.event
      : null);
  const commissionMetaSources = reportEventMeta ? [reportEventMeta] : [];
  const sharedCommissionHeaderRate = resolveSharedCommissionHeaderRate(
    displayByEvent,
    adminFinByEvent,
    commissionMetaSources,
    eventBookingLookups
  );
  const commissionColumnLabel = fmtCommissionColumnLabel(
    displayByEvent,
    adminFinByEvent,
    commissionMetaSources,
    eventBookingLookups,
    t
  );

  const clubTitle =
    resolvedClubName != null && String(resolvedClubName).trim() !== ""
      ? String(resolvedClubName).trim()
      : null;
  const clubIdStr =
    data.clubId != null && data.clubId !== "" ? String(data.clubId) : "";

  return (
    <div className="club-sales-report-display">
      <div className="d-flex flex-wrap align-items-center justify-content-between gap-2 mb-3">
        <div>
          <div
            className="text-uppercase text-muted small mb-1"
            style={{ letterSpacing: "0.05em" }}
          >
            {t("CLUB_SALES_CLUB_ID")}
          </div>
          <div className="d-flex flex-column gap-1 align-items-start">
            <div className="d-flex align-items-center gap-2 flex-wrap">
              <span className="fs-5 fw-semibold text-break">
                {clubTitle ?? dash(data.clubId, t)}
              </span>
              {!clubTitle && clubIdStr ? (
                <Badge bg="secondary" className="fw-normal opacity-90">
                  ID
                </Badge>
              ) : null}
            </div>
            {clubTitle && clubIdStr ? (
              <span
                className="small text-muted font-monospace club-sales-club-id"
                title={clubIdStr}
              >
                {clubIdStr}
              </span>
            ) : null}
          </div>
        </div>
      </div>

      {guestlistMode ? (
        <GuestlistKpiSummaryCards summary={displaySummary} t={t} />
      ) : tableBookingMode ? (
        <>
        <TableBookingKpiSummaryCards
          tableBookings={tableBookings}
          t={t}
          clubId={clubIdStr}
        />
        <CatalogSnapshotKpiCards
          data={data}
          t={t}
          onLoadCatalogLines={onLoadCatalogLines}
        />
        </>
      ) : (
        <>
          <KpiSummaryCards
            summary={displaySummary}
            byChannel={displayByChannel}
            t={t}
          />
          {showTableBookingSections ? (
            <TableBookingKpiSummaryCards
              tableBookings={
                scopeFiltersTable && byEvent.length === 0
                  ? { summary: buildEmptyClubSalesResult().tableBookings.summary }
                  : tableBookings
              }
              t={t}
              clubId={clubIdStr}
            />
          ) : null}
          {!guestlistMode ? (
            <CatalogSnapshotKpiCards
              data={data}
              t={t}
              onLoadCatalogLines={onLoadCatalogLines}
            />
          ) : null}
          <ChannelOverviewCards rows={displayByChannel} t={t} />
        </>
      )}

      {/* {!guestlistMode && adminFinancials ? (
        <AdminFinancialSettlementSection
          adminFinancials={adminFinancials}
          selectedEventId={selectedEventId}
          byCategory={byCategory}
          resolvedEventTitle={resolvedEventTitle}
          t={t}
        />
      ) : null} */}

      {!guestlistMode ? (
      <>
      {showTicketReportSections && byClub.length > 0 && !singleClubFiltered ? (
        <Card className="border-0 shadow-sm mb-4">
          <Card.Header className="club-sales-card-header py-3 border-bottom">
            <span className="fw-semibold">{t("CLUB_SALES_SECTION_BY_CLUB")}</span>
          </Card.Header>
          <Card.Body className="p-0">
            <div className="table-responsive">
              <Table hover className="mb-0 align-middle club-sales-ref-table">
                <thead className="club-sales-table-head">
                  <tr className="small text-uppercase" style={{ fontSize: "0.7rem" }}>
                    <th className="ps-4 py-3 border-0">{t("CLUB_SALES_COL_CLUB")}</th>
                    <th className="py-3 border-0">{t("CLUB_SALES_COL_SLUG")}</th>
                    <th className="text-end py-3 border-0">{t("CLUB_SALES_REF_COL_BOOKINGS")}</th>
                    <th className="text-end py-3 border-0">{t("CLUB_SALES_REF_COL_TICKETS_SOLD")}</th>
                    <th className="text-end pe-4 py-3 border-0">{t("CLUB_SALES_REF_COL_TOTAL_REVENUE")}</th>
                  </tr>
                </thead>
                <tbody>
                  {byClub.map((row, i) => (
                    <tr key={`club-${i}-${row?.clubId ?? i}`}>
                      <td className="ps-4 fw-medium">{dash(row?.clubName, t)}</td>
                      <td className="small text-muted">{dash(row?.clubSlug, t)}</td>
                      <td className="text-end tabular-nums">{row?.orderCount ?? "—"}</td>
                      <td className="text-end tabular-nums">{row?.ticketsSold ?? "—"}</td>
                      <td className="text-end pe-4 fw-medium tabular-nums">
                        {fmtCurrency(pickManagerTicketRevenue(row))}
                      </td>
                    </tr>
                  ))}
                  {byClub.length > 0 ? (
                    <tr className="club-sales-table-total-row fw-bold">
                      <td className="ps-4">{t("CLUB_SALES_REF_ROW_TOTAL")}</td>
                      <td className="text-muted small">—</td>
                      <td className="text-end tabular-nums">{byClubTotals.orderCount}</td>
                      <td className="text-end tabular-nums">{byClubTotals.ticketsSold}</td>
                      <td className="text-end pe-4 tabular-nums">
                        {fmtCurrency(byClubTotals.revenue)}
                      </td>
                    </tr>
                  ) : null}
                </tbody>
              </Table>
            </div>
          </Card.Body>
        </Card>
      ) : null}

      {showTicketReportSections ? (
      <Card className="border-0 shadow-sm mb-4">
        <Card.Header className="club-sales-card-header py-3 border-bottom d-flex flex-column flex-sm-row flex-wrap align-items-sm-start justify-content-between gap-2">
          <div>
            <span className="fw-semibold d-block">
              {t("CLUB_SALES_EVENTS_TABLE_TITLE")}
            </span>
            <span className="text-muted small fw-normal d-block mt-1">
              {singleEventSelected
                ? t("CLUB_SALES_EVENTS_TABLE_HINT_SINGLE")
                : t("CLUB_SALES_EVENTS_TABLE_HINT_SPLIT")}
            </span>
          </div>
        </Card.Header>
        <Card.Body className="p-0">
          {displayByEvent.length === 0 ? (
            <EmptySection t={t} />
          ) : (
            <div className="table-responsive club-sales-ticket-breakdown-wrap">
              <Table hover className="mb-0 align-middle club-sales-ref-table">
                <thead className="club-sales-table-head">
                  <tr className="small text-uppercase" style={{ fontSize: "0.68rem" }}>
                    <th className="ps-4 py-3 border-0">{t("CLUB_SALES_COL_EVENT")}</th>
                    <th className="py-3 border-0">{t("CLUB_SALES_REF_COL_EVENT_DATE")}</th>
                    <th className="text-end py-3 border-0">
                      {t("CLUB_SALES_COL_TICKET_ORDERS")}
                    </th>
                    <th className="py-3 border-0 club-sales-col-ticket-sold">
                      {t("CLUB_SALES_REF_COL_TICKETS_SOLD")}
                    </th>
                    {showCategoryBreakdown ? (
                      <th className="text-end py-3 border-0">
                        {t("CLUB_SALES_CATEGORY_COL_ONLINE_REVENUE")}
                      </th>
                    ) : null}
                    {splitEventMetrics && !showCategoryBreakdown ? (
                      <th className="text-end py-3 border-0">
                        {showAdminEventFinancials
                          ? t("CLUB_SALES_CATEGORY_COL_ONLINE_REVENUE")
                          : t("CLUB_SALES_REF_COL_TOTAL_REVENUE")}
                      </th>
                    ) : null}
                    {!splitEventMetrics ? (
                      <>
                    <th className="text-end py-3 border-0">
                      {t("CLUB_SALES_REF_COL_TOTAL_REVENUE")}
                    </th>
                    <th className="text-end py-3 border-0 club-sales-col-table-group-start">
                      {t("CLUB_SALES_TABLE_BOOKING_COL_BOOKINGS")}
                    </th>
                    <th className="text-end py-3 border-0">
                      {t("CLUB_SALES_TABLE_BOOKING_COL_GUESTS")}
                    </th>
                    <th className="text-end py-3 border-0">
                      {t("CLUB_SALES_COL_TABLE_REVENUE")}
                    </th>
                    <th
                      className={`text-end py-3 border-0${
                        showAdminEventFinancials ? "" : " pe-3"
                      }`}
                    >
                      {t("CLUB_SALES_TABLE_BOOKING_COL_MIN_SPEND")}
                    </th>
                    {showTableBookingSections ? (
                      <th className="text-end py-3 border-0 pe-3" />
                    ) : null}
                      </>
                    ) : null}
                    {showAdminEventFinancials ? (
                      <>
                        {!splitEventMetrics ? (
                          <th className="text-end py-3 border-0">
                            {t("CLUB_SALES_CATEGORY_COL_ONLINE_REVENUE")}
                          </th>
                        ) : null}
                        <th className="text-end py-3 border-0">
                          {commissionColumnLabel}
                        </th>
                        <th className="text-end py-3 border-0">
                          {t("CLUB_SALES_FIN_CGST")}
                        </th>
                        <th className="text-end py-3 border-0">
                          {t("CLUB_SALES_FIN_SGST")}
                        </th>
                        <th className="text-end pe-4 py-3 border-0">
                          {t("CLUB_SALES_SETTLEMENT_COL_STATUS")}
                        </th>
                      </>
                    ) : null}
                  </tr>
                </thead>
                <tbody>
                  {displayByEvent.map((row, i) => {
                    const adminFin = pickAdminFinForEventRow(row, adminFinByEvent);
                    const rowBookingRate = pickEventBookingPercentage(
                      row,
                      adminFinByEvent,
                      commissionMetaSources,
                      eventBookingLookups
                    );
                    const tb = pickEventRowTableBookings(row);
                    const hasTable = hasEventRowTableBookings(row);
                    const eventKey = row?.eventId ?? row?._id ?? i;
                    const openLines = () => {
                      const id = row?.eventId ?? row?._id;
                      onOpenTableBookingLines(
                        id != null && id !== "" ? String(id) : null
                      );
                    };
                    return (
                      <tr
                        key={`ev-${i}-${eventKey}`}
                        className={
                          splitEventMetrics
                            ? ""
                            : [
                                isTableOnlyEventRow(row)
                                  ? "club-sales-event-row-table-only"
                                  : hasTable
                                    ? "club-sales-table-booking-row-clickable"
                                    : "",
                              ]
                                .filter(Boolean)
                                .join(" ")
                        }
                      >
                        <td className="ps-4 fw-medium">
                          {pickTableBookingEventLabel(row, t)}
                        </td>
                        <td className="small text-nowrap">
                          {row?.startdatetime
                            ? moment(row.startdatetime).format("DD MMM YYYY, h:mm A")
                            : dash(null, t)}
                        </td>
                        <td className="text-end tabular-nums">
                          {numOrZero(row?.orderCount) > 0
                            ? row.orderCount
                            : dash(null, t)}
                        </td>
                        <td className="club-sales-col-ticket-sold">
                          <TicketSoldCell row={row} excludeGuestlist />
                        </td>
                        {showCategoryBreakdown ? (
                          <td className="text-end fw-medium tabular-nums">
                            {showAdminEventFinancials
                              ? fmtAdminFinancialCell(adminFin, "onlineRevenue")
                              : fmtEventOnlineRevenueDisplay(row, t)}
                          </td>
                        ) : null}
                        {splitEventMetrics && !showCategoryBreakdown ? (
                          <td className="text-end fw-medium tabular-nums">
                            {showAdminEventFinancials
                              ? fmtAdminFinancialCell(adminFin, "onlineRevenue")
                              : fmtTicketRevenueDisplay(row, t)}
                          </td>
                        ) : null}
                        {!splitEventMetrics ? (
                          <>
                        <td className="text-end fw-medium tabular-nums">
                          {fmtTicketRevenueDisplay(row, t)}
                        </td>
                        <td className="text-end tabular-nums club-sales-col-table-group-start">
                          {fmtTableBookingCount(tb.bookingCount, row, t)}
                        </td>
                        <td className="text-end tabular-nums">
                          {fmtTableBookingCount(tb.guestsTotal, row, t)}
                        </td>
                        <td className="text-end tabular-nums">
                          {fmtTableBookingOnlineMoney(tb, row, t)}
                        </td>
                        <td
                          className={`text-end tabular-nums${
                            showAdminEventFinancials ? "" : " pe-3"
                          }`}
                        >
                          {fmtTableBookingMoney(tb.minimumSpendTotal, row, t)}
                        </td>
                        {showTableBookingSections ? (
                          <td className="text-end pe-3">
                            {hasTable ? (
                              <Button
                                variant="outline-light"
                                size="sm"
                                type="button"
                                onClick={openLines}
                              >
                                {t("CLUB_SALES_TABLE_BOOKING_VIEW_LINES")}
                              </Button>
                            ) : (
                              dash(null, t)
                            )}
                          </td>
                        ) : null}
                          </>
                        ) : null}
                        {showAdminEventFinancials ? (
                          <>
                            {!splitEventMetrics ? (
                              <td className="text-end tabular-nums">
                                {fmtAdminFinancialCell(adminFin, "onlineRevenue")}
                              </td>
                            ) : null}
                            <td className="text-end tabular-nums">
                              <RizztixCommissionBreakdownCell
                                fin={adminFin}
                                bookingRate={
                                  sharedCommissionHeaderRate == null
                                    ? rowBookingRate
                                    : null
                                }
                                t={t}
                              />
                            </td>
                            <td className="text-end tabular-nums">
                              {fmtAdminFinancialCell(adminFin, "cgst")}
                            </td>
                            <td className="text-end tabular-nums">
                              {fmtAdminFinancialCell(adminFin, "sgst")}
                            </td>
                            <td className="text-end pe-4">
                              <EventSettlementStatusBadge
                                eventEnded={adminFin?.eventEnded}
                                t={t}
                              />
                            </td>
                          </>
                        ) : null}
                      </tr>
                    );
                  })}
                  {!singleEventSelected ? (
                    <tr className="club-sales-table-total-row fw-bold">
                      <td className="ps-4">{t("CLUB_SALES_REF_ROW_TOTAL")}</td>
                      <td>—</td>
                      <td className="text-end tabular-nums">{byEventTotals.orderCount}</td>
                      <td className="club-sales-col-ticket-sold">
                        <TicketSoldCell totals={byEventTotals} excludeGuestlist />
                      </td>
                      {showCategoryBreakdown ? (
                        <td className="text-end tabular-nums">
                          {fmtAdminFinancialCell(eventAdminTotals, "onlineRevenue")}
                        </td>
                      ) : (
                        <td className="text-end tabular-nums">
                          {showAdminEventFinancials
                            ? fmtAdminFinancialCell(eventAdminTotals, "onlineRevenue")
                            : fmtInrMoneyDisplay(byEventTotals.revenue)}
                        </td>
                      )}
                      {showAdminEventFinancials ? (
                        <>
                          <td className="text-end tabular-nums">
                            <RizztixCommissionBreakdownCell
                              fin={eventAdminTotals}
                              t={t}
                            />
                          </td>
                          <td className="text-end tabular-nums">
                            {fmtAdminFinancialCell(eventAdminTotals, "cgst")}
                          </td>
                          <td className="text-end tabular-nums">
                            {fmtAdminFinancialCell(eventAdminTotals, "sgst")}
                          </td>
                          <td className="text-end pe-4">—</td>
                        </>
                      ) : null}
                    </tr>
                  ) : null}
                </tbody>
              </Table>
            </div>
          )}
        </Card.Body>
      </Card>
      ) : null}

      {showTicketReportSections && showCategoryBreakdown ? (
        <CategoryBreakdownTableCard
          categories={byCategory}
          resolvedEventTitle={resolvedEventTitle}
          selectedEventId={selectedEventId}
          t={t}
        />
      ) : null}

      {showTableBookingSections ? (
        singleEventSelected ? (
          <EventSelectedTableBookingSection
            tb={tableBookingSummary}
            eventRow={
              byEvent.find((row) =>
                eventRowMatchesSelectedEvent(row, selectedEventId)
              ) ?? null
            }
            isAdmin={isAdmin}
            onOpenLines={() =>
              onOpenTableBookingLines(
                selectedEventId?.trim() ? String(selectedEventId).trim() : null
              )
            }
            t={t}
          />
        ) : (
          <TableBookingsByEventSection
            rows={displayTableBookingByEvent}
            summary={tableBookingSummary}
            eventBookingLookups={eventBookingLookups}
            adminFinByEvent={adminFinByEvent}
            isAdmin={isAdmin}
            onOpenTableBookingLines={onOpenTableBookingLines}
            t={t}
          />
        )
      ) : null}

      {(showEmployeeSalesContext || tableBookingMode) && !selectedStaffUserId ? (
        <>
          {showEmployeeSalesContext ? (
            <OfflineTicketsByEmployeeSection rows={offlineByEmployee} t={t} />
          ) : null}
          {showTableBookingSections ? (
            <TableReferralsByEmployeeSection
              rows={tableBookingByEmployee}
              t={t}
            />
          ) : null}
        </>
      ) : null}

      {breakdownSeries.length > 0 ? (
        <BreakdownOverTimeSection
          rows={breakdownSeries}
          breakdownLabel={breakdownLabel}
          t={t}
        />
      ) : null}

      {(showEmployeeSalesContext || tableBookingMode) && selectedStaffUserId ? (
        showTableBookingSections &&
        staffTableBookingLinesResolved.length > 0 ? (
          <StaffTableBookingLinesSection
            lines={staffTableBookingLinesResolved}
            staffLabel={selectedStaffLabel}
            t={t}
          />
        ) : null
      ) : null}

      </>
      ) : null}
    </div>
  );
}

function GuestlistBookingsTable({
  items,
  loading,
  page,
  limit,
  total,
  searchTerm = "",
  onPageChange,
  onLimitChange,
  t,
}) {
  const displayItems = useMemo(
    () => filterBookingListItems(items, searchTerm),
    [items, searchTerm]
  );
  const searching = Boolean(String(searchTerm || "").trim());
  const totalPages = searching
    ? 1
    : Math.max(1, Math.ceil((total || 0) / (limit || 20)));

  return (
    <Card className="border-0 shadow-sm">
      <Card.Header className="club-sales-card-header py-3 border-bottom d-flex flex-column flex-sm-row flex-wrap align-items-sm-center justify-content-between gap-2">
        <div>
          <span className="fw-semibold d-block">{t("GUESTLIST_BOOKINGS_TITLE")}</span>
          <span className="text-muted small fw-normal d-block mt-1">
            {t("GUESTLIST_BOOKINGS_HINT")}
          </span>
        </div>
        <div className="d-flex flex-wrap align-items-center gap-2">
          <Form.Select
            className="club-sales-bookings-limit"
            style={{ width: "auto", minWidth: "5.5rem" }}
            value={String(limit)}
            onChange={(e) => onLimitChange(Number(e.target.value))}
          >
            {BOOKING_PAGE_SIZE_OPTIONS.map((size) => (
              <option key={size} value={String(size)}>
                {size}
              </option>
            ))}
          </Form.Select>
        </div>
      </Card.Header>
      <Card.Body className="p-0">
        {loading ? (
          <div className="club-sales-loading-panel border-0">
            <Spinner animation="border" size="sm" role="status" />
          </div>
        ) : displayItems.length === 0 ? (
          <EmptySection t={t} />
        ) : (
          <div className="table-responsive club-sales-bookings-wrap">
            <Table hover className="mb-0 align-middle small club-sales-ref-table">
              <thead className="club-sales-table-head">
                <tr style={{ fontSize: "0.68rem" }} className="text-uppercase">
                  <th className="ps-3 py-3 border-0">{t("GUESTLIST_COL_DATE")}</th>
                  <th className="py-3 border-0">{t("CLUB_SALES_COL_EVENT")}</th>
                  <th className="py-3 border-0">{t("GUESTLIST_CATEGORY")}</th>
                  <th className="py-3 border-0">{t("GUESTLIST_GUEST_NAME")}</th>
                  <th className="py-3 border-0">{t("GUESTLIST_GUEST_EMAIL")}</th>
                  <th className="py-3 border-0">{t("GUESTLIST_GUEST_PHONE")}</th>
                  <th className="text-end py-3 border-0">{t("CLUB_SALES_BOOKING_QTY")}</th>
                  <th className="text-end py-3 border-0">{t("CLUB_SALES_BOOKING_TOTAL")}</th>
                  <th className="py-3 border-0">{t("GUESTLIST_COL_ADDED_BY")}</th>
                  <th className="pe-3 py-3 border-0">{t("GUESTLIST_COL_BADGE")}</th>
                </tr>
              </thead>
              <tbody>
                {displayItems.map((row, i) => (
                  <tr key={`gl-bk-${row?.orderRef ?? row?.id ?? i}`}>
                    <td className="ps-3 text-nowrap">
                      {formatBookingTimestamp(row, t)}
                    </td>
                    <td className="small fw-medium">
                      {dash(pickBookingEventTitle(row) || null, t)}
                    </td>
                    <td className="small">
                      {dash(pickTicketTypeLabel(row) || null, t)}
                    </td>
                    <td className="small">{dash(row?.buyername, t)}</td>
                    <td className="small text-break">
                      {dash(row?.buyeremail, t)}
                    </td>
                    <td className="small">{dash(row?.buyerphone, t)}</td>
                    <td className="text-end tabular-nums">
                      {pickTicketQty(row) ?? "—"}
                    </td>
                    <td className="text-end tabular-nums fw-medium">
                      {fmtCurrency(row?.amountPaid ?? row?.amount ?? 0)}
                    </td>
                    <td className="small">{dash(row?.soldBy?.fullname, t)}</td>
                    <td className="pe-3">
                      {row?.isguestlist ? (
                        <Badge pill bg="info" className="fw-normal px-2 py-1">
                          {t("GUESTLIST_BADGE")}
                        </Badge>
                      ) : (
                        <span className="text-muted small">—</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>
        )}
        {!loading && !searching && total > 0 ? (
          <div className="d-flex flex-wrap align-items-center justify-content-between gap-2 px-3 py-3 border-top border-secondary border-opacity-25">
            <span className="small text-muted">
              {t("CLUB_SALES_BOOKINGS_PAGE_OF", {
                page,
                totalPages,
                count: total,
              })}
            </span>
            <Pagination className="mb-0 flex-wrap">
              <Pagination.Prev
                disabled={page <= 1}
                onClick={() => onPageChange(page - 1)}
              />
              <Pagination.Item active>{page}</Pagination.Item>
              <Pagination.Next
                disabled={page >= totalPages}
                onClick={() => onPageChange(page + 1)}
              />
            </Pagination>
          </div>
        ) : null}
        {!loading && searching ? (
          <div className="px-3 py-3 border-top border-secondary border-opacity-25">
            <span className="small text-muted">
              {t("CLUB_SALES_BOOKINGS_SEARCH_MATCHES", {
                count: displayItems.length,
              })}
            </span>
          </div>
        ) : null}
      </Card.Body>
    </Card>
  );
}

function BookingBuyerCell({ row, t }) {
  const { name, phone, email } = pickBookingBuyerParts(row);
  if (!name && !phone && !email) {
    return <span className="small">{dash(null, t)}</span>;
  }
  return (
    <div className="small">
      {name ? <div className="fw-medium text-break">{name}</div> : null}
      {phone || email ? (
        <div className="text-muted text-break" style={{ fontSize: "0.85em" }}>
          {[phone, email].filter(Boolean).join(" · ")}
        </div>
      ) : null}
    </div>
  );
}

/**
 * Booking value column. Always shows ticket face value (`amount`), never
 * `amountPaid`, so complimentary guestlist rows read their real ticket value
 * instead of ₹0. Guestlist rows add a subtle "Comp · ₹0 paid" label.
 */
function BookingValueCell({ row, t }) {
  const amount = pickBookingAmount(row);
  if (!isGuestlistBookingRow(row)) {
    return <>{fmtCurrency(amount)}</>;
  }
  const paid = pickBookingAmountPaid(row) ?? 0;
  return (
    <div>
      <div>{fmtCurrency(amount)}</div>
      <div
        className="text-muted fw-normal"
        style={{ fontSize: "0.72rem" }}
        title={t("CLUB_SALES_BOOKING_COMP_TOOLTIP")}
      >
        {t("CLUB_SALES_BOOKING_COMP_PAID", { paid: fmtCurrency(paid) })}
      </div>
    </div>
  );
}

function ClubSalesBookingsTable({
  items,
  loading,
  page,
  limit,
  total,
  searchTerm = "",
  onPageChange,
  onLimitChange,
  isAdmin,
  canSeeAdminIds = isAdmin,
  t,
}) {
  const displayItems = useMemo(() => {
    const grouped = groupBookingListRows(items);
    return filterBookingListItems(grouped, searchTerm);
  }, [items, searchTerm]);

  // Admin-only identifier columns. Show when the session is admin OR the API
  // payload exposes the admin-only fields. We detect via orderRef/paymentid/
  // transactionid (NOT bookingRef — normalizeBookingListItem adds bookingRef
  // for every row, so it is not a reliable admin signal).
  const payloadHasAdminIds = useMemo(
    () =>
      asArray(displayItems).some(
        (it) =>
          it &&
          typeof it === "object" &&
          ("orderRef" in it || "paymentid" in it || "transactionid" in it)
      ),
    [displayItems]
  );
  const showAdminIds = Boolean(canSeeAdminIds) || payloadHasAdminIds;

  const searching = Boolean(String(searchTerm || "").trim());
  const totalPages = searching
    ? 1
    : Math.max(1, Math.ceil((total || 0) / (limit || 20)));

  return (
    <Card className="border-0 shadow-sm">
      <Card.Header className="club-sales-card-header py-3 border-bottom d-flex flex-column flex-sm-row flex-wrap align-items-sm-center justify-content-between gap-2">
        <div>
          <span className="fw-semibold d-block">{t("CLUB_SALES_BOOKINGS_TITLE")}</span>
          <span className="text-muted small fw-normal d-block mt-1">
            {t("CLUB_SALES_BOOKINGS_HINT")}
          </span>
        </div>
        <div className="d-flex flex-wrap align-items-center gap-2">
          <Form.Select
            className="club-sales-bookings-limit"
            style={{ width: "auto", minWidth: "5.5rem" }}
            value={String(limit)}
            onChange={(e) => onLimitChange(Number(e.target.value))}
          >
            {BOOKING_PAGE_SIZE_OPTIONS.map((size) => (
              <option key={size} value={String(size)}>
                {size}
              </option>
            ))}
          </Form.Select>
        </div>
      </Card.Header>
      <Card.Body className="p-0">
        {loading ? (
          <div className="club-sales-loading-panel border-0">
            <Spinner animation="border" size="sm" role="status" />
          </div>
        ) : displayItems.length === 0 ? (
          <EmptySection t={t} />
        ) : (
          <div className="table-responsive club-sales-bookings-wrap">
            <Table hover className="mb-0 align-middle small club-sales-ref-table">
              <thead className="club-sales-table-head">
                <tr style={{ fontSize: "0.68rem" }} className="text-uppercase">
                  {showAdminIds ? (
                    <th className="ps-3 py-3 border-0">{t("CLUB_SALES_BOOKING_ORDER_REF")}</th>
                  ) : null}
                  <th className={`${showAdminIds ? "" : "ps-3 "}py-3 border-0`}>
                    {t("CLUB_SALES_BOOKING_USER_DETAILS")}
                  </th>
                  {showAdminIds ? (
                    <th className="py-3 border-0">{t("CLUB_SALES_BOOKING_ID")}</th>
                  ) : null}
                  <th className="py-3 border-0">{t("CLUB_SALES_COL_EVENT")}</th>
                  <th className="py-3 border-0">{t("CLUB_SALES_BOOKING_TIME")}</th>
                  <th className="py-3 border-0">{t("CLUB_SALES_BOOKING_TICKET_TYPE")}</th>
                  <th className="text-end py-3 border-0">{t("CLUB_SALES_BOOKING_QTY")}</th>
                  <th className="text-end py-3 border-0">{t("CLUB_SALES_BOOKING_TOTAL")}</th>
                  {/* <th className="py-3 border-0">{t("CLUB_SALES_BOOKING_REF")}</th> */}
                  <th className="py-3 border-0">{t("CLUB_SALES_COL_CHANNEL")}</th>
                  <th className="py-3 border-0">{t("CLUB_SALES_BOOKING_PAYMENT")}</th>
                  {isAdmin ? (
                    <th className="py-3 border-0">{t("CLUB_SALES_COL_CLUB")}</th>
                  ) : null}
                  <th className={`${showAdminIds ? "" : "pe-3 "}py-3 border-0`}>
                    {t("CLUB_SALES_BOOKING_SOLD_BY")}
                  </th>
                  {showAdminIds ? (
                    <th className="pe-3 py-3 border-0">{t("CLUB_SALES_BOOKING_TXN_ID")}</th>
                  ) : null}
                </tr>
              </thead>
              <tbody>
                {displayItems.map((row, i) => (
                  <tr key={`bk-${pickBookingRef(row) || pickOrderId(row) || i}`}>
                    {showAdminIds ? (
                      <td className="ps-3 font-monospace small text-break">
                        {dash(pickAdminOrderRef(row) || null, t)}
                      </td>
                    ) : null}
                    <td className={showAdminIds ? "" : "ps-3"}>
                      <BookingBuyerCell row={row} t={t} />
                    </td>
                    {showAdminIds ? (
                      <td className="font-monospace small fw-medium text-nowrap">
                        {dash(pickAdminBookingId(row) || null, t)}
                      </td>
                    ) : null}
                    <td className="small fw-medium">{dash(pickBookingEventTitle(row) || null, t)}</td>
                    <td className="text-nowrap">{formatBookingTimestamp(row, t)}</td>
                    <td className="small">{dash(pickTicketTypeLabel(row) || null, t)}</td>
                    <td className="text-end tabular-nums">{pickTicketQty(row) ?? "—"}</td>
                    <td className="text-end tabular-nums fw-medium">
                      <BookingValueCell row={row} t={t} />
                    </td>
                    {/* <td className="font-monospace small fw-medium">
                      {pickBookingRefDisplay(row, t)}
                    </td> */}
                    <td>
                      <ChannelBadge
                        value={
                          row?.isguestlist || row?.isGuestlist
                            ? "GUESTLIST"
                            : row?.salechannel
                        }
                        t={t}
                      />
                    </td>
                    <td className="small">{dash(row?.offlinepaymentmethod, t)}</td>
                    {isAdmin ? (
                      <td className="small">{dash(row?.club?.name, t)}</td>
                    ) : null}
                    <td className={`${showAdminIds ? "" : "pe-3 "}small`}>
                      {dash(row?.soldBy?.fullname, t)}
                    </td>
                    {showAdminIds ? (
                      <td className="pe-3 font-monospace small text-break">
                        {dash(pickAdminTransactionId(row) || null, t)}
                      </td>
                    ) : null}
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>
        )}
        {!loading && !searching && total > 0 ? (
          <div className="d-flex flex-wrap align-items-center justify-content-between gap-2 px-3 py-3 border-top border-secondary border-opacity-25">
            <span className="small text-muted">
              {t("CLUB_SALES_BOOKINGS_PAGE_OF", {
                page,
                totalPages,
                count: total,
              })}
            </span>
            <Pagination className="mb-0 flex-wrap">
              <Pagination.Prev
                disabled={page <= 1}
                onClick={() => onPageChange(page - 1)}
              />
              <Pagination.Item active>{page}</Pagination.Item>
              <Pagination.Next
                disabled={page >= totalPages}
                onClick={() => onPageChange(page + 1)}
              />
            </Pagination>
          </div>
        ) : null}
        {!loading && searching ? (
          <div className="px-3 py-3 border-top border-secondary border-opacity-25">
            <span className="small text-muted">
              {t("CLUB_SALES_BOOKINGS_SEARCH_MATCHES", {
                count: displayItems.length,
              })}
            </span>
          </div>
        ) : null}
      </Card.Body>
    </Card>
  );
}

function IssuedBuyerCell({ row, t }) {
  const b = row?.buyer;
  const name = (b?.name ?? "").trim();
  const phone = (b?.phone ?? "").trim();
  const email = (b?.email ?? "").trim();
  if (!name && !phone && !email) {
    return <span className="small">{dash(null, t)}</span>;
  }
  return (
    <div className="small">
      {name ? <div className="fw-medium text-break">{name}</div> : null}
      {phone || email ? (
        <div className="text-muted text-break" style={{ fontSize: "0.85em" }}>
          {[phone, email].filter(Boolean).join(" · ")}
        </div>
      ) : null}
    </div>
  );
}

function RegisteredUserCell({ ru, t }) {
  if (!ru || typeof ru !== "object") {
    return (
      <span className="text-muted small">{t("CLUB_SALES_ISSUED_NO_ACCOUNT")}</span>
    );
  }
  const name = (ru.fullname ?? "").trim();
  const phone = (ru.phone ?? "").trim();
  const email = (ru.email ?? "").trim();
  if (!name && !phone && !email) {
    return (
      <span className="text-muted small">{t("CLUB_SALES_ISSUED_NO_ACCOUNT")}</span>
    );
  }
  return (
    <div className="small">
      {name ? <div className="fw-medium text-break">{name}</div> : null}
      {phone || email ? (
        <div className="text-muted text-break" style={{ fontSize: "0.85em" }}>
          {[phone, email].filter(Boolean).join(" · ")}
        </div>
      ) : null}
    </div>
  );
}

function ClubSalesIssuedTicketsTable({
  items,
  loading,
  page,
  limit,
  total,
  onPageChange,
  onLimitChange,
  isAdmin,
  resendingOrderId,
  onResendLink,
  // onCopyTicketUrl,
  t,
}) {
  const totalPages = Math.max(1, Math.ceil((total || 0) / (limit || 20)));

  return (
    <Card className="border-0 shadow-sm">
      <Card.Header className="club-sales-card-header py-3 border-bottom d-flex flex-column flex-sm-row flex-wrap align-items-sm-center justify-content-between gap-2">
        <div>
          <span className="fw-semibold d-block">{t("CLUB_SALES_ISSUED_TITLE")}</span>
          <span className="text-muted small fw-normal d-block mt-1">
            {t("CLUB_SALES_ISSUED_HINT")}
          </span>
        </div>
        <div className="d-flex flex-wrap align-items-center gap-2">
          <Form.Select
            className="club-sales-bookings-limit"
            style={{ width: "auto", minWidth: "5.5rem" }}
            value={String(limit)}
            onChange={(e) => onLimitChange(Number(e.target.value))}
          >
            {BOOKING_PAGE_SIZE_OPTIONS.map((size) => (
              <option key={size} value={String(size)}>
                {size}
              </option>
            ))}
          </Form.Select>
        </div>
      </Card.Header>
      <Card.Body className="p-0">
        {loading ? (
          <div className="club-sales-loading-panel border-0">
            <Spinner animation="border" size="sm" role="status" />
          </div>
        ) : items.length === 0 ? (
          <EmptySection t={t} />
        ) : (
          <div className="table-responsive club-sales-bookings-wrap">
            <Table hover className="mb-0 align-middle small club-sales-ref-table">
              <thead className="club-sales-table-head">
                <tr style={{ fontSize: "0.68rem" }} className="text-uppercase">
                  <th className="ps-3 py-3 border-0">{t("CLUB_SALES_BOOKING_BUYER")}</th>
                  <th className="py-3 border-0">{t("CLUB_SALES_ISSUED_REGISTERED_USER")}</th>
                  <th className="py-3 border-0">{t("CLUB_SALES_COL_EVENT")}</th>
                  <th className="py-3 border-0">{t("CLUB_SALES_ISSUED_SALE_TIME")}</th>
                  <th className="py-3 border-0">{t("CLUB_SALES_BOOKING_PAYMENT")}</th>
                  <th className="text-end py-3 border-0">{t("CLUB_SALES_BOOKING_TOTAL")}</th>
                  <th className="py-3 border-0">{t("CLUB_SALES_ISSUED_BOOKING_REF")}</th>
                  {/* <th className="py-3 border-0">{t("CLUB_SALES_ISSUED_VIEW_LINK")}</th> */}
                  <th className="py-3 border-0">{t("CLUB_SALES_BOOKING_SOLD_BY")}</th>
                  {isAdmin ? (
                    <th className="py-3 border-0">{t("CLUB_SALES_COL_CLUB")}</th>
                  ) : null}
                  <th className="pe-3 py-3 border-0 text-nowrap">{t("CLUB_SALES_ISSUED_RESEND_LINK")}</th>
                </tr>
              </thead>
              <tbody>
                {items.map((row, i) => {
                  const oid = row?.id ?? row?._id;
                  const oidStr = oid != null ? String(oid) : "";
                  // const url = row?.viewTicketUrl;
                  const busy = oidStr && resendingOrderId === oidStr;
                  return (
                    <tr key={`iss-${row?.orderRef ?? oidStr ?? i}`}>
                      <td className="ps-3">
                        <IssuedBuyerCell row={row} t={t} />
                      </td>
                      <td className="align-top">
                        <RegisteredUserCell ru={row?.registeredUser} t={t} />
                      </td>
                      <td className="small fw-medium">{dash(row?.event?.title, t)}</td>
                      <td className="text-nowrap">{formatBookingTimestamp(row, t)}</td>
                      <td className="small">{dash(row?.offlinepaymentmethod, t)}</td>
                      <td className="text-end tabular-nums fw-medium">
                        {fmtCurrency(row?.amount)}
                        {row?.currency &&
                        String(row.currency).toUpperCase() !== DEFAULT_CURRENCY_CODE ? (
                          <span className="text-muted small ms-1">{row.currency}</span>
                        ) : null}
                      </td>
                      <td className="font-monospace small fw-medium">
                        {dash(pickBookingRef(row) || null, t)}
                      </td>
                      {/* <td className="align-top">
                        {url ? (
                          <div className="d-flex flex-column gap-1">
                            <Button
                              variant="outline-primary"
                              size="sm"
                              className="py-0 px-2"
                              href={url}
                              target="_blank"
                              rel="noopener noreferrer"
                            >
                              {t("CLUB_SALES_ISSUED_OPEN_LINK")}
                            </Button>
                            <Button
                              variant="outline-secondary"
                              size="sm"
                              className="py-0 px-2"
                              type="button"
                              onClick={() => onCopyTicketUrl(url)}
                            >
                              {t("CLUB_SALES_ISSUED_COPY_LINK")}
                            </Button>
                          </div>
                        ) : (
                          dash(null, t)
                        )}
                      </td> */}
                      <td className="small">{dash(row?.soldBy?.fullname, t)}</td>
                      {isAdmin ? (
                        <td className="small">{dash(row?.club?.name, t)}</td>
                      ) : null}
                      <td className="pe-3 align-middle">
                        <Button
                          variant="outline-success"
                          size="sm"
                          type="button"
                          disabled={!oidStr || busy}
                          onClick={() => onResendLink(oidStr)}
                        >
                          {busy ? t("LOADING") : t("CLUB_SALES_ISSUED_RESEND_LINK")}
                        </Button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </Table>
          </div>
        )}
        {!loading && total > 0 ? (
          <div className="d-flex flex-wrap align-items-center justify-content-between gap-2 px-3 py-3 border-top border-secondary border-opacity-25">
            <span className="small text-muted">
              {t("CLUB_SALES_ISSUED_PAGE_OF", {
                page,
                totalPages,
                count: total,
              })}
            </span>
            <Pagination className="mb-0 flex-wrap">
              <Pagination.Prev
                disabled={page <= 1}
                onClick={() => onPageChange(page - 1)}
              />
              <Pagination.Item active>{page}</Pagination.Item>
              <Pagination.Next
                disabled={page >= totalPages}
                onClick={() => onPageChange(page + 1)}
              />
            </Pagination>
          </div>
        ) : null}
      </Card.Body>
    </Card>
  );
}

/** Order/payment statuses supported by GET /order/event/:eventId/bookings. */
const EVENT_BOOKING_ORDER_STATUSES = [
  "Pending",
  "Paid",
  "Fail",
  "Cancelled",
  "Refunded",
  "Refund-fail",
];
const EVENT_BOOKING_PAYMENT_STATUSES = [
  "created",
  "attempted",
  "authorized",
  "captured",
  "refunded",
  "failed",
];

/** Map an order/payment status string to a bootstrap badge variant. */
function eventBookingStatusVariant(status) {
  const s = String(status || "").toLowerCase();
  if (["paid", "captured", "authorized"].includes(s)) return "success";
  if (["pending", "created", "attempted"].includes(s)) return "warning";
  if (["refunded"].includes(s)) return "info";
  if (["fail", "failed", "refund-fail", "cancelled"].includes(s)) return "danger";
  return "secondary";
}

/**
 * One bookings[] item = one booking. The individual ticket types live in
 * tickets[]. Summarize them as `[{ label, qty, price }]` grouped by type
 * (and price) so 100 identical tickets show as one line, not 100.
 */
function summarizeBookingTicketLines(row) {
  const tickets = asArray(row?.tickets);
  if (tickets.length === 0) {
    const label = row?.tickettype;
    const price = row?.ticketAmount ?? row?.ticketprice ?? null;
    if (
      (label == null || String(label).trim() === "") &&
      (price == null || price === "")
    ) {
      return [];
    }
    return [
      {
        label: label != null ? String(label) : "",
        qty: Number(row?.noofticket) || null,
        price: price != null && price !== "" ? Number(price) : null,
      },
    ];
  }

  const byKey = new Map();

  for (const tk of tickets) {
    const label =
      tk?.tickettype ??
      tk?.type ??
      tk?.name ??
      tk?.category ??
      tk?.title ??
      "";
    const labelStr = String(label || "").trim();
    const rawQty =
      tk?.noofticket ?? tk?.qty ?? tk?.quantity ?? tk?.count ?? null;
    const qty =
      rawQty != null && rawQty !== ""
        ? Number(rawQty) || 0
        : 1;
    const price =
      tk?.ticketprice ?? tk?.ticketAmount ?? tk?.price ?? tk?.amount ?? null;
    const priceNum =
      price != null && price !== "" ? Number(price) : null;
    const typeId =
      tk?.tickettypeid ?? tk?.ticketTypeId ?? tk?.typeId ?? "";

    if (!labelStr && qty <= 0 && priceNum == null) continue;

    const key = `${typeId}|${labelStr}|${priceNum ?? ""}`;
    const existing = byKey.get(key);
    if (existing) {
      existing.qty += qty;
    } else {
      byKey.set(key, {
        label: labelStr,
        qty: qty > 0 ? qty : null,
        price: priceNum,
      });
    }
  }

  return [...byKey.values()].filter(
    (line) => line.label !== "" || line.qty != null || line.price != null
  );
}

/** Total ticket count for a booking: prefer noofticket, else sum tickets[]. */
function totalTicketsForBooking(row) {
  if (row?.noofticket != null && row.noofticket !== "") {
    return Number(row.noofticket);
  }
  const tickets = asArray(row?.tickets);
  if (tickets.length === 0) return null;
  const sum = tickets.reduce((acc, tk) => {
    const q = tk?.noofticket ?? tk?.qty ?? tk?.quantity ?? tk?.count ?? 0;
    return acc + (Number(q) || 0);
  }, 0);
  return sum > 0 ? sum : null;
}

function EventBookingTicketTypeCell({ row, t }) {
  const lines = summarizeBookingTicketLines(row);
  if (lines.length === 0) return <span className="small">{dash(null, t)}</span>;
  return (
    <div className="small d-flex flex-column gap-1">
      {lines.map((line, i) => (
        <span key={`tk-${line.label}-${i}`} className="text-break">
          <span className="fw-medium">{line.label || dash(null, t)}</span>
          {line.qty != null ? (
            <span className="text-muted ms-1">+� {line.qty}</span>
          ) : null}
          {line.price != null ? (
            <span className="text-muted ms-1">@ {fmtCurrency(line.price)}</span>
          ) : null}
        </span>
      ))}
    </div>
  );
}

function EventBookingStatusBadge({ status, t }) {
  if (status == null || status === "") {
    return <span className="text-muted small">{t("CLUB_SALES_VALUE_EMPTY")}</span>;
  }
  return (
    <Badge
      pill
      bg={eventBookingStatusVariant(status)}
      className="fw-normal px-2 py-1"
    >
      {String(status)}
    </Badge>
  );
}

/** Render orderStatusSummary / paymentStatusSummary objects as count chips. */
function EventBookingSummaryChips({ label, summary, t }) {
  const entries = Object.entries(summary || {}).filter(
    ([, count]) => Number(count) > 0
  );
  if (entries.length === 0) return null;
  return (
    <div className="d-flex flex-wrap align-items-center gap-2">
      <span className="small text-uppercase text-muted">{label}</span>
      {entries.map(([key, count]) => (
        <Badge
          key={key}
          pill
          bg={eventBookingStatusVariant(key)}
          className="fw-normal px-2 py-1"
        >
          {key}: {count}
        </Badge>
      ))}
    </div>
  );
}

function ClubSalesEventBookingsTable({
  data,
  loading,
  hasEvent,
  page,
  limit,
  statusFilter,
  searchTerm = "",
  onStatusChange,
  onPageChange,
  onLimitChange,
  t,
}) {
  const allBookings = asArray(data?.bookings);
  const searching = Boolean(String(searchTerm || "").trim());
  const bookings = useMemo(
    () => filterEventBookingItems(allBookings, searchTerm),
    [allBookings, searchTerm]
  );
  const pagination = data?.pagination || {};
  const total = Number(pagination.total) || 0;
  const totalPages = searching
    ? 1
    : Number(pagination.totalPages) ||
      Math.max(1, Math.ceil(total / (limit || 20)));

  return (
    <Card className="border-0 shadow-sm">
      <Card.Header className="club-sales-card-header py-3 border-bottom d-flex flex-column flex-sm-row flex-wrap align-items-sm-center justify-content-between gap-2">
        <div>
          <span className="fw-semibold d-block">
            {t("CLUB_SALES_EVENT_BOOKINGS_TITLE")}
          </span>
          <span className="text-muted small fw-normal d-block mt-1">
            {t("CLUB_SALES_EVENT_BOOKINGS_HINT")}
          </span>
        </div>
        <div className="d-flex flex-wrap align-items-center gap-2">
          <Form.Select
            className="club-sales-bookings-limit"
            style={{ width: "auto", minWidth: "11rem" }}
            value={statusFilter}
            onChange={(e) => onStatusChange(e.target.value)}
            disabled={!hasEvent}
            aria-label={t("CLUB_SALES_EVENT_BOOKINGS_STATUS_FILTER")}
          >
            <option value="">{t("CLUB_SALES_EVENT_BOOKINGS_STATUS_ALL")}</option>
            <optgroup label={t("CLUB_SALES_EVENT_BOOKINGS_ORDER_SUMMARY")}>
              {EVENT_BOOKING_ORDER_STATUSES.map((s) => (
                <option key={`os-${s}`} value={s}>
                  {s}
                </option>
              ))}
            </optgroup>
            <optgroup label={t("CLUB_SALES_EVENT_BOOKINGS_PAYMENT_SUMMARY")}>
              {EVENT_BOOKING_PAYMENT_STATUSES.map((s) => (
                <option key={`ps-${s}`} value={s}>
                  {s}
                </option>
              ))}
            </optgroup>
          </Form.Select>
          <Form.Select
            className="club-sales-bookings-limit"
            style={{ width: "auto", minWidth: "5.5rem" }}
            value={String(limit)}
            onChange={(e) => onLimitChange(Number(e.target.value))}
            disabled={!hasEvent}
          >
            {BOOKING_PAGE_SIZE_OPTIONS.map((size) => (
              <option key={size} value={String(size)}>
                {size}
              </option>
            ))}
          </Form.Select>
        </div>
      </Card.Header>
      <Card.Body className="p-0">
        {!hasEvent ? (
          <div className="club-sales-empty-hint p-4 small text-muted">
            {t("CLUB_SALES_EVENT_BOOKINGS_SELECT_EVENT")}
          </div>
        ) : loading ? (
          <div className="club-sales-loading-panel border-0">
            <Spinner animation="border" size="sm" role="status" />
          </div>
        ) : (
          <>
            {/* {(Object.keys(data?.orderStatusSummary || {}).length > 0 ||
              Object.keys(data?.paymentStatusSummary || {}).length > 0) && (
              <div className="d-flex flex-column gap-2 px-3 py-3 border-bottom border-secondary border-opacity-25">
                <EventBookingSummaryChips
                  label={t("CLUB_SALES_EVENT_BOOKINGS_ORDER_SUMMARY")}
                  summary={data?.orderStatusSummary}
                  t={t}
                />
                <EventBookingSummaryChips
                  label={t("CLUB_SALES_EVENT_BOOKINGS_PAYMENT_SUMMARY")}
                  summary={data?.paymentStatusSummary}
                  t={t}
                />
              </div>
            )} */}
            {bookings.length === 0 ? (
              <EmptySection t={t} />
            ) : (
              <div className="table-responsive club-sales-bookings-wrap">
                <Table hover className="mb-0 align-middle small club-sales-ref-table">
                  <thead className="club-sales-table-head">
                    <tr style={{ fontSize: "0.68rem" }} className="text-uppercase">
                      <th className="ps-3 py-3 border-0">
                        {t("CLUB_SALES_EVENT_BOOKINGS_COL_BUYER")}
                      </th>
                      <th className="py-3 border-0 text-center">
                        {t("CLUB_SALES_EVENT_BOOKINGS_COL_TICKETS")}
                      </th>
                      <th className="py-3 border-0">
                        {t("CLUB_SALES_EVENT_BOOKINGS_COL_TYPE")}
                      </th>
                      <th className="text-end py-3 border-0">
                        {t("CLUB_SALES_EVENT_BOOKINGS_COL_AMOUNT")}
                      </th>
                      <th className="text-end py-3 border-0">
                        {t("CLUB_SALES_EVENT_BOOKINGS_COL_TOTAL_PAID")}
                      </th>
                      <th className="py-3 border-0">
                        {t("CLUB_SALES_EVENT_BOOKINGS_COL_ORDER_STATUS")}
                      </th>
                      <th className="py-3 border-0">
                        {t("CLUB_SALES_EVENT_BOOKINGS_COL_PAYMENT_STATUS")}
                      </th>
                      <th className="py-3 border-0">
                        {t("CLUB_SALES_EVENT_BOOKINGS_COL_CHANNEL")}
                      </th>
                      <th className="py-3 border-0">
                        {t("CLUB_SALES_EVENT_BOOKINGS_COL_REF")}
                      </th>
                      <th className="py-3 border-0">
                        {t("CLUB_SALES_EVENT_BOOKINGS_COL_PAYMENT_REF")}
                      </th>
                      <th className="pe-3 py-3 border-0 text-nowrap">
                        {t("CLUB_SALES_EVENT_BOOKINGS_COL_TIME")}
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {bookings.map((row, i) => {
                      const contact = [row?.buyerphone, row?.buyeremail]
                        .filter(Boolean)
                        .join(" -+ ");
                      return (
                        <tr key={`evb-${row?._id ?? row?.bookingref ?? i}`}>
                          <td className="ps-3">
                            <div className="small">
                              {row?.buyername ? (
                                <div className="fw-medium text-break">
                                  {row.buyername}
                                </div>
                              ) : null}
                              {contact ? (
                                <div
                                  className="text-muted text-break"
                                  style={{ fontSize: "0.85em" }}
                                >
                                  {contact}
                                </div>
                              ) : null}
                              {!row?.buyername && !contact
                                ? dash(null, t)
                                : null}
                            </div>
                          </td>
                          <td className="text-center tabular-nums">
                            {(() => {
                              const n = totalTicketsForBooking(row);
                              return n != null ? n : dash(null, t);
                            })()}
                          </td>
                          <td>
                            <EventBookingTicketTypeCell row={row} t={t} />
                          </td>
                          <td className="text-end tabular-nums fw-medium">
                            {fmtCurrency(row?.ticketAmount ?? row?.amount)}
                            {row?.currency &&
                            String(row.currency).toUpperCase() !==
                              DEFAULT_CURRENCY_CODE ? (
                              <span className="text-muted small ms-1">
                                {row.currency}
                              </span>
                            ) : null}
                          </td>
                          <td className="text-end tabular-nums fw-medium">
                            {fmtCurrency(row?.totalAmount ?? row?.amount)}
                            {row?.currency &&
                            String(row.currency).toUpperCase() !==
                              DEFAULT_CURRENCY_CODE ? (
                              <span className="text-muted small ms-1">
                                {row.currency}
                              </span>
                            ) : null}
                          </td>
                          <td>
                            <EventBookingStatusBadge
                              status={row?.orderstatus}
                              t={t}
                            />
                          </td>
                          <td>
                            <EventBookingStatusBadge
                              status={row?.paymentstatus}
                              t={t}
                            />
                          </td>
                          <td className="small">{dash(row?.salechannel, t)}</td>
                          <td className="font-monospace small fw-medium">
                            {dash(row?.bookingref, t)}
                          </td>
                          <td className="font-monospace small">
                            {(() => {
                              const txn =
                                row?.transactionid ?? row?.paymentid ?? null;
                              const ord = row?.orderid ?? null;
                              if (!txn && !ord) return dash(null, t);
                              return (
                                <div className="d-flex flex-column">
                                  {ord ? (
                                    <span className="text-break">
                                      <span className="text-muted me-1">
                                        {t("CLUB_SALES_EVENT_BOOKINGS_ORDER_ID")}:
                                      </span>
                                      {ord}
                                    </span>
                                  ) : null}
                                  {txn ? (
                                    <span className="text-break">
                                      <span className="text-muted me-1">
                                        {t("CLUB_SALES_EVENT_BOOKINGS_TXN_ID")}:
                                      </span>
                                      {txn}
                                    </span>
                                  ) : null}
                                </div>
                              );
                            })()}
                          </td>
                          <td className="text-nowrap">
                            {formatBookingTimestamp(row, t)}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </Table>
              </div>
            )}
          </>
        )}
        {hasEvent && !loading && !searching && total > 0 ? (
          <div className="d-flex flex-wrap align-items-center justify-content-between gap-2 px-3 py-3 border-top border-secondary border-opacity-25">
            <span className="small text-muted">
              {t("CLUB_SALES_EVENT_BOOKINGS_PAGE_OF", {
                page,
                totalPages,
                count: total,
              })}
            </span>
            <Pagination className="mb-0 flex-wrap">
              <Pagination.Prev
                disabled={page <= 1}
                onClick={() => onPageChange(page - 1)}
              />
              <Pagination.Item active>{page}</Pagination.Item>
              <Pagination.Next
                disabled={page >= totalPages}
                onClick={() => onPageChange(page + 1)}
              />
            </Pagination>
          </div>
        ) : null}
        {hasEvent && !loading && searching ? (
          <div className="px-3 py-3 border-top border-secondary border-opacity-25">
            <span className="small text-muted">
              {t("CLUB_SALES_BOOKINGS_SEARCH_MATCHES", {
                count: bookings.length,
              })}
            </span>
          </div>
        ) : null}
      </Card.Body>
    </Card>
  );
}

/**
 * Club sales: GET /club/reports/sales + bookings (scoped by token).
 */
const ClubSalesAnalysisContainer = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const reportSection =
    searchParams.get("section") === "event-end" ? "event-end" : "sales";
  const isSalesReportView = reportSection === "sales";
  const isEventEndReportView = reportSection === "event-end";
  const clubList = useSelector((s) => s?.club?.clubList ?? []);
  const {
    userId,
    isClubEmployee,
    isClubManager,
    isAdmin,
    managedClubId,
  } = useAuth();
  const { empref: myEmpref } = useMyEmpref();

  const canViewGuestlistReports = isClubManager || isAdmin;

  const [mainTab, setMainTab] = useState("overview");

  const [salesDateFrom, setSalesDateFrom] = useState("");
  const [salesDateTo, setSalesDateTo] = useState("");
  const [eventEndDateFrom, setEventEndDateFrom] = useState("");
  const [eventEndDateTo, setEventEndDateTo] = useState("");
  const [saleChannel, setSaleChannel] = useState("");
  const isGuestlistChannelFilter = saleChannel === "GUESTLIST";
  const isTableBookingChannelFilter = saleChannel === "TABLE_BOOKING";
  const [soldByUserId, setSoldByUserId] = useState("");
  const [adminClubId, setAdminClubId] = useState("");
  const [eventId, setEventId] = useState("");
  const [eventScope, setEventScope] = useState(EVENT_SCOPES.ONGOING);
  const [breakdownPeriod, setBreakdownPeriod] = useState("");
  const [eventOptions, setEventOptions] = useState([]);
  const [completedEventId, setCompletedEventId] = useState("");
  const [completedEventOptions, setCompletedEventOptions] = useState([]);
  const [eventSummaryFormat, setEventSummaryFormat] = useState("xlsx");
  const [eventInvoiceDownloading, setEventInvoiceDownloading] = useState(false);

  const [salesLoading, setSalesLoading] = useState(false);
  const [salesResult, setSalesResult] = useState(null);
  const [fetchedClubName, setFetchedClubName] = useState(null);

  const [bookingsLoading, setBookingsLoading] = useState(false);
  const [bookingsPage, setBookingsPage] = useState(1);
  const [bookingsLimit, setBookingsLimit] = useState(10);
  const [bookingsTotal, setBookingsTotal] = useState(0);
  const [bookingsItems, setBookingsItems] = useState([]);
  const [bookingsSearch, setBookingsSearch] = useState("");

  const [issuedLoading, setIssuedLoading] = useState(false);
  const [issuedPage, setIssuedPage] = useState(1);
  const [issuedLimit, setIssuedLimit] = useState(20);
  const [issuedTotal, setIssuedTotal] = useState(0);
  const [issuedItems, setIssuedItems] = useState([]);
  const [resendingOrderId, setResendingOrderId] = useState(null);

  const [evbLoading, setEvbLoading] = useState(false);
  const [evbPage, setEvbPage] = useState(1);
  const [evbLimit, setEvbLimit] = useState(20);
  const [evbStatus, setEvbStatus] = useState("");
  const [evbSearch, setEvbSearch] = useState("");
  const [evbData, setEvbData] = useState(null);

  const [tbLinesOpen, setTbLinesOpen] = useState(false);
  const [tbLinesEventId, setTbLinesEventId] = useState(undefined);
  const [tbLinesEventLabel, setTbLinesEventLabel] = useState("");
  const [tbLinesPage, setTbLinesPage] = useState(1);
  const [tbLinesLimit, setTbLinesLimit] = useState(20);
  const [tbLinesTotal, setTbLinesTotal] = useState(0);
  const [tbLinesItems, setTbLinesItems] = useState([]);
  const [tbLinesLoading, setTbLinesLoading] = useState(false);

  const [staffTableBookingLines, setStaffTableBookingLines] = useState([]);

  const [clubEmployees, setClubEmployees] = useState([]);
  const [clubEmployeesLoading, setClubEmployeesLoading] = useState(false);

  const effectiveClubId = useMemo(() => {
    if (isAdmin && adminClubId) return String(adminClubId).trim();
    if (!isAdmin && managedClubId) return String(managedClubId).trim();
    return null;
  }, [isAdmin, adminClubId, managedClubId]);

  useEffect(() => {
    setSalesResult(null);
    setStaffTableBookingLines([]);
  }, [salesDateFrom, salesDateTo]);

  useEffect(() => {
    dispatch(clubListResponce());
  }, [dispatch]);

  useEffect(() => {
    if (isEventEndReportView && !isAdmin && !isClubManager) {
      navigate("/club-sales-analysis?section=sales", { replace: true });
    }
  }, [isEventEndReportView, isAdmin, isClubManager, navigate]);

  useEffect(() => {
    let cancelled = false;
    const loadEvents = async () => {
      if (!effectiveClubId) {
        setEventOptions([]);
        return;
      }
      try {
        const [currentRaw, pastRaw] = await Promise.all([
          instance.get(CURRENT_EVENTS, {
            params: { clubid: effectiveClubId },
          }),
          instance.get(PAST_EVENTS, {
            params: { clubid: effectiveClubId },
          }),
        ]);
        const list = mergeEventLists(
          normalizeListPayload(currentRaw),
          normalizeListPayload(pastRaw)
        );
        if (!cancelled) setEventOptions(list);
      } catch {
        if (!cancelled) setEventOptions([]);
      }
    };
    loadEvents();
    return () => {
      cancelled = true;
    };
  }, [effectiveClubId]);

  const filteredScopedEventOptions = useMemo(() => {
    if (!effectiveClubId) return asArray(eventOptions);
    return filterEventsByScope(eventOptions, eventScope, salesDateFrom, salesDateTo);
  }, [eventOptions, eventScope, salesDateFrom, salesDateTo, effectiveClubId]);

  const scopedEventIdsForReport = useMemo(
    () =>
      new Set(
        filteredScopedEventOptions.map((ev) => String(ev._id)).filter(Boolean)
      ),
    [filteredScopedEventOptions]
  );

  const showBreakdownFilter = eventScope === EVENT_SCOPES.PAST;

  const scopeAllEventsLabel = useMemo(() => {
    switch (eventScope) {
      case EVENT_SCOPES.PAST:
        return t("CLUB_SALES_ALL_PAST_IN_RANGE");
      case EVENT_SCOPES.UPCOMING:
        return t("CLUB_SALES_ALL_UPCOMING_IN_RANGE");
      case EVENT_SCOPES.ONGOING:
        return t("CLUB_SALES_ALL_ONGOING_IN_RANGE");
      default:
        return t("CLUB_SALES_ALL_ACTIVE_IN_RANGE");
    }
  }, [eventScope, t]);

  const scopeNoneEventsLabel = useMemo(() => {
    switch (eventScope) {
      case EVENT_SCOPES.PAST:
        return t("CLUB_SALES_PAST_NONE_IN_RANGE");
      case EVENT_SCOPES.UPCOMING:
        return t("CLUB_SALES_UPCOMING_NONE_IN_RANGE");
      case EVENT_SCOPES.ONGOING:
        return t("CLUB_SALES_ONGOING_NONE_IN_RANGE");
      default:
        return t("CLUB_SALES_EVENTS_NONE_IN_RANGE");
    }
  }, [eventScope, t]);

  const filteredCompletedEventOptions = useMemo(
    () =>
      filterEventsByStartDateRange(
        completedEventOptions,
        eventEndDateFrom,
        eventEndDateTo
      ),
    [completedEventOptions, eventEndDateFrom, eventEndDateTo]
  );

  const resolvedClubLabel = useMemo(() => {
    if (!effectiveClubId) return null;
    const hit = (clubList || []).find(
      (c) => String(c._id) === String(effectiveClubId)
    );
    return hit?.name || hit?.slug || effectiveClubId;
  }, [effectiveClubId, clubList]);

  useEffect(() => {
    if (
      eventId &&
      !filteredScopedEventOptions.some(
        (ev) => String(ev._id) === String(eventId)
      )
    ) {
      setEventId("");
    }
  }, [filteredScopedEventOptions, eventId]);

  useEffect(() => {
    if (!showBreakdownFilter && breakdownPeriod) {
      setBreakdownPeriod("");
    }
  }, [showBreakdownFilter, breakdownPeriod]);

  useEffect(() => {
    setEventId("");
    setBreakdownPeriod("");
  }, [eventScope]);

  useEffect(() => {
    if (
      completedEventId &&
      !filteredCompletedEventOptions.some(
        (ev) => String(ev._id) === String(completedEventId)
      )
    ) {
      setCompletedEventId("");
    }
  }, [filteredCompletedEventOptions, completedEventId]);

  useEffect(() => {
    let cancelled = false;
    const loadCompletedEvents = async () => {
      if (!effectiveClubId) {
        setCompletedEventOptions([]);
        setCompletedEventId("");
        return;
      }
      try {
        const raw = await instance.get(PAST_EVENTS, {
          params: { clubid: effectiveClubId },
        });
        const list = normalizeListPayload(raw).filter((ev) =>
          isEventStatusCompleted(ev?.eventstatus)
        );
        if (!cancelled) {
          setCompletedEventOptions(list);
          setCompletedEventId((prev) =>
            prev && list.some((ev) => String(ev._id) === String(prev))
              ? prev
              : ""
          );
        }
      } catch {
        if (!cancelled) {
          setCompletedEventOptions([]);
          setCompletedEventId("");
        }
      }
    };
    loadCompletedEvents();
    return () => {
      cancelled = true;
    };
  }, [effectiveClubId]);

  useEffect(() => {
    setSoldByUserId("");
  }, [effectiveClubId]);

  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      if (!effectiveClubId || isClubEmployee) {
        setClubEmployees([]);
        setClubEmployeesLoading(false);
        return;
      }
      setClubEmployeesLoading(true);
      try {
        const res = await fetchClubEmployees(effectiveClubId);
        const raw = res?.data ?? res;
        let list = asClubEmployeesList(raw);
        /* Some APIs double-wrap: { data: { data: [...] } } */
        if (
          list.length === 0 &&
          raw &&
          typeof raw === "object" &&
          raw.data != null
        ) {
          list = asClubEmployeesList(raw.data);
        }
        if (!cancelled) setClubEmployees(list);
      } catch (err) {
        if (!cancelled) setClubEmployees([]);
        if (!cancelled) {
          const msg =
            (typeof err?.response?.data?.message === "string" &&
              err.response.data.message) ||
            (typeof err?.response?.data?.error === "string" &&
              err.response.data.error) ||
            (typeof err?.message === "string" && err.message) ||
            t("CLUB_EMPLOYEES_LOAD_ERROR");
          dispatch(
            showToast({
              show: false,
              message: msg,
              type: "error",
            })
          );
        }
      } finally {
        if (!cancelled) setClubEmployeesLoading(false);
      }
    };
    run();
    return () => {
      cancelled = true;
    };
  }, [effectiveClubId, isClubEmployee, dispatch, t]);

  const employeeSelectOptions = useMemo(() => {
    const rows = clubEmployees
      .map((row) => {
        const id = pickClubEmployeeUserId(row);
        if (!id) return null;
        return { id, label: formatClubEmployeeOptionLabel(row) };
      })
      .filter(Boolean);
    rows.sort((a, b) =>
      a.label.localeCompare(b.label, undefined, { sensitivity: "base" })
    );
    return rows;
  }, [clubEmployees]);

  const effectiveSoldByUserId = useMemo(() => {
    if (isClubEmployee && userId) return String(userId);
    return soldByUserId?.trim() || "";
  }, [isClubEmployee, userId, soldByUserId]);

  const showEmployeeStaffFilter = useMemo(
    () =>
      !isClubEmployee &&
      !isGuestlistChannelFilter &&
      saleChannel !== "ONLINE",
    [isClubEmployee, isGuestlistChannelFilter, saleChannel]
  );

  useEffect(() => {
    if (saleChannel === "ONLINE" && soldByUserId) {
      setSoldByUserId("");
    }
  }, [saleChannel]);

  useEffect(() => {
    if (isTableBookingChannelFilter && mainTab === "issued") {
      setMainTab("overview");
    }
  }, [isTableBookingChannelFilter, mainTab]);

  const selectedStaffLabel = useMemo(() => {
    if (!effectiveSoldByUserId) return "";
    const hit = employeeSelectOptions.find((o) => o.id === effectiveSoldByUserId);
    if (hit?.label) return hit.label;
    const offlineHit = asArray(salesResult?.offlineByEmployee).find(
      (r) => String(r?.soldByUserId ?? r?._id ?? "") === effectiveSoldByUserId
    );
    if (offlineHit?.fullname) return String(offlineHit.fullname);
    const tbHit = asArray(
      normalizeTableBookingsFromReport(salesResult)?.byEmployee
    ).find(
      (r) => String(r?.referredByUserId ?? "") === effectiveSoldByUserId
    );
    if (tbHit?.fullname) return String(tbHit.fullname);
    return "";
  }, [effectiveSoldByUserId, employeeSelectOptions, salesResult]);

  const reportClubId = useMemo(() => {
    const raw = salesResult?.clubId ?? managedClubId ?? effectiveClubId;
    if (raw == null || raw === "") return null;
    return String(raw);
  }, [salesResult?.clubId, managedClubId, effectiveClubId]);

  useEffect(() => {
    if (!salesResult || !reportClubId) {
      setFetchedClubName(null);
      return;
    }
    const hit = (clubList || []).find((c) => String(c._id) === reportClubId);
    if (hit?.name || hit?.slug) {
      setFetchedClubName(null);
      return;
    }
    const apiName =
      salesResult.clubName ??
      salesResult.club?.name ??
      (typeof salesResult.club === "object" && salesResult.club !== null
        ? salesResult.club.name
        : null);
    if (typeof apiName === "string" && apiName.trim() !== "") {
      setFetchedClubName(null);
      return;
    }
    let cancelled = false;
    clubService
      .getClubAdmin(reportClubId)
      .then((res) => {
        const raw = res?.data?.data ?? res?.data ?? res;
        const name = raw?.name ?? raw?.slug;
        if (!cancelled) {
          setFetchedClubName(
            typeof name === "string" && name.trim() !== "" ? name.trim() : null
          );
        }
      })
      .catch(() => {
        if (!cancelled) setFetchedClubName(null);
      });
    return () => {
      cancelled = true;
    };
  }, [salesResult, reportClubId, clubList]);

  const resolvedClubNameForReport = useMemo(() => {
    if (!reportClubId || !salesResult) return null;
    const apiName =
      salesResult.clubName ??
      salesResult.club?.name ??
      (typeof salesResult.club === "object" && salesResult.club !== null
        ? salesResult.club.name
        : null);
    if (typeof apiName === "string" && apiName.trim() !== "") {
      return apiName.trim();
    }
    const hit = (clubList || []).find((c) => String(c._id) === reportClubId);
    if (hit?.name) return String(hit.name);
    if (hit?.slug) return String(hit.slug);
    return fetchedClubName;
  }, [reportClubId, salesResult, clubList, fetchedClubName]);

  const eventBookingLookups = useMemo(
    () => buildEventBookingPercentageLookups(eventOptions),
    [eventOptions]
  );

  const resolvedEventTitle = useMemo(() => {
    if (!eventId) return "";
    const ev = eventOptions.find((e) => String(e._id) === String(eventId));
    return ev?.title ? String(ev.title) : "";
  }, [eventId, eventOptions]);

  const resolvedCompletedEventTitle = useMemo(() => {
    if (!completedEventId) return "";
    const ev = completedEventOptions.find(
      (e) => String(e._id) === String(completedEventId)
    );
    return ev?.title ? String(ev.title) : "";
  }, [completedEventId, completedEventOptions]);

  const selectedCompletedEventStartdatetime = useMemo(() => {
    if (!completedEventId) return null;
    const ev = completedEventOptions.find(
      (e) => String(e._id) === String(completedEventId)
    );
    return ev?.startdatetime ?? null;
  }, [completedEventId, completedEventOptions]);

  const canShowCompletedEventsSection = Boolean(
    effectiveClubId && (isAdmin || isClubManager)
  );

  const canShowClubDateRangeSection = Boolean(effectiveClubId);

  const canDownloadEventSummary = Boolean(
    completedEventId?.trim() && canShowCompletedEventsSection
  );

  const eventSummaryDownload = useEventSummaryDownload({
    eventId: completedEventId,
    eventTitle: resolvedCompletedEventTitle,
    eventStartdatetime: selectedCompletedEventStartdatetime,
    format: eventSummaryFormat === "csv" ? "csv" : "xlsx",
    enabled: canDownloadEventSummary,
    clubId: effectiveClubId,
    clubEmployees,
  });

  const {
    downloading: eventSummaryDownloading,
    checkingReadiness: eventSummaryCheckingReadiness,
    showPendingModal: showEventSummaryPendingModal,
    readinessData: eventSummaryReadinessData,
    forceClosingKey: eventSummaryForceClosingKey,
    closePendingModal: closeEventSummaryPendingModal,
    handleDownload: handleDownloadEventSummary,
    handleCheckScannerStatus: handleCheckEventSummaryScannerStatus,
    handleRetryReadiness: handleRetryEventSummaryReadiness,
    handleForceCloseDevice: handleForceCloseEventSummaryDevice,
  } = eventSummaryDownload;

  /**
   * camelCase query for GET /club/reports/sales (and base for bookings/issued lists after omitSalesReportOnlyParams).
   * Do not send clubId for club manager / employee G�� backend scopes via JWT.
   */
  const buildClubSalesReportQueryParams = useCallback(() => {
    const p = {};
    if (saleChannel === "GUESTLIST") {
      p.guestlist = true;
    } else if (saleChannel === "TABLE_BOOKING") {
      p.tableBookingsOnly = true;
    } else {
      if (saleChannel === "ONLINE" || saleChannel === "OFFLINE") {
        p.saleChannel = saleChannel;
      }
    }
    if (eventId?.trim()) p.eventId = eventId.trim();
    if (
      showBreakdownFilter &&
      (breakdownPeriod === "week" || breakdownPeriod === "month")
    ) {
      p.breakdown = breakdownPeriod;
    }
    if (isAdmin && adminClubId?.trim()) {
      p.clubId = adminClubId.trim();
    }
    const skipStaffFilterOnChannel =
      saleChannel === "ONLINE" || saleChannel === "GUESTLIST";
    if (isClubEmployee && userId && !skipStaffFilterOnChannel) {
      p.soldByUserId = userId;
    } else if (soldByUserId?.trim() && !skipStaffFilterOnChannel) {
      p.soldByUserId = soldByUserId.trim();
    }
    if (eventScope && eventScope !== EVENT_SCOPES.ALL) {
      p.eventType = eventScope;
    }
    if (!eventId?.trim() && hasSaleDateRange(salesDateFrom, salesDateTo)) {
      appendClubSalesReportDateParams(p, salesDateFrom, salesDateTo);
    }
    return p;
  }, [
    saleChannel,
    eventId,
    eventScope,
    salesDateFrom,
    salesDateTo,
    breakdownPeriod,
    showBreakdownFilter,
    isAdmin,
    adminClubId,
    isClubEmployee,
    userId,
    soldByUserId,
  ]);

  const buildClubSalesListQueryParams = useCallback(
    () => omitSalesReportOnlyParams(buildClubSalesReportQueryParams()),
    [buildClubSalesReportQueryParams]
  );

  /** Table booking lines: same filters as sales report lists + optional drill-down event. */
  const buildTableBookingLinesQueryParams = useCallback(() => {
    const p = { ...buildClubSalesListQueryParams() };
    if (eventId?.trim()) {
      p.eventId = eventId.trim();
    }
    if (isAdmin && adminClubId?.trim()) {
      p.clubId = adminClubId.trim();
    } else if (salesResult?.clubId != null && salesResult.clubId !== "") {
      p.clubId = String(salesResult.clubId);
    }
    if (
      effectiveSoldByUserId &&
      saleChannel !== "ONLINE" &&
      saleChannel !== "GUESTLIST"
    ) {
      p.soldByUserId = effectiveSoldByUserId;
    }
    return p;
  }, [
    buildClubSalesListQueryParams,
    eventId,
    isAdmin,
    adminClubId,
    salesResult?.clubId,
    effectiveSoldByUserId,
    saleChannel,
  ]);

  const loadCatalogSnapshotPage = useCallback(
    async ({ cardKey, page = 1, limit = 10 }) => {
      const query = CATALOG_CARD_QUERY[cardKey];
      if (!query) {
        return { lines: [], page: 1, limit: 10, total: 0, totalPages: 0 };
      }
      const catalogLimit = Math.min(100, Math.max(1, Number(limit) || 10));
      const catalogPage = Math.max(1, Number(page) || 1);
      const params = {
        ...buildClubSalesReportQueryParams(),
        ...query,
        catalogPage,
        catalogLimit,
      };
      const block = unwrapResponse(await fetchClubSalesReport(params));
      const pageData = pickCatalogSnapshotPage(block, catalogPage, catalogLimit);
      return {
        ...pageData,
        lines: catalogLinesForCard(pageData.lines, cardKey),
      };
    },
    [buildClubSalesReportQueryParams]
  );

  const loadSalesReport = useCallback(async () => {
    const from = String(salesDateFrom || "").trim();
    const to = String(salesDateTo || "").trim();
    const effectiveFrom = from || to;
    const effectiveTo = to || from;
    if (effectiveFrom && effectiveTo && effectiveFrom > effectiveTo) {
      dispatch(
        showToast({
          message: t("CLUB_SALES_DATE_RANGE_INVALID"),
          type: "error",
        })
      );
      return;
    }
    setSalesLoading(true);
    setSalesResult(null);
    setStaffTableBookingLines([]);
    try {
      if (
        shouldUseEmptyScopedSalesReport(
          eventScope,
          eventId,
          scopedEventIdsForReport
        )
      ) {
        setSalesResult(
          buildEmptyClubSalesResult({
            clubId: effectiveClubId,
            eventType: eventScope,
            salesDateFrom,
            salesDateTo,
          })
        );
        return;
      }

      const reportParams = buildClubSalesReportQueryParams();
      const listParams = omitSalesReportOnlyParams(reportParams);
      const block = unwrapResponse(await fetchClubSalesReport(reportParams));
      let nextBlock = block;

      const staffId = isClubEmployee && userId
        ? String(userId)
        : soldByUserId?.trim() || "";
      if (
        staffId &&
        saleChannel !== "ONLINE" &&
        saleChannel !== "GUESTLIST" &&
        !asArray(block?.tableBookingLines ?? block?.table_booking_lines).length
      ) {
        try {
          const lineParams = {
            ...listParams,
            soldByUserId: staffId,
            page: 1,
            limit: 100,
          };
          const linesBlock = unwrapResponse(
            await fetchClubTableBookingReportLines(lineParams)
          );
          const lines = asArray(linesBlock?.items);
          if (lines.length) {
            nextBlock = { ...nextBlock, tableBookingLines: lines };
          }
          setStaffTableBookingLines(lines);
        } catch {
          setStaffTableBookingLines([]);
        }
      } else {
        setStaffTableBookingLines(
          asArray(block?.tableBookingLines ?? block?.table_booking_lines)
        );
      }
      setSalesResult(nextBlock);
    } catch (err) {
      dispatch(
        showToast({
          message:
            typeof err === "string"
              ? err
              : err?.message || t("CLUB_SALES_LOAD_ERROR"),
          type: "error",
        })
      );
    } finally {
      setSalesLoading(false);
    }
  }, [
    buildClubSalesReportQueryParams,
    salesDateFrom,
    salesDateTo,
    eventId,
    dispatch,
    t,
    isClubEmployee,
    userId,
    soldByUserId,
    saleChannel,
    eventScope,
    scopedEventIdsForReport,
    effectiveClubId,
  ]);

  const loadBookings = async (
    page = bookingsPage,
    search = bookingsSearch,
    { silent = false } = {}
  ) => {
    if (!silent) setBookingsLoading(true);
    try {
      if (
        shouldUseEmptyScopedSalesReport(
          eventScope,
          eventId,
          scopedEventIdsForReport
        )
      ) {
        setBookingsItems([]);
        setBookingsTotal(0);
        setBookingsPage(1);
        return;
      }

      const searchTerm = String(search || "").trim();
      const lim = searchTerm ? 100 : clampPageLimit(bookingsLimit, 10);
      const fetchPage = searchTerm ? 1 : page;
      const baseParams = buildClubSalesListQueryParams();
      const fetchBlock = async (nextPage, nextLimit) => {
        const params = {
          ...baseParams,
          page: nextPage,
          limit: nextLimit,
        };
        if (isTableBookingChannelFilter) {
          const block = unwrapResponse(
            await fetchClubTableBookingReportLines(params)
          );
          return {
            items: asArray(block?.items),
            total: Number(block?.total) || 0,
            limit: Number(block?.limit) || nextLimit,
          };
        }
        const block = unwrapResponse(await fetchClubSalesBookings(params));
        return {
          items: asArray(block?.items).map(normalizeBookingListItem),
          total: Number(block?.total) || 0,
          limit: Number(block?.limit) || nextLimit,
        };
      };
      const pageResult = searchTerm
        ? await fetchBlock(1, 100)
        : await collectLimitedPage(fetchBlock, fetchPage, lim);
      setBookingsItems(pageResult.items);
      setBookingsTotal(pageResult.total);
      setBookingsPage(pageResult.page);
    } catch (err) {
      setBookingsItems([]);
      setBookingsTotal(0);
      dispatch(
        showToast({
          message:
            typeof err === "string"
              ? err
              : err?.message || t("CLUB_SALES_BOOKINGS_LOAD_ERROR"),
          type: "error",
        })
      );
    } finally {
      if (!silent) setBookingsLoading(false);
    }
  };

  const loadIssuedTickets = async (page = issuedPage) => {
    setIssuedLoading(true);
    try {
      const lim = Math.min(100, Math.max(1, issuedLimit));
      const params = {
        ...buildClubSalesListQueryParams(),
        page,
        limit: lim,
      };
      const block = unwrapResponse(await fetchClubSalesIssuedTickets(params));
      const items = asArray(block?.items).map(normalizeBookingListItem);
      setIssuedItems(items);
      setIssuedTotal(Number(block?.total) || 0);
      setIssuedPage(Number(block?.page) || page);
    } catch (err) {
      setIssuedItems([]);
      setIssuedTotal(0);
      dispatch(
        showToast({
          message:
            typeof err === "string"
              ? err
              : err?.message || t("CLUB_SALES_ISSUED_LOAD_ERROR"),
          type: "error",
        })
      );
    } finally {
      setIssuedLoading(false);
    }
  };

  const loadEventBookings = async (
    page = evbPage,
    search = evbSearch,
    { silent = false } = {}
  ) => {
    const id = String(eventId || "").trim();
    if (!id) {
      setEvbData(null);
      setEvbPage(1);
      return;
    }
    if (!silent) setEvbLoading(true);
    try {
      const searchTerm = String(search || "").trim();
      // When searching we filter client-side, so pull a wide page (max 100).
      const lim = searchTerm ? 100 : Math.min(100, Math.max(1, evbLimit));
      const fetchPage = searchTerm ? 1 : page;
      const block = unwrapResponse(
        await fetchEventBookings(id, {
          status: evbStatus || undefined,
          page: fetchPage,
          limit: lim,
        })
      );
      setEvbData(block || null);
      setEvbPage(searchTerm ? 1 : Number(block?.pagination?.page) || page);
    } catch (err) {
      setEvbData(null);
      dispatch(
        showToast({
          message:
            (typeof err?.response?.data?.message === "string" &&
              err.response.data.message) ||
            (typeof err === "string" ? err : err?.message) ||
            t("CLUB_SALES_EVENT_BOOKINGS_LOAD_ERROR"),
          type: "error",
        })
      );
    } finally {
      if (!silent) setEvbLoading(false);
    }
  };

  const onEvbLimitChange = (next) => {
    setEvbLimit(next);
    setEvbPage(1);
  };

  const onEvbStatusChange = (next) => {
    setEvbStatus(next);
    setEvbPage(1);
  };

  const clearEvbSearch = () => {
    setEvbSearch("");
    setEvbPage(1);
    loadEventBookings(1, "");
  };

  /*
  const copyIssuedTicketUrl = useCallback(
    async (url) => {
      if (!url || typeof url !== "string") return;
      try {
        await navigator.clipboard.writeText(url);
        dispatch(
          showToast({
            message: t("CLUB_SALES_ISSUED_LINK_COPIED"),
            type: "success",
          })
        );
      } catch {
        dispatch(
          showToast({
            message: t("CLUB_SALES_ISSUED_COPY_FAILED"),
            type: "error",
          })
        );
      }
    },
    [dispatch, t]
  );
  */

  const handleResendIssuedLink = useCallback(
    async (orderId) => {
      const id = String(orderId || "").trim();
      if (!id) return;
      setResendingOrderId(id);
      try {
        const raw = unwrapResponse(await postIssuedTicketResendLink(id));
        const via = raw?.resentVia;
        const channels =
          Array.isArray(via) && via.length ? via.join(", ") : "";
        dispatch(
          showToast({
            message: channels
              ? t("CLUB_SALES_ISSUED_RESEND_OK", { channels })
              : t("CLUB_SALES_ISSUED_RESEND_OK", { channels: "G��" }),
            type: "success",
          })
        );
      } catch (err) {
        const msg =
          (typeof err?.response?.data?.message === "string" &&
            err.response.data.message) ||
          (typeof err?.response?.data?.error === "string" &&
            err.response.data.error) ||
          (typeof err?.message === "string" && err.message) ||
          t("CLUB_SALES_ISSUED_RESEND_FAILED");
        dispatch(
          showToast({
            message: msg,
            type: "error",
          })
        );
      } finally {
        setResendingOrderId(null);
      }
    },
    [dispatch, t]
  );

  const onBookingsLimitChange = (next) => {
    setBookingsLimit(next);
    setBookingsPage(1);
  };

  const clearBookingsSearch = () => {
    setBookingsSearch("");
    setBookingsPage(1);
    loadBookings(1, "");
  };

  const onIssuedLimitChange = (next) => {
    setIssuedLimit(next);
    setIssuedPage(1);
  };

  const loadTableBookingLines = useCallback(
    async (page = 1, eventIdForLines = tbLinesEventId, limitOverride) => {
      setTbLinesLoading(true);
      try {
        const lim = Math.min(
          100,
          Math.max(1, limitOverride ?? tbLinesLimit)
        );
        const params = {
          ...buildTableBookingLinesQueryParams(),
          page,
          limit: lim,
        };
        if (eventIdForLines !== undefined) {
          if (eventIdForLines != null && eventIdForLines !== "") {
            params.eventId = eventIdForLines;
          } else {
            delete params.eventId;
          }
        }
        const block = unwrapResponse(
          await fetchClubTableBookingReportLines(params)
        );
        setTbLinesItems(asArray(block?.items));
        setTbLinesTotal(Number(block?.total) || 0);
        setTbLinesPage(Number(block?.page) || page);
      } catch (err) {
        setTbLinesItems([]);
        setTbLinesTotal(0);
        dispatch(
          showToast({
            message:
              typeof err === "string"
                ? err
                : err?.message || t("CLUB_SALES_TABLE_BOOKING_LINES_LOAD_ERROR"),
            type: "error",
          })
        );
      } finally {
        setTbLinesLoading(false);
      }
    },
    [buildTableBookingLinesQueryParams, dispatch, t, tbLinesLimit]
  );

  const openTableBookingLines = useCallback(
    (eventIdForLines) => {
      const label =
        eventIdForLines == null || eventIdForLines === ""
          ? t("CLUB_SALES_TABLE_BOOKING_NO_EVENT")
          : resolvedEventTitle &&
              String(eventIdForLines) === String(eventId)
            ? resolvedEventTitle
            : (() => {
                const hit = eventOptions.find(
                  (e) => String(e._id) === String(eventIdForLines)
                );
                return hit?.title ? String(hit.title) : String(eventIdForLines);
              })();
      setTbLinesEventId(eventIdForLines);
      setTbLinesEventLabel(label);
      setTbLinesPage(1);
      setTbLinesOpen(true);
      loadTableBookingLines(1, eventIdForLines);
    },
    [eventId, eventOptions, loadTableBookingLines, resolvedEventTitle, t]
  );

  const closeTableBookingLines = useCallback(() => {
    setTbLinesOpen(false);
  }, []);

  const onTbLinesPageChange = useCallback(
    (page) => {
      setTbLinesPage(page);
      loadTableBookingLines(page, tbLinesEventId);
    },
    [loadTableBookingLines, tbLinesEventId]
  );

  const onTbLinesLimitChange = useCallback(
    (next) => {
      setTbLinesLimit(next);
      setTbLinesPage(1);
      loadTableBookingLines(1, tbLinesEventId, next);
    },
    [loadTableBookingLines, tbLinesEventId]
  );

  const canExportReportCsv = useMemo(() => {
    if (salesResult == null || typeof salesResult !== "object") return false;
    const byEvent = asArray(salesResult.byEvent ?? salesResult.by_event);
    const byCategory = asArray(salesResult.byCategory ?? salesResult.by_category);
    const showCategoryBreakdown =
      Boolean(eventId?.trim()) && byCategory.length > 0;
    return showCategoryBreakdown || byEvent.length > 0;
  }, [salesResult, eventId]);

  const handleExportReportCsv = useCallback(() => {
    exportClubSalesReportToCsv({
      data: salesResult,
      selectedEventId: eventId,
      resolvedEventTitle,
      isAdmin,
      t,
    });
  }, [salesResult, eventId, resolvedEventTitle, isAdmin, t]);

  const handleDownloadEventInvoice = useCallback(async () => {
    if (!completedEventId?.trim()) return;
    setEventInvoiceDownloading(true);
    try {
      const { blob, filename } = await downloadClubEventInvoice(
        completedEventId.trim()
      );
      triggerBlobDownload(blob, filename || "tax_invoice.pdf");
    } catch (err) {
      dispatch(
        showToast({
          message:
            typeof err === "string"
              ? err
              : err?.message || t("CLUB_SALES_EVENT_INVOICE_DOWNLOAD_ERROR"),
          type: "error",
        })
      );
    } finally {
      setEventInvoiceDownloading(false);
    }
  }, [dispatch, completedEventId, t]);

  useEffect(() => {
    if (mainTab !== "bookings" || salesResult == null) return;
    loadBookings(1);
  }, [mainTab, salesResult, bookingsLimit]);

  useEffect(() => {
    if (mainTab !== "bookings" || salesResult == null) return;

    const searchTerm = String(bookingsSearch).trim();
    if (!searchTerm) return;

    const timer = setTimeout(() => {
      setBookingsPage(1);
      loadBookings(1, searchTerm, { silent: true });
    }, 500);

    return () => clearTimeout(timer);
  }, [bookingsSearch, mainTab, salesResult]);

  useEffect(() => {
    if (mainTab !== "issued" || salesResult == null) return;
    loadIssuedTickets(1);
  }, [mainTab, salesResult, issuedLimit]);

  useEffect(() => {
    if (mainTab !== "eventBookings" || salesResult == null) return;
    loadEventBookings(1);
  }, [mainTab, salesResult, evbLimit, evbStatus, eventId]);

  useEffect(() => {
    if (mainTab !== "eventBookings" || salesResult == null) return;
    if (!eventId?.trim()) return;

    const searchTerm = String(evbSearch).trim();
    if (!searchTerm) return;

    const timer = setTimeout(() => {
      setEvbPage(1);
      loadEventBookings(1, searchTerm, { silent: true });
    }, 500);

    return () => clearTimeout(timer);
  }, [evbSearch, mainTab, salesResult, eventId]);

  const pageTitle = [
    {
      title: t("CLUB_MANAGER_SALES_ANALYSIS"),
      path: "#",
    },
  ];

  const reportPageTitle = isEventEndReportView
    ? t("CLUB_SALES_MENU_EVENT_END_REPORT")
    : t("CLUB_ANALYSIS_TAB_SALES");

  const pageShellClass =
    "pageContent categoryManagement club-staff-page-shell club-sales-analysis-page";

  return (
    <div className={pageShellClass}>
      <Helmet>
        <title>{reportPageTitle} | RizzTix</title>
      </Helmet>
      <Breadcrumbs
        innerPagetitle={reportPageTitle}
        breadcrumbItem={reportPageTitle}
        pageTitle={pageTitle}
        BreadcrumbInner={true}
        BreadcrumbmainTitle={true}
      />
      <Card className="mb-0 border-0 shadow-sm">
        <Card.Body className="pt-2 pb-4">
          {isAdmin ? (
            <div className="club-sales-admin-club-card p-3 p-md-4 mb-3 rounded-3">
              <Row className="g-3 align-items-end">
                <Col xs={12} md={6} lg={4}>
                  <Form.Label className="small mb-1">
                    {t("CLUB_SALES_ADMIN_CLUB_FILTER")}
                  </Form.Label>
                  <ClubSalesSearchableClubSelect
                    clubs={clubList}
                    value={adminClubId}
                    allLabel={t("CLUB_SALES_ALL_PARTNERS")}
                    onChange={(nextClubId) => {
                      setAdminClubId(nextClubId);
                      setEventId("");
                      setCompletedEventId("");
                    }}
                  />
                </Col>
                {!effectiveClubId ? (
                  <Col xs={12} md={8} lg={8}>
                    <p className="small text-muted mb-0">
                      {t("CLUB_SALES_SELECT_CLUB_FOR_DATE_RANGE")}
                    </p>
                  </Col>
                ) : null}
              </Row>
            </div>
          ) : null}
          {canShowClubDateRangeSection && isSalesReportView ? (
            <div className="club-sales-club-scope-card p-3 p-md-4 mb-3 rounded-3">
              <div className="d-flex flex-wrap align-items-center justify-content-between gap-2 mb-3">
                <div>
                  <h6 className="fw-semibold mb-1">
                    {t("CLUB_SALES_DATE_RANGE_SECTION")}
                  </h6>
                  <p className="small text-muted mb-0">
                    {t("CLUB_SALES_DATE_RANGE_HINT")}
                  </p>
                </div>
                {resolvedClubLabel ? (
                  <Badge
                    bg="secondary"
                    className="fw-normal px-3 py-2 club-sales-club-badge"
                  >
                    {resolvedClubLabel}
                  </Badge>
                ) : null}
              </div>
              <Row className="g-3 align-items-end club-sales-date-range-row">
                <Col xs={12} sm={6} md={4} lg={3}>
                  <Form.Label className="small mb-1">
                    {t("CLUB_SALES_DATE_FROM")}
                  </Form.Label>
                  <Form.Control
                    type="date"
                    value={salesDateFrom}
                    max={salesDateTo || undefined}
                    onChange={(e) => setSalesDateFrom(e.target.value)}
                  />
                </Col>
                <Col xs={12} sm={6} md={4} lg={3}>
                  <Form.Label className="small mb-1">
                    {t("CLUB_SALES_DATE_TO")}
                  </Form.Label>
                  <Form.Control
                    type="date"
                    value={salesDateTo}
                    min={salesDateFrom || undefined}
                    onChange={(e) => setSalesDateTo(e.target.value)}
                  />
                </Col>
              </Row>
            </div>
          ) : null}
          {canShowCompletedEventsSection && isEventEndReportView ? (
            <div className="club-sales-club-scope-card p-3 p-md-4 mb-3 rounded-3">
              <div className="d-flex flex-wrap align-items-center justify-content-between gap-2 mb-3">
                <div>
                  <h6 className="fw-semibold mb-1">
                    {t("CLUB_SALES_DATE_RANGE_SECTION")}
                  </h6>
                  <p className="small text-muted mb-0">
                    {t("CLUB_SALES_DATE_RANGE_HINT")}
                  </p>
                </div>
                {resolvedClubLabel ? (
                  <Badge
                    bg="secondary"
                    className="fw-normal px-3 py-2 club-sales-club-badge"
                  >
                    {resolvedClubLabel}
                  </Badge>
                ) : null}
              </div>
              <Row className="g-3 align-items-end club-sales-date-range-row">
                <Col xs={12} sm={6} md={4} lg={3}>
                  <Form.Label className="small mb-1">
                    {t("CLUB_SALES_DATE_FROM")}
                  </Form.Label>
                  <Form.Control
                    type="date"
                    value={eventEndDateFrom}
                    max={eventEndDateTo || undefined}
                    onChange={(e) => setEventEndDateFrom(e.target.value)}
                  />
                </Col>
                <Col xs={12} sm={6} md={4} lg={3}>
                  <Form.Label className="small mb-1">
                    {t("CLUB_SALES_DATE_TO")}
                  </Form.Label>
                  <Form.Control
                    type="date"
                    value={eventEndDateTo}
                    min={eventEndDateFrom || undefined}
                    onChange={(e) => setEventEndDateTo(e.target.value)}
                  />
                </Col>
              </Row>
              <div className="club-sales-completed-events-block mt-4 pt-4">
                <h6 className="fw-semibold mb-1">
                  {t("CLUB_SALES_COMPLETED_EVENTS_SECTION")}
                </h6>
                <p className="small text-muted mb-3">
                  {t("CLUB_SALES_COMPLETED_EVENTS_HINT")}
                </p>
                <Row className="g-3 align-items-end">
                  <Col xs={12} md={6} lg={5}>
                    <Form.Label className="small mb-1">
                      {t("CLUB_SALES_COMPLETED_EVENT_SELECT")}
                    </Form.Label>
                    <ClubSalesSearchableEventSelect
                      events={filteredCompletedEventOptions}
                      value={completedEventId}
                      onChange={setCompletedEventId}
                      disabled={!filteredCompletedEventOptions.length}
                      placeholder={
                        filteredCompletedEventOptions.length
                          ? t("CLUB_SALES_COMPLETED_EVENT_PLACEHOLDER")
                          : hasSaleDateRange(eventEndDateFrom, eventEndDateTo)
                            ? t("CLUB_SALES_COMPLETED_EVENTS_NONE_IN_RANGE")
                            : t("CLUB_SALES_COMPLETED_EVENTS_NONE")
                      }
                    />
                  </Col>
                {canDownloadEventSummary ? (
                  <Col xs={12} lg={7}>
                    <div className="d-flex flex-wrap align-items-center gap-2">
                      <Form.Select
                        className="club-sales-event-summary-format"
                        style={{ width: "auto", minWidth: "6.5rem" }}
                        value={eventSummaryFormat}
                        onChange={(e) =>
                          setEventSummaryFormat(e.target.value)
                        }
                        disabled={
                          eventSummaryDownloading ||
                          eventSummaryCheckingReadiness
                        }
                        aria-label={t("CLUB_SALES_EVENT_SUMMARY_FORMAT")}
                      >
                        <option value="xlsx">XLSX</option>
                        <option value="csv">CSV</option>
                      </Form.Select>
                      <Button
                        variant="outline-light"
                        size="sm"
                        onClick={handleCheckEventSummaryScannerStatus}
                        disabled={
                          eventSummaryDownloading ||
                          eventSummaryCheckingReadiness
                        }
                      >
                        {eventSummaryCheckingReadiness ? (
                          <>
                            <Spinner
                              animation="border"
                              size="sm"
                              className="me-2"
                              role="status"
                            />
                            {t("EVENT_SUMMARY_CHECKING_SCANNERS")}
                          </>
                        ) : (
                          t("EVENT_SUMMARY_CHECK_SCANNER_STATUS")
                        )}
                      </Button>
                      <Button
                        variant="outline-light"
                        size="sm"
                        onClick={handleDownloadEventSummary}
                        disabled={
                          eventSummaryDownloading ||
                          eventSummaryCheckingReadiness
                        }
                      >
                        {eventSummaryDownloading ? (
                          <>
                            <Spinner
                              animation="border"
                              size="sm"
                              className="me-2"
                              role="status"
                            />
                            {t("CLUB_SALES_EVENT_SUMMARY_DOWNLOADING")}
                          </>
                        ) : (
                          t("CLUB_SALES_EVENT_SUMMARY_DOWNLOAD")
                        )}
                      </Button>
                      {isAdmin ? (
                        <Button
                          variant="outline-light"
                          size="sm"
                          onClick={handleDownloadEventInvoice}
                          disabled={eventInvoiceDownloading}
                        >
                          {eventInvoiceDownloading ? (
                            <>
                              <Spinner
                                animation="border"
                                size="sm"
                                className="me-2"
                                role="status"
                              />
                              {t("CLUB_SALES_EVENT_INVOICE_DOWNLOADING")}
                            </>
                          ) : (
                            t("CLUB_SALES_EVENT_INVOICE_DOWNLOAD")
                          )}
                        </Button>
                      ) : null}
                    </div>
                  </Col>
                ) : null}
                </Row>
              </div>
            </div>
          ) : null}
          {isSalesReportView ? (
          <>
          <div className="club-sales-filters-card p-3 p-md-4 mb-4">
            <h6 className="fw-semibold mb-3">
              {isGuestlistChannelFilter
                ? t("GUESTLIST_REPORT_TITLE")
                : isTableBookingChannelFilter
                  ? t("CLUB_SALES_TABLE_BOOKING_REPORT_TITLE")
                  : t("CLUB_ANALYSIS_TAB_SALES")}
            </h6>
            <Form className="row g-3">
              <Col xs={12} sm={6} lg={3}>
                <Form.Label className="small mb-1">
                  {t("CLUB_SALES_FILTER_EVENT_SCOPE")}
                </Form.Label>
                <Form.Select
                  value={eventScope}
                  onChange={(e) => setEventScope(e.target.value)}
                  disabled={!effectiveClubId}
                >
                  <option value={EVENT_SCOPES.ONGOING}>
                    {t("CLUB_SALES_SCOPE_ONGOING")}
                  </option>
                  <option value={EVENT_SCOPES.UPCOMING}>
                    {t("CLUB_SALES_SCOPE_UPCOMING")}
                  </option>
                  <option value={EVENT_SCOPES.PAST}>
                    {t("CLUB_SALES_SCOPE_PAST")}
                  </option>
                  <option value={EVENT_SCOPES.ALL}>
                    {t("CLUB_SALES_SCOPE_ALL")}
                  </option>
                </Form.Select>
              </Col>
              <Col xs={12} sm={6} lg={3}>
                <Form.Label className="small mb-1">
                  {t("CLUB_SALES_FILTER_EVENT")}
                </Form.Label>
                <ClubSalesSearchableEventSelect
                  events={filteredScopedEventOptions}
                  value={eventId}
                  onChange={setEventId}
                  disabled={!effectiveClubId}
                  placeholder={
                    !effectiveClubId
                      ? t("CLUB_SALES_ONGOING_NEED_CLUB")
                      : filteredScopedEventOptions.length
                        ? scopeAllEventsLabel
                        : scopeNoneEventsLabel
                  }
                />
                {effectiveClubId && filteredScopedEventOptions.length === 0 ? (
                  <Form.Text className="text-muted small">
                    {scopeNoneEventsLabel}
                  </Form.Text>
                ) : !effectiveClubId ? (
                  <Form.Text className="text-muted small">
                    {t("CLUB_SALES_ONGOING_NEED_CLUB")}
                  </Form.Text>
                ) : null}
              </Col>
              {showBreakdownFilter ? (
                <Col xs={12} sm={6} lg={3}>
                  <Form.Label className="small mb-1">
                    {t("CLUB_SALES_BREAKDOWN")}
                  </Form.Label>
                  <Form.Select
                    value={breakdownPeriod}
                    onChange={(e) => setBreakdownPeriod(e.target.value)}
                  >
                    <option value="">{t("CLUB_SALES_BREAKDOWN_NONE")}</option>
                    <option value="week">{t("CLUB_SALES_BREAKDOWN_WEEK")}</option>
                    <option value="month">{t("CLUB_SALES_BREAKDOWN_MONTH")}</option>
                  </Form.Select>
                </Col>
              ) : null}
              <Col xs={12} sm={6} lg={showBreakdownFilter ? 3 : 4}>
                <Form.Label className="small mb-1">
                  {t("CLUB_SALES_CHANNEL")}
                </Form.Label>
                <Form.Select
                  value={saleChannel}
                  onChange={(e) => setSaleChannel(e.target.value)}
                >
                  <option value="">{t("CLUB_SALES_CHANNEL_ALL")}</option>
                  <option value="ONLINE">ONLINE</option>
                  <option value="OFFLINE">OFFLINE</option>
                  {canViewGuestlistReports ? (
                    <option value="GUESTLIST">
                      {t("CLUB_SALES_CHANNEL_GUESTLIST")}
                    </option>
                  ) : null}
                  <option value="TABLE_BOOKING">
                    {t("CLUB_SALES_CHANNEL_TABLE_BOOKING")}
                  </option>
                </Form.Select>
              </Col>
              {showEmployeeStaffFilter ? (
                <Col xs={12} sm={6} lg={showBreakdownFilter ? 3 : 4}>
                  <Form.Label className="small mb-1">
                    {t("CLUB_SALES_STAFF_FILTER")}
                  </Form.Label>
                  <Form.Select
                    value={soldByUserId}
                    onChange={(e) => setSoldByUserId(e.target.value)}
                    disabled={!effectiveClubId || clubEmployeesLoading}
                  >
                    <option value="">{t("CLUB_SALES_STAFF_ALL")}</option>
                    {employeeSelectOptions.map((o) => (
                      <option key={o.id} value={o.id}>
                        {o.label}
                      </option>
                    ))}
                  </Form.Select>
                  {!effectiveClubId ? (
                    <Form.Text className="text-muted small">
                      {t("CLUB_SALES_STAFF_NEED_CLUB")}
                    </Form.Text>
                  ) : clubEmployeesLoading ? (
                    <Form.Text className="text-muted small">
                      {t("LOADING")}
                    </Form.Text>
                  ) : clubEmployees.length > 0 &&
                    employeeSelectOptions.length === 0 ? (
                    <Form.Text className="text-muted small">
                      {t("CLUB_SALES_STAFF_IDS_HINT")}
                    </Form.Text>
                  ) : employeeSelectOptions.length === 0 ? (
                    <Form.Text className="text-muted small">
                      {t("CLUB_SALES_STAFF_NONE")}
                    </Form.Text>
                  ) : (
                    <Form.Text className="text-muted small">
                      {t("CLUB_SALES_STAFF_FILTER_HINT")}
                    </Form.Text>
                  )}
                </Col>
              ) : null}
              {saleChannel === "ONLINE" && !isClubEmployee ? (
                <Col xs={12}>
                  <p className="small text-muted mb-0">
                    {t("CLUB_SALES_STAFF_ONLINE_DISABLED")}
                  </p>
                </Col>
              ) : null}
              {isClubEmployee && userId && (
                <Col xs={12}>
                  <p className="small text-muted mb-0">
                    {myEmpref
                      ? t("CLUB_SALES_EMPLOYEE_SCOPED_EMPREF", {
                          empref: myEmpref,
                        })
                      : t("CLUB_SALES_EMPLOYEE_SCOPED", {
                          id: userId,
                        })}
                  </p>
                </Col>
              )}
              <Col xs={12} className="d-flex flex-wrap gap-2 align-items-center pt-1">
                <CustomButton
                  type="button"
                  className="fillButton"
                  onClick={loadSalesReport}
                  disabled={salesLoading}
                >
                  {salesLoading ? t("LOADING") : t("CLUB_SALES_LOAD_REPORT")}
                </CustomButton>
                {/* <Button
                  variant="outline-light"
                  size="sm"
                  onClick={handleExportReportCsv}
                  disabled={salesLoading || !canExportReportCsv}
                >
                  {t("CLUB_SALES_EXPORT_CSV")}
                </Button> */}
              </Col>
            </Form>
          </div>

          {salesLoading ? (
            <div className="club-sales-loading-panel rounded-3 club-sales-empty-hint">
              <div className="d-flex align-items-center gap-2 small">
                <Spinner animation="border" size="sm" role="status" />
                <span>{t("LOADING")}</span>
              </div>
            </div>
          ) : null}
          {!salesLoading && salesResult != null ? (
            <div className="mt-2 pt-4 border-top club-sales-report-divider">
              <Tabs
                id="club-sales-main-tabs"
                activeKey={mainTab}
                onSelect={(k) => setMainTab(k || "overview")}
                className="mb-3 club-sales-main-tabs"
              >
                <Tab eventKey="overview" title={t("CLUB_SALES_TAB_OVERVIEW")}>
                  <h5 className="mb-3 fw-semibold">{t("CLUB_SALES_REPORT_RESULT")}</h5>
                  <ClubSalesReportDisplay
                    data={salesResult}
                    resolvedClubName={resolvedClubNameForReport}
                    resolvedEventTitle={resolvedEventTitle}
                    selectedEventId={eventId}
                    reportEventScope={eventScope}
                    scopedEventIds={scopedEventIdsForReport}
                    breakdownPeriod={breakdownPeriod}
                    selectedStaffUserId={effectiveSoldByUserId}
                    selectedStaffLabel={selectedStaffLabel}
                    saleChannelFilter={saleChannel}
                    guestlistMode={isGuestlistChannelFilter}
                    tableBookingMode={isTableBookingChannelFilter}
                    isAdmin={isAdmin}
                    singleClubFiltered={Boolean(effectiveClubId)}
                    eventBookingLookups={eventBookingLookups}
                    onOpenTableBookingLines={openTableBookingLines}
                    staffTableBookingLines={staffTableBookingLines}
                    onLoadCatalogLines={loadCatalogSnapshotPage}
                    t={t}
                  />
                  <TableBookingLinesModal
                    show={tbLinesOpen}
                    onHide={closeTableBookingLines}
                    eventLabel={tbLinesEventLabel}
                    items={tbLinesItems}
                    loading={tbLinesLoading}
                    page={tbLinesPage}
                    limit={tbLinesLimit}
                    total={tbLinesTotal}
                    onPageChange={onTbLinesPageChange}
                    onLimitChange={onTbLinesLimitChange}
                    t={t}
                  />
                </Tab>
                <Tab eventKey="bookings" title={t("CLUB_SALES_TAB_BOOKINGS")}>
                  <div className="d-flex flex-wrap align-items-center justify-content-between gap-2 mb-3">
                    {!isTableBookingChannelFilter ? (
                    <div className="d-flex flex-wrap align-items-center gap-2 flex-grow-1">
                      <Form.Control
                        type="search"
                        size="sm"
                        className="club-sales-bookings-search-group"
                        placeholder={t("CLUB_SALES_BOOKINGS_SEARCH_PLACEHOLDER")}
                        value={bookingsSearch}
                        onChange={(e) => setBookingsSearch(e.target.value)}
                        aria-label={t("CLUB_SALES_BOOKINGS_SEARCH_PLACEHOLDER")}
                      />
                      {bookingsSearch ? (
                        <Button
                          type="button"
                          variant="link"
                          size="sm"
                          className="text-muted px-0"
                          onClick={clearBookingsSearch}
                          disabled={bookingsLoading}
                        >
                          {t("CLEAR_SEARCH")}
                        </Button>
                      ) : null}
                    </div>
                    ) : (
                      <div className="flex-grow-1" />
                    )}
                    <Button
                      variant="outline-light"
                      size="sm"
                      className="border-secondary"
                      onClick={() => loadBookings(bookingsPage)}
                      disabled={bookingsLoading}
                    >
                      {bookingsLoading ? t("LOADING") : t("CLUB_SALES_BOOKINGS_REFRESH")}
                    </Button>
                  </div>
                  {isGuestlistChannelFilter ? (
                    <GuestlistBookingsTable
                      items={bookingsItems}
                      loading={bookingsLoading}
                      page={bookingsPage}
                      limit={bookingsLimit}
                      total={bookingsTotal}
                      searchTerm={bookingsSearch}
                      onPageChange={(p) => {
                        setBookingsPage(p);
                        loadBookings(p);
                      }}
                      onLimitChange={onBookingsLimitChange}
                      t={t}
                    />
                  ) : isTableBookingChannelFilter ? (
                    <>
                      <TableBookingLinesTable
                        items={bookingsItems}
                        t={t}
                        showGateCollections
                      />
                      {bookingsTotal > bookingsLimit ? (
                        <div className="d-flex justify-content-end mt-3">
                          <Pagination className="mb-0">
                            <Pagination.Prev
                              disabled={bookingsPage <= 1 || bookingsLoading}
                              onClick={() => loadBookings(bookingsPage - 1)}
                            />
                            <Pagination.Item active>{bookingsPage}</Pagination.Item>
                            <Pagination.Next
                              disabled={
                                bookingsPage * bookingsLimit >= bookingsTotal ||
                                bookingsLoading
                              }
                              onClick={() => loadBookings(bookingsPage + 1)}
                            />
                          </Pagination>
                        </div>
                      ) : null}
                    </>
                  ) : (
                    <ClubSalesBookingsTable
                      items={bookingsItems}
                      loading={bookingsLoading}
                      page={bookingsPage}
                      limit={bookingsLimit}
                      total={bookingsTotal}
                      searchTerm={bookingsSearch}
                      onPageChange={(p) => {
                        setBookingsPage(p);
                        loadBookings(p);
                      }}
                      onLimitChange={onBookingsLimitChange}
                      isAdmin={isAdmin && !adminClubId}
                      canSeeAdminIds={isAdmin}
                      t={t}
                    />
                  )}
                </Tab>
                <Tab eventKey="issued" title={t("CLUB_SALES_TAB_ISSUED_TICKETS")} disabled={isTableBookingChannelFilter}>
                  <div className="d-flex justify-content-end mb-3">
                    <Button
                      variant="outline-light"
                      size="sm"
                      className="border-secondary"
                      onClick={() => loadIssuedTickets(issuedPage)}
                      disabled={issuedLoading}
                    >
                      {issuedLoading ? t("LOADING") : t("CLUB_SALES_ISSUED_REFRESH")}
                    </Button>
                  </div>
                  <ClubSalesIssuedTicketsTable
                    items={issuedItems}
                    loading={issuedLoading}
                    page={issuedPage}
                    limit={issuedLimit}
                    total={issuedTotal}
                    onPageChange={(p) => {
                      setIssuedPage(p);
                      loadIssuedTickets(p);
                    }}
                    onLimitChange={onIssuedLimitChange}
                    isAdmin={isAdmin && !adminClubId}
                    resendingOrderId={resendingOrderId}
                    onResendLink={handleResendIssuedLink}
                    // onCopyTicketUrl={copyIssuedTicketUrl}
                    t={t}
                  />
                </Tab>
                {isAdmin ? (
                <Tab
                  eventKey="eventBookings"
                  title={t("CLUB_SALES_TAB_EVENT_BOOKINGS")}
                >
                  <div className="d-flex flex-wrap align-items-center justify-content-between gap-2 mb-3">
                    <div className="d-flex flex-wrap align-items-center gap-2 flex-grow-1">
                      <Form.Control
                        type="search"
                        size="sm"
                        className="club-sales-bookings-search-group"
                        placeholder={t(
                          "CLUB_SALES_EVENT_BOOKINGS_SEARCH_PLACEHOLDER"
                        )}
                        value={evbSearch}
                        onChange={(e) => setEvbSearch(e.target.value)}
                        disabled={!eventId?.trim()}
                        aria-label={t(
                          "CLUB_SALES_EVENT_BOOKINGS_SEARCH_PLACEHOLDER"
                        )}
                      />
                      {evbSearch ? (
                        <Button
                          type="button"
                          variant="link"
                          size="sm"
                          className="text-muted px-0"
                          onClick={clearEvbSearch}
                          disabled={evbLoading}
                        >
                          {t("CLEAR_SEARCH")}
                        </Button>
                      ) : null}
                    </div>
                    <Button
                      variant="outline-light"
                      size="sm"
                      className="border-secondary"
                      onClick={() => loadEventBookings(evbPage)}
                      disabled={evbLoading || !eventId?.trim()}
                    >
                      {evbLoading
                        ? t("LOADING")
                        : t("CLUB_SALES_EVENT_BOOKINGS_REFRESH")}
                    </Button>
                  </div>
                  <ClubSalesEventBookingsTable
                    data={evbData}
                    loading={evbLoading}
                    hasEvent={Boolean(eventId?.trim())}
                    page={evbPage}
                    limit={evbLimit}
                    statusFilter={evbStatus}
                    searchTerm={evbSearch}
                    onStatusChange={onEvbStatusChange}
                    onPageChange={(p) => {
                      setEvbPage(p);
                      loadEventBookings(p);
                    }}
                    onLimitChange={onEvbLimitChange}
                    t={t}
                  />
                </Tab>
                ) : null}
              </Tabs>
            </div>
          ) : null}
          </>
          ) : null}
        </Card.Body>
      </Card>
      <EventSummaryPendingDevicesModal
        show={showEventSummaryPendingModal}
        onHide={closeEventSummaryPendingModal}
        readiness={eventSummaryReadinessData}
        onRetry={handleRetryEventSummaryReadiness}
        onForceClose={handleForceCloseEventSummaryDevice}
        retrying={eventSummaryCheckingReadiness || eventSummaryDownloading}
        forceClosingKey={eventSummaryForceClosingKey}
      />
    </div>
  );
};

export default ClubSalesAnalysisContainer;
